/**
 * 用途：创建广告向导入口，根据 URL 中的 platform 参数将请求分发到对应平台（Facebook / Google / TikTok）
 *       的具体创建向导组件
 * 路由：/campaigns/:platform/create
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { MultiSelect } from '../components/MultiSelect';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import {
  accountsApi,
  type AdPlatform,
  type FacebookPage,
  type FacebookPixel,
  type FacebookCatalog,
  type FacebookProductSet,
  type FacebookInterest,
  type FacebookBehavior,
  type FacebookInstagramAccount,
  type FacebookPartnershipInfo,
  type FacebookPartnershipPage,
  type FacebookPartnershipInstagramAccount,
  type FacebookCustomAudience,
  type CarouselCardRequest,
  type FacebookGeoLocation,
  type DraftPayloadRequest,
  type FacebookExistingPost,
} from '../api/accounts';
import { useAppStore } from '../store/useAppStore';
import { confirmDialog } from '../store/useConfirmStore';
import { useIsMobile } from '../hooks/useIsMobile';
import { adTagsApi, type TagDefinition } from '../api/adTags';
import { CreateGoogleSearchAdWizard } from './CreateGoogleSearchAdWizard';
import { CreateTikTokAdWizard } from './CreateTikTokAdWizard';
import {
  DateTimePicker as SharedDateTimePicker,
  UploadZone as SharedUploadZone,
} from './adWizardShared';
import { FacebookPlacementSelector } from '../components/FacebookPlacementSelector';
import { TableLoadingCell } from '../components/TableLoadingOverlay';
import {
  type FacebookPlacementMode,
  getDefaultFacebookPlacementState,
  normalizeFacebookPlacementState,
} from './facebookPlacements';
import {
  filterFacebookInstagramAccountsByBrand,
  filterFacebookPagesByBrand,
  pickDefaultFacebookInstagramActorID,
  pickDefaultFacebookPageID,
} from './facebookBrandAssets';

// ─── Constants ────────────────────────────────────────────────────────────────

type WizardStep = 1 | 2 | 3;
type ToastType = 'success' | 'error' | 'warning' | 'info';
type AdType = 'image' | 'video' | 'carousel' | 'catalog' | 'partnership' | 'existing' | 'post';
type PartnershipContentSource = 'existing_post' | 'new_creative' | 'input_code';
type PartnershipIdentityOrder = 'brand_first' | 'partner_first';
type PartnershipHeadlineIdentity = 'dynamic_identity' | 'brand_identity' | 'partner_identity';
const DISABLED_PARTNERSHIP_CONTENT_SOURCES = new Set<PartnershipContentSource>(['existing_post', 'new_creative']);
const PARTNERSHIP_CONTENT_SOURCE_UNAVAILABLE_DESC = '因缺少 FB 合作方公共主页接口权限，暂不可用，后续开放';
const PARTNERSHIP_PARTNER_SELECTOR_DISABLED = true;
type CreateDraftPayload = Record<string, unknown>;
type WizardAccountOption = { id: string; account_id: string; account_name?: string; is_mine?: boolean };
const FACEBOOK_CATALOG_DESTINATION = 'WEBSITE';
const DEFAULT_FACEBOOK_CATALOG_NAME = 'Zeelool-422';
const DEFAULT_FACEBOOK_PIXEL_NAME = 'zeelool-新0325';
const FACEBOOK_GEO_PRIORITY = ['US', 'CA', 'GB', 'AU', 'NZ'];
const TAG_MULTI_VALUE_SEPARATOR = '、';
const PARTNERSHIP_STATUS_LABEL: Record<string, string> = {
  APPROVED: '已授权',
  PENDING_APPROVAL: '待确认',
  REJECTED: '已拒绝',
  REVOKED: '已撤销',
  SELF_REMOVED: '已移除',
  CANCELED: '已取消',
  ACTIVE: '已授权',
  AD_CODE: '待获取',
  PENDING_VALIDATION: '待验证',
};

const normalizeAccountIDForCompare = (value?: string | null) => {
  const id = String(value ?? '').trim();
  return id.toLowerCase().startsWith('act_') ? id.slice(4) : id;
};

const resolveAccountIDFromOptions = (options: WizardAccountOption[], value?: string | null) => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const normalized = normalizeAccountIDForCompare(raw);
  const match = options.find((option) => (
    option.id === raw ||
    normalizeAccountIDForCompare(option.id) === normalized ||
    normalizeAccountIDForCompare(option.account_id) === normalized
  ));
  return match?.account_id ?? '';
};

const isPlacementTag = (td: TagDefinition) => {
  const text = `${td.key} ${td.label}`.toLowerCase();
  return text.includes('版位') || text.includes('placement');
};

const parseMultiTagValue = (raw?: string) => {
  const value = (raw || '').trim();
  if (!value) return [];
  return value.split(/[、,]/).map((v) => v.trim()).filter(Boolean);
};

const serializeMultiTagValue = (values: string[]) => values.map((v) => v.trim()).filter(Boolean).join(TAG_MULTI_VALUE_SEPARATOR);

const setTagValue = (prev: Record<string, string>, key: string, value: string) => {
  const next = { ...prev };
  const cleaned = value.trim();
  if (cleaned) next[key] = cleaned;
  else delete next[key];
  return next;
};

// 支持的广告平台配置
const AD_PLATFORMS: { key: AdPlatform; label: string; icon: string; available: boolean }[] = [
  { key: 'facebook', label: 'Facebook', icon: '📘', available: true },
  { key: 'tiktok',   label: 'TikTok',   icon: '🎵', available: true },
  { key: 'google',   label: 'Google',   icon: '🔍', available: true },
];

const OBJECTIVES = [
  {
    value: 'OUTCOME_AWARENESS',
    label: '知名度',
    desc: '让更多人了解您的品牌、产品或服务',
    billingHint: '按千次曝光计费 (CPM)',
    color: 'var(--primary, #1210B8)',
  },
  {
    value: 'OUTCOME_TRAFFIC',
    label: '流量',
    desc: '将用户引导至您的网站、应用或 Messenger',
    billingHint: '按点击计费 (CPC)',
    color: 'var(--green)',
  },
  {
    value: 'OUTCOME_SALES',
    label: '销售转化',
    desc: '推动网站购买、目录销售或线下转化',
    billingHint: '按转化优化，建议配置 Pixel',
    color: 'var(--amber, #F59E0B)',
  },
];

const DEFAULT_GOALS_BY_OBJECTIVE: Record<string, { value: string; label: string }[]> = {
  OUTCOME_AWARENESS: [
    { value: 'REACH', label: '广告覆盖人数最大化' },
    { value: 'IMPRESSIONS', label: '展示次数最大化' },
  ],
  OUTCOME_TRAFFIC: [
    { value: 'LANDING_PAGE_VIEWS', label: '落地页浏览量最大化' },
  ],
  OUTCOME_SALES: [
    { value: 'OFFSITE_CONVERSIONS', label: '转化量最大化' },
    { value: 'VALUE', label: '转化价值最大化' },
  ],
};

const DEFAULT_GOAL_LABEL_BY_VALUE: Record<string, string> = Object.values(DEFAULT_GOALS_BY_OBJECTIVE)
  .flat()
  .reduce((acc, goal) => {
    acc[goal.value] = goal.label;
    return acc;
  }, {} as Record<string, string>);

const FALLBACK_GOAL_LABELS: Record<string, string> = {
  LINK_CLICKS: '链接点击量最大化',
  IMPRESSIONS: '展示次数最大化',
  REACH: '广告覆盖人数最大化',
  LANDING_PAGE_VIEWS: '落地页浏览量最大化',
  OFFSITE_CONVERSIONS: '转化量最大化',
  VALUE: '转化价值最大化',
};

function goalLabelByValue(value: string): string {
  return DEFAULT_GOAL_LABEL_BY_VALUE[value] || FALLBACK_GOAL_LABELS[value] || value;
}

function buildFacebookAttributionSpec(clickDays: '1' | '7', engagedViewDays: '0' | '1', viewDays: '0' | '1'): string {
  const parts = [`${clickDays}D_CLICK`];
  if (engagedViewDays === '1') parts.push('1D_ENGAGED_VIEW');
  if (viewDays === '1') parts.push('1D_VIEW');
  return parts.join('_');
}

function formatFacebookAttributionSummary(clickDays: '1' | '7', engagedViewDays: '0' | '1', viewDays: '0' | '1'): string {
  const parts = [`点击后${clickDays}天内`];
  if (engagedViewDays === '1') parts.push('互动后1天内');
  if (viewDays === '1') parts.push('浏览后1天内');
  return parts.join('，');
}

type FacebookAttributionModel = 'standard' | 'incremental';

const FACEBOOK_ATTRIBUTION_MODEL_OPTIONS: { value: FacebookAttributionModel; label: string }[] = [
  { value: 'standard', label: '标准归因' },
  { value: 'incremental', label: '增量归因' },
];

const CONVERSION_EVENTS = [
  { value: 'PURCHASE', label: '购买' },
  { value: 'ADD_TO_CART', label: '加入购物车' },
  { value: 'INITIATE_CHECKOUT', label: '开始结账' },
  { value: 'LEAD', label: '潜在客户' },
  { value: 'COMPLETE_REGISTRATION', label: '完成注册' },
  { value: 'SUBSCRIBE', label: '订阅' },
  { value: 'VIEW_CONTENT', label: '查看内容' },
];

const FACEBOOK_LANGUAGE_OPTIONS = [
  { value: 6, label: '英语' },
  { value: 17, label: '中文（简体）' },
  { value: 24, label: '西班牙语' },
  { value: 10, label: '法语' },
  { value: 7, label: '德语' },
  { value: 11, label: '日语' },
];

const DEFAULT_INCLUDED_COUNTRIES = ['US'];
const DEFAULT_EXCLUDED_COUNTRIES = ['CA', 'GB', 'DE', 'FR'];
const DEFAULT_FACEBOOK_LOCALES = [6];
const FACEBOOK_COUNTRY_FALLBACK_LABELS: Record<string, string> = {
  US: '美国',
  CA: '加拿大',
  GB: '英国',
  DE: '德国',
  FR: '法国',
};


const CTA_BY_OBJ: Record<string, { value: string; label: string }[]> = {
  OUTCOME_AWARENESS: [
    { value: 'LEARN_MORE', label: '了解更多' },
    { value: 'WATCH_MORE', label: '观看更多' },
    { value: 'SUBSCRIBE', label: '订阅' },
    { value: 'FOLLOW_PAGE', label: '关注主页' },
    { value: 'NO_BUTTON', label: '无按钮' },
  ],
  OUTCOME_TRAFFIC: [
    { value: 'LEARN_MORE', label: '了解更多' },
    { value: 'SIGN_UP', label: '立即注册' },
    { value: 'CONTACT_US', label: '联系我们' },
    { value: 'CALL_NOW', label: '立即致电' },
    { value: 'BOOK_TRAVEL', label: '立即预订' },
    { value: 'APPLY_NOW', label: '立即申请' },
    { value: 'DOWNLOAD', label: '立即下载' },
  ],
  OUTCOME_SALES: [
    { value: 'SHOP_NOW', label: '立即购买' },
    { value: 'BUY_NOW', label: '立即下单' },
    { value: 'GET_OFFER', label: '获取优惠' },
    { value: 'GET_QUOTE', label: '获取报价' },
    { value: 'ORDER_NOW', label: '立即点单' },
    { value: 'ADD_TO_CART', label: '加入购物车' },
  ],
  _default: [
    { value: 'LEARN_MORE', label: '了解更多' },
    { value: 'SHOP_NOW', label: '立即购买' },
    { value: 'SIGN_UP', label: '立即注册' },
    { value: 'GET_QUOTE', label: '获取报价' },
    { value: 'CONTACT_US', label: '联系我们' },
    { value: 'NO_BUTTON', label: '无按钮' },
  ],
};

const AD_TYPES: { key: AdType; label: string }[] = [
  { key: 'image', label: '单图广告' },
  { key: 'video', label: '单视频广告' },
  { key: 'carousel', label: '轮播广告' },
  { key: 'existing', label: '使用已有创意ID' },
  { key: 'post', label: '使用现有帖子' },
];

const DPA_VARS = [
  { v: '{{product.name | titleize}}', desc: '商品名称' },
  { v: '{{product.current_price}}', desc: '当前价格' },
  { v: '{{product.original_price}}', desc: '原价' },
  { v: '{{product.brand}}', desc: '品牌' },
  { v: '{{product.description}}', desc: '商品描述' },
];

type CreativeEnhancementKey =
  | 'text_generation'
  | 'add_text_overlay'
  | 'image_touchups'
  | 'music_generation'
  | 'text_optimizations'
  | 'image_animation'
  | 'product_tags'
  | 'inline_comment'
  | 'enhance_cta'
  | 'image_brightness_and_contrast'
  | 'reveal_details_over_time'
  | 'show_summary';

const CREATIVE_ENHANCEMENT_OPTIONS: {
  key: CreativeEnhancementKey;
  label: string;
  desc: string;
  defaultOn: boolean;
  section: 'advanced' | 'required';
}[] = [
  { key: 'text_generation', label: '增强素材文案', desc: '基于素材上下文自动生成更丰富的文案表达', defaultOn: false, section: 'advanced' },
  { key: 'add_text_overlay', label: '叠加文字', desc: '在素材上自动叠加与你文案匹配的文字元素', defaultOn: false, section: 'advanced' },
  { key: 'image_touchups', label: '视觉润色', desc: '自动优化画面细节与观感表现', defaultOn: true, section: 'advanced' },
  { key: 'music_generation', label: '添加音乐', desc: '自动为素材匹配背景音乐增强吸引力', defaultOn: false, section: 'advanced' },
  { key: 'text_optimizations', label: '文案改进', desc: '自动重排重点短语并优化文案呈现', defaultOn: true, section: 'advanced' },
  { key: 'image_animation', label: '添加动画', desc: '为图片/文案生成轻量动态效果提升停留', defaultOn: false, section: 'advanced' },
  { key: 'product_tags', label: '添加商品标记', desc: '在创意中展示商品标记，强化商品识别', defaultOn: false, section: 'advanced' },
  { key: 'inline_comment', label: '相关评论', desc: '展示与广告相关的优质评论', defaultOn: true, section: 'required' },
  { key: 'enhance_cta', label: '增强行动号召', desc: '根据版位强化 CTA 的可见性和表达', defaultOn: true, section: 'required' },
  { key: 'image_brightness_and_contrast', label: '调整亮度和对比度', desc: '自动微调亮度与对比度，提升可读性', defaultOn: false, section: 'required' },
  { key: 'reveal_details_over_time', label: '逐步显示详情', desc: '通过渐进方式展示更多卖点细节', defaultOn: false, section: 'required' },
  { key: 'show_summary', label: '显示精选', desc: '自动突出展示更可能促成点击的关键信息', defaultOn: false, section: 'required' },
];

const getDefaultCreativeEnhancements = () => CREATIVE_ENHANCEMENT_OPTIONS.reduce((acc, option) => {
  acc[option.key] = option.defaultOn;
  return acc;
}, {} as Record<CreativeEnhancementKey, boolean>);

const ADVANCED_CREATIVE_ENHANCEMENT_OPTIONS = CREATIVE_ENHANCEMENT_OPTIONS.filter((item) => item.section === 'advanced');
const REQUIRED_CREATIVE_ENHANCEMENT_OPTIONS = CREATIVE_ENHANCEMENT_OPTIONS.filter((item) => item.section === 'required');

// ─── Small helper components ──────────────────────────────────────────────────

function FormGroup({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div className="form-group" style={style}>{children}</div>;
}

function Label({ children, required, style }: { children: React.ReactNode; required?: boolean; style?: React.CSSProperties }) {
  return (
    <label className="form-label" style={style}>
      {children}
      {required && <span style={{ color: 'var(--red)', marginLeft: 3 }}>*</span>}
    </label>
  );
}

function Hint({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return <div className="form-hint" style={style}>{children}</div>;
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{
      border: '1px solid var(--border)',
      borderRadius: 10,
      marginBottom: 18,
    }}>
      <div style={{
        padding: '10px 16px',
        borderBottom: '1px solid var(--border)',
        fontSize: 12,
        fontWeight: 700,
        color: 'var(--text3)',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        background: 'var(--surface2)',
        borderRadius: '10px 10px 0 0',
      }}>
        {title}
      </div>
      <div style={{ padding: '14px 16px' }}>{children}</div>
    </div>
  );
}

function SelectionCard({
  title,
  description,
  active,
  disabled,
  onClick,
  compact = false,
}: {
  title: string;
  description?: string;
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        gap: compact ? 4 : 6,
        width: '100%',
        minHeight: compact ? 56 : 72,
        padding: compact ? '10px 14px' : '12px 14px',
        borderRadius: 10,
        border: active ? '1px solid rgba(67, 56, 202, 0.45)' : '1px solid var(--border)',
        background: active ? 'rgba(67, 56, 202, 0.06)' : '#fff',
        boxShadow: active ? '0 0 0 1px rgba(67, 56, 202, 0.08) inset' : 'none',
        color: disabled ? 'var(--text4)' : 'var(--text)',
        textAlign: 'left',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        transition: 'all .18s ease',
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 700 }}>{title}</span>
      {description && <span style={{ fontSize: 11, lineHeight: 1.45, color: 'var(--text3)' }}>{description}</span>}
    </button>
  );
}

// ─── Horizontal Step Bar (top of left card) ──────────────────────────────────

function HorizontalStepBar({
  step,
  minStep = 1,
  maxStep = step,
  onStepClick,
}: {
  step: WizardStep;
  minStep?: WizardStep;
  maxStep?: WizardStep;
  onStepClick?: (step: WizardStep) => void;
}) {
  const steps = [
    { n: 1 as WizardStep, label: '广告系列', sub: 'Campaign' },
    { n: 2 as WizardStep, label: '广告组', sub: 'Ad Set' },
    { n: 3 as WizardStep, label: '广告创意', sub: 'Ad' },
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 22, paddingBottom: 16, borderBottom: '1px solid var(--border)' }}>
      {steps.map((s, i) => {
        const done = step > s.n || s.n < minStep;
        const active = step === s.n;
        const clickable = !!onStepClick && s.n <= maxStep;
        return (
          <React.Fragment key={s.n}>
            <button
              type="button"
              onClick={() => clickable && onStepClick?.(s.n)}
              disabled={!clickable}
              title={clickable ? `查看/修改${s.label}` : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                border: 'none',
                background: 'transparent',
                padding: 0,
                cursor: clickable ? 'pointer' : 'default',
                textAlign: 'left',
              }}
            >
              <div style={{
                width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700,
                background: done ? 'var(--green)' : active ? 'var(--primary)' : 'var(--surface3)',
                color: done || active ? '#fff' : 'var(--text3)',
                transition: 'all .2s',
              }}>
                {done ? '✓' : s.n}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: active ? 700 : 500, color: active ? 'var(--text)' : done ? 'var(--green)' : 'var(--text3)', lineHeight: 1.2 }}>{s.label}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)' }}>{s.sub}</div>
              </div>
            </button>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 1, background: step > s.n ? 'var(--green)' : 'var(--border)', margin: '0 12px', transition: 'background .3s' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ─── Performance Benchmarks ───────────────────────────────────────────────────

function PerformanceBenchmarks({ objective }: { objective: string }) {
  const data: Record<string, { metric: string; range: string; tip: string }[]> = {
    OUTCOME_AWARENESS: [
      { metric: 'CPM', range: '$3 – $8', tip: '千次曝光成本' },
      { metric: 'CTR', range: '0.5% – 1.5%', tip: '展示类广告参考' },
      { metric: '频次', range: '建议 1.5–3× / 周', tip: '避免受众疲劳' },
    ],
    OUTCOME_TRAFFIC: [
      { metric: 'CPC', range: '$0.3 – $1.5', tip: '链接点击成本' },
      { metric: 'CPM', range: '$5 – $15', tip: '流量目标' },
      { metric: 'CTR', range: '1% – 3%', tip: '电商类广告参考' },
    ],
    OUTCOME_SALES: [
      { metric: 'CPA', range: '$10 – $50', tip: '购买成本（行业差异大）' },
      { metric: 'ROAS', range: '2× – 5×', tip: '建议配置 Pixel 追踪' },
      { metric: 'CPM', range: '$8 – $25', tip: '转化竞价更高' },
    ],
  };
  const rows = data[objective] || data['OUTCOME_SALES'];
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', background: 'var(--surface2)', marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        成效参考
      </div>
      {rows.map((r) => (
        <div key={r.metric} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>{r.metric}</div>
            {r.tip && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1 }}>{r.tip}</div>}
          </div>
          <span style={{ fontSize: 12, color: 'var(--primary-light)', fontWeight: 600, whiteSpace: 'nowrap', marginLeft: 8 }}>{r.range}</span>
        </div>
      ))}
      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--border)', lineHeight: 1.5 }}>
        参考值因受众质量、素材、竞争情况而异
      </div>
    </div>
  );
}

// ─── Delivery Logic Panel ─────────────────────────────────────────────────────

function DeliveryLogicPanel({ step, objective }: { step: WizardStep; objective: string }) {
  const stepHints: Record<WizardStep, string[]> = {
    1: [
      '广告系列确定整体推广目标与预算上限',
      '选择「销量」目标需配置 Pixel',
      '启用 CBO 后广告系列统一调配预算',
      'Meta 会自动将预算分配到效果最佳的广告组',
    ],
    2: [
      '广告组控制受众、出价和排期',
      '受众越精准，CPA 通常越低',
      '兴趣标签与自定义受众可叠加使用',
      '总预算模式需设置结束时间',
      '转化优化需配置好 Pixel 并积累事件数据',
    ],
    3: [
      '广告创意直接影响点击率和转化率',
      '单图：1:1 或 1.91:1，≤30 MB',
      '视频：15–60 秒效果最佳，首帧要抓眼球',
      '轮播：2–10 张，每张独立标题和链接',
      'Advantage+：多素材自动测试最优组合',
    ],
  };
  const objHints: Record<string, string[]> = {
    OUTCOME_AWARENESS: ['按 CPM 计费，优化覆盖人数', '不需要 Pixel，适合品牌曝光'],
    OUTCOME_TRAFFIC: ['优化链接点击或落地页浏览', '建议选「落地页浏览」减少无效点击'],
    OUTCOME_SALES: ['需配置 Pixel 追踪购买事件', '积累 50+ 转化后算法效率提升明显'],
  };
  const lines = [...(stepHints[step] || []), ...(objHints[objective] || [])];
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', background: 'var(--surface2)', marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        投放逻辑说明
      </div>
      <ul style={{ margin: 0, paddingLeft: 14, fontSize: 11, color: 'var(--text2)', lineHeight: 1.85 }}>
        {lines.map((h, i) => <li key={i}>{h}</li>)}
      </ul>
    </div>
  );
}

// ─── Creative Spec Reference (step 3 sidebar) ─────────────────────────────────

function CreativeSpecRef() {
  return (
    <div style={{
      border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px',
      background: 'var(--surface2)', marginTop: 12,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        创意规格参考
      </div>
      <table style={{ fontSize: 11, width: '100%', borderCollapse: 'collapse', lineHeight: 1.9 }}>
        <tbody>
          {[
            ['主文案', '≤ 125 字符'],
            ['标题', '≤ 40 字符'],
            ['描述', '≤ 30 字符'],
            ['图片比例', '1:1 (1080×1080)'],
            ['图片大小', '≤ 30 MB'],
            ['视频时长', '15–60 秒最佳'],
            ['轮播卡片', '2–10 张'],
          ].map(([k, v]) => (
            <tr key={k}>
              <td style={{ color: 'var(--text3)', paddingRight: 6, whiteSpace: 'nowrap' }}>{k}</td>
              <td style={{ color: 'var(--text2)' }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TikTokGuidePanel({
  step,
  objective,
  adFormat,
}: {
  step: WizardStep;
  objective: string;
  adFormat: string;
}) {
  const stepHints: Record<WizardStep, string[]> = {
    1: [
      'TikTok 广告系列先确定营销目标、预算模式和初始状态。',
      '转化/商品销售目标建议提前准备 Pixel 与优化事件，避免后续无法起量。',
      '预算尽量保持稳定，减少频繁大幅改动对学习期的干扰。',
    ],
    2: [
      '广告组负责排期、版位、出价和受众，决定系统如何拿量。',
      '自动版位更适合冷启动，手动版位会缩小流量范围。',
      '总预算或明确活动周期时，建议设置开始/结束时间。',
    ],
    3: [
      'TikTok 创意优先考虑原生感，不要直接照搬 Facebook 的横版素材。',
      '前几秒需要快速给出卖点、价格或使用场景，避免开场过慢。',
      '文案与 CTA 要和落地页保持一致，减少点击后的预期落差。',
    ],
  };

  const objectiveHints: Record<string, string[]> = {
    TRAFFIC: ['更适合用清晰利益点和明确 CTA 拉动点击。'],
    CONVERSIONS: ['需要确保 Pixel 与优化事件可用，系统才会按转化信号优化。'],
    PRODUCT_SALES: ['商品销售建议同步准备商品卖点、价格锚点和落地页转化链路。'],
    VIDEO_VIEWS: ['更适合强调前 3 秒 hook、完播节奏和画面停留。'],
    LEAD_GENERATION: ['线索目标建议在首屏明确表单收益，减少无效提交。'],
    APP_PROMOTION: ['应用推广建议突出核心功能演示和安装后的直接价值。'],
    REACH: ['覆盖目标更看重触达稳定性，素材需快速表达品牌记忆点。'],
  };

  const formatHints: Record<string, string[]> = {
    SINGLE_VIDEO: [
      '单视频建议优先竖版 9:16，画面主体放在安全区内。',
      '建议启用配音、字幕或音乐，让内容更接近 TikTok 原生体验。',
    ],
    SINGLE_IMAGE: [
      '单图片素材建议主体突出、信息层级简单，避免文字过密。',
      '图片广告也要保留明显的产品利益点和动作指令。',
    ],
    SPARK_ADS: [
      'Spark Ads 需要使用已授权的 TikTok 内容，内容 ID 必须有效。',
      '优先选择已有互动基础的帖子，更容易承接社交证明。',
    ],
  };

  const lines = [
    ...(stepHints[step] || []),
    ...(objectiveHints[objective] || []),
    ...(step === 3 ? formatHints[adFormat] || [] : []),
  ];

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', background: 'var(--surface2)', marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        TikTok 投放说明
      </div>
      <ul style={{ margin: 0, paddingLeft: 14, fontSize: 11, color: 'var(--text2)', lineHeight: 1.85 }}>
        {lines.map((line, index) => <li key={`${step}-${objective}-${adFormat}-${index}`}>{line}</li>)}
      </ul>
    </div>
  );
}

function TikTokCreativeSpecRef({ adFormat }: { adFormat: string }) {
  const rowsByFormat: Record<string, [string, string][]> = {
    SINGLE_VIDEO: [
      ['推荐方向', '9:16 竖版优先'],
      ['清晰度', '建议 720P 及以上'],
      ['节奏', '前 3 秒给卖点'],
      ['音频', '建议开启配音/音乐'],
      ['字幕', '避免压边，保留安全区'],
    ],
    SINGLE_IMAGE: [
      ['推荐比例', '9:16 或 1:1'],
      ['画面重点', '主体清晰、背景简洁'],
      ['文字密度', '避免整屏堆字'],
      ['卖点表达', '一屏只讲一个核心利益点'],
      ['CTA', '按钮与文案统一'],
    ],
    SPARK_ADS: [
      ['素材来源', '已发布并授权的 TikTok 内容'],
      ['必填信息', '有效的 TikTok 内容 ID'],
      ['优先选择', '已有互动基础的原生帖子'],
      ['落地方式', '可延续原贴互动与社交证明'],
      ['注意事项', '确保授权关系和身份一致'],
    ],
  };

  const rows = rowsByFormat[adFormat] || rowsByFormat.SINGLE_VIDEO;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', background: 'var(--surface2)', marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        TikTok 创意要点
      </div>
      <table style={{ fontSize: 11, width: '100%', borderCollapse: 'collapse', lineHeight: 1.9 }}>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}>
              <td style={{ color: 'var(--text3)', paddingRight: 6, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{k}</td>
              <td style={{ color: 'var(--text2)' }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Local Ad Preview (mock FB feed card, always visible) ────────────────────

const CTA_LABEL_MAP: Record<string, string> = {
  SHOP_NOW: '立即购买', BUY_NOW: '立即下单', LEARN_MORE: '了解更多',
  SIGN_UP: '立即注册', GET_OFFER: '获取优惠', ORDER_NOW: '立即点单',
  GET_QUOTE: '获取报价', ADD_TO_CART: '加入购物车', CONTACT_US: '联系我们',
  DOWNLOAD: '立即下载', APPLY_NOW: '立即申请', CALL_NOW: '立即致电',
  WATCH_MORE: '观看更多', SUBSCRIBE: '订阅', FOLLOW_PAGE: '关注主页',
  NO_BUTTON: '', BOOK_TRAVEL: '立即预订',
};

function LocalAdPreview({
  adType, imageHash, imagePreviewURL, videoID, message, headline, description,
  link, ctaType, pageName, carouselCount, carouselPreviews,
  partnershipMediaURL, partnershipActorName, partnershipPermalinkURL, existingCreativeID, existingPost,
}: {
  adType: AdType; imageHash: string; imagePreviewURL: string; videoID: string;
  message: string; headline: string; description: string;
  link: string; ctaType: string; pageName: string; carouselCount?: number;
  carouselPreviews?: string[];
  partnershipMediaURL?: string;
  partnershipActorName?: string;
  partnershipPermalinkURL?: string;
  existingCreativeID?: string;
  existingPost?: FacebookExistingPost | null;
}) {
  const domain = link ? (() => { try { return new URL(link).hostname.replace('www.', ''); } catch { return ''; } })() : '';
  const ctaLabel = ctaType ? (CTA_LABEL_MAP[ctaType] ?? ctaType) : '';

  if (adType === 'existing') {
    return (
      <div style={{
        border: '1px solid #dddfe2', borderRadius: 8, background: '#fff',
        fontFamily: '-apple-system, Helvetica, Arial, sans-serif',
        overflow: 'hidden', fontSize: 14, color: '#1c1e21',
        boxShadow: '0 1px 3px rgba(0,0,0,.1)',
      }}>
        <div style={{ padding: '12px 14px', borderBottom: '1px solid #e5e7eb', background: '#f7f8fa', fontSize: 12, fontWeight: 700 }}>
          使用已有 Facebook Creative
        </div>
        <div style={{ padding: '18px 14px', lineHeight: 1.7 }}>
          <div style={{ fontSize: 13, color: '#1c1e21', marginBottom: 6 }}>已应用创意 ID：<strong>{existingCreativeID || '未应用'}</strong></div>
          <div style={{ fontSize: 12, color: '#65676b' }}>右侧下方的 Facebook 真实预览会直接调用 `generatepreviews` 加载这个 creative。</div>
        </div>
      </div>
    );
  }

  if (adType === 'post') {
    return (
      <div style={{ border: '1px solid #dddfe2', borderRadius: 8, background: '#fff', overflow: 'hidden', fontSize: 14, color: '#1c1e21' }}>
        <div style={{ padding: '10px 12px', fontWeight: 700 }}>{existingPost?.platform === 'instagram' ? 'Instagram 帖子' : 'Facebook 帖子'}</div>
        {existingPost?.thumbnail_url ? <img src={existingPost.thumbnail_url} alt="帖子素材" style={{ display: 'block', width: '100%', maxHeight: 260, objectFit: 'cover' }} /> : <div style={{ height: 180, display: 'grid', placeItems: 'center', background: '#f0f2f5', color: '#65676b' }}>选择帖子后显示素材</div>}
        <div style={{ padding: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{existingPost?.title || '未选择帖子'}</div>
          {message && <div style={{ fontSize: 13, lineHeight: 1.45, marginBottom: 6 }}>{message.length > 120 ? message.slice(0, 120) + '...' : message}</div>}
          <div style={{ fontSize: 12, color: '#65676b' }}>{existingPost?.id || ''}</div>
        </div>
        {(ctaLabel || link) && (
          <div style={{ margin: '0 12px 12px', padding: '8px 10px', background: '#f0f2f5', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            {link && <span style={{ fontSize: 11, color: '#65676b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{domain || link}</span>}
            {ctaLabel && <button style={{ padding: '5px 10px', background: '#e4e6eb', border: 'none', borderRadius: 5, fontWeight: 700, fontSize: 11, cursor: 'default', flexShrink: 0, color: '#1c1e21' }}>{ctaLabel}</button>}
          </div>
        )}
      </div>
    );
  }

  // ── Partnership (Instagram post boost) preview ──────────────────────────────
  if (adType === 'partnership') {
    return (
      <div style={{
        border: '1px solid #dbdbdb', borderRadius: 8, background: '#fff',
        fontFamily: '-apple-system, Helvetica, Arial, sans-serif',
        overflow: 'hidden', fontSize: 14, color: '#1c1e21',
      }}>
        {/* IG-style header */}
        <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 9 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0, padding: 2 }}>
            <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>👤</div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 12, color: '#1c1e21' }}>{partnershipActorName || '创作者'}</div>
            <div style={{ fontSize: 10, color: '#8e8e8e' }}>赞助 · Instagram</div>
          </div>
          <div style={{ fontSize: 18, color: '#8e8e8e', cursor: 'default' }}>···</div>
        </div>
        {/* Post media */}
        {partnershipMediaURL ? (
          <img src={partnershipMediaURL} alt="帖子预览" style={{ display: 'block', width: '100%', maxHeight: 300, objectFit: 'cover' }} />
        ) : (
          <div style={{ height: 220, background: 'linear-gradient(135deg,#f09433 0%,#bc1888 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff', gap: 8 }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4.5"/><circle cx="17.5" cy="6.5" r="1"/></svg>
            <span style={{ fontSize: 12, opacity: 0.9 }}>Instagram 帖子预览</span>
          </div>
        )}
        {/* IG action bar */}
        <div style={{ padding: '8px 12px 4px', display: 'flex', gap: 14 }}>
          {['♡', '💬', '⇗'].map((icon) => (
            <span key={icon} style={{ fontSize: 22, color: '#1c1e21', cursor: 'default', lineHeight: 1 }}>{icon}</span>
          ))}
        </div>
        {/* Caption / message override */}
        <div style={{ padding: '4px 12px 10px', fontSize: 13, lineHeight: 1.5, color: '#1c1e21' }}>
          {message ? (
            <><strong style={{ marginRight: 5 }}>{partnershipActorName || '创作者'}</strong>{message.length > 100 ? message.slice(0, 100) + '…' : message}</>
          ) : (
            <span style={{ color: '#8e8e8e', fontStyle: 'italic', fontSize: 12 }}>红人原帖文案（可在下方覆盖）</span>
          )}
        </div>
        {/* CTA override */}
        {(ctaLabel || link) && (
          <div style={{ margin: '0 12px 10px', padding: '7px 12px', background: '#f0f2f5', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            {link && <span style={{ fontSize: 11, color: '#65676b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{domain || link}</span>}
            {ctaLabel && <button style={{ padding: '5px 10px', background: '#e4e6eb', border: 'none', borderRadius: 5, fontWeight: 700, fontSize: 11, cursor: 'default', flexShrink: 0, color: '#1c1e21' }}>{ctaLabel}</button>}
          </div>
        )}
        {partnershipPermalinkURL && (
          <div style={{ padding: '0 12px 8px' }}>
            <a href={partnershipPermalinkURL} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: '#8e8e8e' }}>查看原帖 →</a>
          </div>
        )}
      </div>
    );
  }

  // Generate a consistent avatar color from the page name
  const avatarColor = (() => {
    const colors = ['#1877f2', '#e41e3f', '#42b72a', '#f5a623', '#7b68ee', '#00b4d8'];
    if (!pageName) return colors[0];
    return colors[pageName.charCodeAt(0) % colors.length];
  })();

  return (
    <div style={{
      border: '1px solid #dddfe2', borderRadius: 8, background: '#fff',
      fontFamily: '-apple-system, Helvetica, Arial, sans-serif',
      overflow: 'hidden', fontSize: 14, color: '#1c1e21',
      boxShadow: '0 1px 3px rgba(0,0,0,.1)',
    }}>
      {/* Header — matches Facebook native ad card */}
      <div style={{ padding: '12px 12px 6px', display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* Avatar: page initial in colored circle */}
        <div style={{
          width: 40, height: 40, borderRadius: '50%', background: avatarColor,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 17, fontWeight: 700, color: '#fff', flexShrink: 0, letterSpacing: 0,
        }}>
          {(pageName || 'P')[0].toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1c1e21', lineHeight: 1.2 }}>{pageName || '品牌主页'}</div>
          <div style={{ fontSize: 12, color: '#65676b', display: 'flex', alignItems: 'center', gap: 3, marginTop: 3 }}>
            <span>广告</span>
            <span style={{ fontSize: 8 }}>·</span>
            {/* globe / public icon */}
            <svg width="11" height="11" viewBox="0 0 24 24" fill="#65676b"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/><path d="M12 2a10 10 0 100 20A10 10 0 0012 2zm0 18a8 8 0 110-16 8 8 0 010 16z"/></svg>
          </div>
        </div>
        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, color: '#65676b' }}>
          <span style={{ fontSize: 20, cursor: 'default', padding: '2px 6px', borderRadius: 4 }}>···</span>
          <span style={{ fontSize: 16, cursor: 'default', padding: '2px 6px', borderRadius: 4 }}>✕</span>
        </div>
      </div>

      {/* Primary text */}
      {message ? (
        <div style={{ padding: '0 12px 10px', fontSize: 14, lineHeight: 1.5, color: '#1c1e21', wordBreak: 'break-word' }}>
          {message.length > 125 ? message.slice(0, 125) + '...' : message}
        </div>
      ) : (
        <div style={{ padding: '0 12px 10px', fontSize: 13, color: '#bcc0c4', fontStyle: 'italic' }}>
          填写主文案后显示...
        </div>
      )}

      {/* Media area */}
      {adType === 'image' && (
        imagePreviewURL ? (
          <img src={imagePreviewURL} alt="ad preview" style={{ display: 'block', width: '100%', maxHeight: 260, objectFit: 'cover' }} />
        ) : imageHash ? (
          <div style={{ background: 'linear-gradient(135deg,#667eea 0%,#764ba2 100%)', height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, gap: 6 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            图片已上传
          </div>
        ) : (
          <div style={{ background: '#f0f2f5', height: 220, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#bcc0c4', gap: 8 }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            <span style={{ fontSize: 12 }}>上传图片后显示</span>
          </div>
        )
      )}

      {adType === 'video' && (
        <div style={{ background: videoID ? '#1c1e21' : '#f0f2f5', height: 220, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: videoID ? '#fff' : '#bcc0c4', gap: 8, position: 'relative', overflow: 'hidden' }}>
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: videoID ? 'rgba(0,0,0,.08)' : 'rgba(0,0,0,.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
          </div>
          <span style={{ fontSize: 12, zIndex: 1 }}>{videoID ? '视频已上传' : '上传视频后显示'}</span>
        </div>
      )}

      {adType === 'carousel' && (
        <div style={{ height: 200, position: 'relative', overflow: 'hidden', background: '#f0f2f5' }}>
          {(carouselPreviews && carouselPreviews.length > 0) ? (
            <img src={carouselPreviews[0]} alt="carousel 1" style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bcc0c4', flexDirection: 'column', gap: 6, fontSize: 12 }}>
              <span style={{ fontSize: 24 }}>⊞</span>
              <span>轮播广告 — 上传卡片图片后显示</span>
            </div>
          )}
          <button style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,.85)', border: 'none', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'default', fontSize: 14, boxShadow: '0 1px 4px rgba(0,0,0,.2)' }}>‹</button>
          <button style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,.85)', border: 'none', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'default', fontSize: 14, boxShadow: '0 1px 4px rgba(0,0,0,.2)' }}>›</button>
          <div style={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 4 }}>
            {Array.from({ length: Math.min(carouselCount || 2, 5) }).map((_, i) => (
              <div key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: i === 0 ? '#1877f2' : 'rgba(0,0,0,.25)' }} />
            ))}
          </div>
        </div>
      )}

      {adType === 'catalog' && (
        <div style={{ background: '#f0f2f5', height: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#bcc0c4', gap: 6, fontSize: 12 }}>
          <span style={{ fontSize: 28 }}>🛍️</span>
          <span>动态商品广告 — 图片来自目录</span>
        </div>
      )}

      {/* Link card — face2 style: domain / headline / description / CTA */}
      {['image', 'video', 'catalog'].includes(adType) && (
        <div style={{ borderTop: '1px solid #e4e6eb', background: '#f2f3f5' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {domain && <div style={{ fontSize: 11, color: '#8a8d91', marginBottom: 3 }}>{domain}</div>}
              <div style={{ fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: headline ? '#1c1e21' : '#bcc0c4', lineHeight: 1.3 }}>
                {headline || '广告标题'}
              </div>
              {description && (
                <div style={{ fontSize: 12, color: '#65676b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>{description}</div>
              )}
            </div>
            {ctaLabel && (
              <button style={{ padding: '7px 14px', background: '#e2e5e9', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'default', whiteSpace: 'nowrap', flexShrink: 0, color: '#1c1e21' }}>
                {ctaLabel}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Reactions row */}
      <div style={{ padding: '8px 12px', display: 'flex', gap: 4, borderTop: '1px solid #e4e6eb' }}>
        {[['👍', '赞'], ['💬', '评论'], ['↗', '分享']].map(([icon, label]) => (
          <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, justifyContent: 'center', fontSize: 12, color: '#65676b', padding: '4px 0', cursor: 'default' }}>
            {icon} {label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── FB API Preview Panel (enhancement, shown when API returns html) ──────────

function AdPreviewPanel({ html, loading, error, onRefresh }: { html: string | null; loading: boolean; error?: string | null; onRefresh?: () => void }) {
  // Extract the inner iframe src so we can offer an "open in new tab" link
  const previewSrc = html ? (html.match(/src="([^"]+)"/)?.[1] ?? null) : null;
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginTop: 10 }}>
      <div style={{
        padding: '7px 12px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)',
        fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6,
      }}>
        <span>Facebook 帖子预览</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {previewSrc && (
            <a href={previewSrc} target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 10, color: 'var(--primary-light)', textDecoration: 'none', padding: '2px 6px', borderRadius: 4, border: '1px solid var(--border)' }}>
              ↗ 新标签页
            </a>
          )}
          {onRefresh && (
            <button onClick={onRefresh} disabled={loading} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: 'var(--primary-light)', padding: '2px 6px', borderRadius: 4 }}>
              {loading ? '加载中...' : '↺ 刷新'}
            </button>
          )}
        </div>
      </div>
      <div style={{ background: '#f0f2f5', minHeight: 60, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {loading ? (
          <div style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 8, color: '#999', fontSize: 12 }}>
            <div className="spinner" style={{ width: 14, height: 14 }} />
            请求 Facebook 预览中...
          </div>
        ) : html ? (
          <iframe
            srcDoc={html}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            style={{ width: '100%', border: 'none', height: 700, display: 'block' }}
            title="FB Ad Preview"
          />
        ) : error ? (
          <div style={{ padding: 16, fontSize: 11, color: '#f87171', textAlign: 'center', lineHeight: 1.6 }}>
            预览失败：{error}<br />
            <span style={{ color: '#bcc0c4' }}>点击「↺ 刷新」重试</span>
          </div>
        ) : (
          <div style={{ padding: 16, fontSize: 11, color: '#bcc0c4', textAlign: 'center', lineHeight: 1.6 }}>
            需要 Page ID 和素材<br />上传完成后点击「↺ 刷新」
          </div>
        )}
      </div>
    </div>
  );
}

// ─── FB Error Parser ──────────────────────────────────────────────────────────

interface FbErrorInfo {
  step: string;       // e.g. "广告系列"
  message: string;    // cleaned FB message
  code: string;       // e.g. "[FB #100]"
  hint: string;       // actionable Chinese hint
}

const FB_HINTS: Array<{ pattern: RegExp | string; hint: string }> = [
  { pattern: /spend.?cap|花费上限/i,         hint: '广告系列花费上限最低需设置 $100，或清空该字段不限制' },
  { pattern: /campaign_lifetime_budget|campaign.*budget/i, hint: '广告系列终身预算需 ≥ $100' },
  { pattern: /daily_budget|adset.*budget/i,  hint: '检查广告组预算金额，每日预算最低约 $1' },
  { pattern: /optimization_goal|billing_event/i, hint: '当前目标下该优化目标/计费方式不支持，请调整' },
  { pattern: /2490408|广告目标不支持选中的成效目标/i, hint: '成效目标与广告目标不匹配：请在当前广告目标下重新选择成效目标' },
  { pattern: /bid_strategy|bid_amount|ROAS/i, hint: '检查出价策略与出价金额的组合是否正确' },
  { pattern: /2490487|需要设置竞价金额或竞价限制/i, hint: '请检查出价策略：BID_CAP/COST_CAP 需填写 bid_amount，MIN_ROAS 需填写 roas 且优化目标为 VALUE' },
  { pattern: /1815857|竞价参考金额|必须在 bid_amount/i, hint: '此广告账户要求所有策略必须填写竞价参考金额（bid_amount），请在「出价设置」中补填金额后重试' },
  { pattern: /FB\s*#2\b|unexpected error/i, hint: 'Facebook 返回了系统级错误。优先检查：成效目标与广告目标是否匹配、Sales 转化是否为 WEBSITE+Pixel、频次上限是否只用于 REACH、版位与平台是否一致。' },
  { pattern: /page.*id|主页.*id/i,           hint: '主页 ID 无效或账号无该主页管理权限' },
  { pattern: /pixel|event_type|promoted_object/i, hint: '检查像素 ID 或转化事件配置' },
  { pattern: /image_hash/i,                  hint: '图片 hash 无效，请重新上传图片' },
  { pattern: /video_id/i,                    hint: '视频 ID 无效，请重新上传视频' },
  { pattern: /link_url|link$/i,              hint: '落地页 URL 无效，请检查链接格式' },
  { pattern: /access_token|token.*expired|OAuthException.*190/i, hint: '账号授权已过期，请返回账号管理页重新授权' },
  { pattern: /permission|#200\b/i,           hint: '账号缺少该操作权限，请检查广告账号权限设置' },
  { pattern: /targeting|age_min|age_max|geo_location/i, hint: '检查受众定向设置（年龄/地区/兴趣）' },
  { pattern: /instagram|actor/i,             hint: 'Instagram 账号 ID 无效，请重新选择或留空' },
  { pattern: /creative|adcreative/i,         hint: '检查广告素材与文案是否完整' },
];

function parseFbError(raw: string): FbErrorInfo {
  // Detect step prefix
  let step = '';
  let rest = raw;
  if (raw.startsWith('广告系列')) { step = '广告系列'; rest = raw.replace(/^广告系列[验证创建失败：]+/, ''); }
  else if (raw.startsWith('广告组')) { step = '广告组'; rest = raw.replace(/^广告组[验证创建失败：]+/, ''); }
  else if (raw.startsWith('广告创意')) { step = '广告创意'; rest = raw.replace(/^广告创意[验证创建失败：]+/, ''); }

  // Strip nested Chinese prefix (e.g. "广告组创建失败：")
  rest = rest.replace(/^广告[系列组创意]+创建失败：/, '');

  // Extract [FB #xxx] code
  const codeMatch = rest.match(/\[FB #[\d/]+\]/);
  const code = codeMatch ? codeMatch[0] : '';
  const message = rest.replace(/\s*\[FB #[\d/]+\]/, '').trim();
  const hasInlineGuidance = /请检查|优先检查|建议[:：]/.test(message);

  // Find matching hint
  let hint = '';
  if (!hasInlineGuidance) {
    for (const { pattern, hint: h } of FB_HINTS) {
      if (typeof pattern === 'string' ? raw.includes(pattern) : pattern.test(raw)) {
        // Avoid duplicated hint text when message already contains similar guidance.
        if (!message.includes(h)) {
          hint = h;
        }
        break;
      }
    }
  }
  return { step, message, code, hint };
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function CreateAdWizard() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const { platform: urlPlatformParam } = useParams<{ platform: string }>();
  const { brand, brandId, siteId, hasPermission, adContext } = useAppStore();
  const userLevel = adContext?.data_level ?? 1;
  const lockedSiteId = useRef(siteId);

  // ── Skip-step mode for Facebook (新增组 / 新增广告) ────────────────────────
  const wizardMode = searchParams.get('mode') as 'add_adset' | 'add_ad' | null;
  const existingCampaignId = searchParams.get('campaign_id') ? Number(searchParams.get('campaign_id')) : 0;
  const existingAdSetId = searchParams.get('adset_id') ? Number(searchParams.get('adset_id')) : 0;
  const urlAccountId = searchParams.get('account_id') || '';
  const initialStep: WizardStep = wizardMode === 'add_ad' ? 3 : wizardMode === 'add_adset' ? 2 : 1;
  const [parentDataLoaded, setParentDataLoaded] = useState(!wizardMode);
  const platformLocked = !!urlPlatformParam || !!wizardMode;

  // ── TikTok "create under" mode ────────────────────────────────────────────
  const underEntity = searchParams.get('under') as 'campaign' | 'adset' | null;
  const parentId = Number(searchParams.get('parent_id')) || 0;
  const createUnder = underEntity && parentId > 0 ? { entity: underEntity, parentId } : undefined;

  // ── Global ────────────────────────────────────────────────────────────────
  const [platform, setPlatform] = useState<AdPlatform>((urlPlatformParam as AdPlatform) || (searchParams.get('platform') as AdPlatform) || 'facebook');
  const activePlatform = useMemo(() => AD_PLATFORMS.find((p) => p.key === platform) ?? AD_PLATFORMS[0], [platform]);
  const [step, setStep] = useState<WizardStep>(initialStep);
  const [maxVisitedStep, setMaxVisitedStep] = useState<WizardStep>(initialStep);
  const [tikTokStep, setTikTokStep] = useState<WizardStep>(1);
  const [tikTokObjective, setTikTokObjective] = useState('TRAFFIC');
  const [tikTokAdFormat, setTikTokAdFormat] = useState('SINGLE_VIDEO');
  const [submitting, setSubmitting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: ToastType } | null>(null);

  const showToast = useCallback((msg: string, type: ToastType = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), type === 'error' ? 6000 : 3500);
  }, []);

  // Block site switching while creating
  useEffect(() => {
    if (siteId !== lockedSiteId.current) {
      showToast('新建广告期间不允许切换站点', 'error');
      useAppStore.getState().selectSiteById(lockedSiteId.current);
    }
  }, [siteId]);

  // ── Account ───────────────────────────────────────────────────────────────
  const [accountOptions, setAccountOptions] = useState<WizardAccountOption[]>([]);
  const [selectedAccountID, setSelectedAccountID] = useState('');

  useEffect(() => {
    if (!brandId) return;
    const loadOptions = async () => {
      try {
        if (platform === 'facebook') {
          const res = await accountsApi.listFacebookOptions(brandId, siteId, 'campaign_write');
          // TODO: 权限暂时放开，不再过滤 is_mine (2026-04-13)
          // ? res.list.filter((o) => o.is_mine).map((o) => ({ id: o.id, account_id: o.account_id, account_name: o.account_name, is_mine: true }))
          const opts: WizardAccountOption[] = Array.isArray(res.list)
            ? res.list.map((o) => ({ id: o.id, account_id: o.account_id, account_name: o.account_name, is_mine: true }))
            : [];
          setAccountOptions(opts);
          setSelectedAccountID((prev) => {
            const urlAccountID = resolveAccountIDFromOptions(opts, urlAccountId);
            if (urlAccountID) return urlAccountID;
            if (prev && opts.some((o) => o.account_id === prev)) return prev;
            return opts[0]?.account_id ?? '';
          });
          return;
        }

        const res = await accountsApi.list({ brand_id: brandId, site_id: siteId, platform, scope: 'campaign_write', page: 1, size: 100 });
        // TODO: 权限暂时放开，不再过滤 is_mine (2026-04-13)
        // ? res.data.filter((o) => o.is_mine).map((o) => ({ id: o.id, account_id: o.account_id, account_name: o.account_name, is_mine: true }))
        const opts: WizardAccountOption[] = Array.isArray(res.data)
          ? res.data.map((o) => ({ id: o.id, account_id: o.account_id, account_name: o.account_name, is_mine: true }))
          : [];
        setAccountOptions(opts);
        setSelectedAccountID((prev) => {
          const urlAccountID = resolveAccountIDFromOptions(opts, urlAccountId);
          if (urlAccountID) return urlAccountID;
          if (prev && opts.some((o) => o.account_id === prev)) return prev;
          return opts[0]?.account_id ?? '';
        });
      } catch {
        setAccountOptions([]);
        setSelectedAccountID('');
      }
    };

    void loadOptions();
  }, [brandId, siteId, platform, urlAccountId]);

  // ── Load parent entity data for skip-step modes ────────────────────────────
  useEffect(() => {
    if (!brandId || !wizardMode) return;
    const loadParentData = async () => {
      try {
        if (wizardMode === 'add_adset' && existingCampaignId > 0) {
          // Load campaign data to get objective, budget scope, account_id, etc.
          const detail = await accountsApi.getFacebookCampaignEditDetail(brandId, existingCampaignId, siteId);
          if (detail.campaign) {
            const c = detail.campaign;
            setCampaignName(c.name || '');
            setCampaignObjective(c.campaign_objective || 'OUTCOME_SALES');
            setCampaignStatus(c.campaign_status || 'PAUSED');
            if (c.campaign_budget_scope) setSalesBudgetScope(c.campaign_budget_scope);
            if (c.campaign_budget_scope === 'campaign') setCboEnabled(true);
            if (c.daily_budget > 0) setDailyBudget(String(c.daily_budget));
            if (c.campaign_lifetime_budget > 0) {
              setCampaignLifetimeBudget(String(c.campaign_lifetime_budget));
              setUseCampaignLifetimeBudget(true);
            }
            if (c.spend_cap > 0) setSpendCap(String(c.spend_cap));
            if (c.account_id) setSelectedAccountID(c.account_id);
            if (urlAccountId) setSelectedAccountID(urlAccountId);
            // 标签回填
            if (c.tags && Object.keys(c.tags).length > 0) {
              setCampaignTags(c.tags as Record<string, string>);
            }
          }
        } else if (wizardMode === 'add_ad' && existingAdSetId > 0) {
          // Load adset data to get campaign context + adset-level config
          const detail = await accountsApi.getFacebookAdSetEditDetail(brandId, existingAdSetId, siteId);
          if (detail.campaign) {
            const c = detail.campaign;
            setCampaignName(c.name || '');
            setCampaignObjective(c.campaign_objective || 'OUTCOME_SALES');
            setCampaignStatus(c.campaign_status || 'PAUSED');
            if (c.campaign_budget_scope) setSalesBudgetScope(c.campaign_budget_scope);
            if (c.account_id) setSelectedAccountID(c.account_id);
            if (c.tags && Object.keys(c.tags).length > 0) {
              setCampaignTags(c.tags as Record<string, string>);
            }
          }
          if (detail.adset) {
            const a = detail.adset;
            setAdsetName(a.name || '');
            setBudgetType(a.budget_type === 'lifetime' ? 'lifetime' : 'daily');
            if (a.adset_daily_budget > 0) setAdsetDailyBudget(String(a.adset_daily_budget));
            if (a.adset_lifetime_budget > 0) setAdsetLifetimeBudget(String(a.adset_lifetime_budget));
            if (a.optimization_goal) setOptimizationGoal(a.optimization_goal);
            if (a.destination_type) setDestinationType(a.destination_type);
            if (a.bid_strategy) setBidStrategy(a.bid_strategy);
            if (a.bid_amount > 0) setBidAmount(String(a.bid_amount));
            if (a.bid_constraints_roas && a.bid_constraints_roas > 0) setBidConstraintsROAS(String(a.bid_constraints_roas));
            if (a.start_time) setStartTime(a.start_time);
            if (a.end_time) setEndTime(a.end_time);
            if (Array.isArray(a.countries)) setSelectedCountries(a.countries);
            if (Array.isArray(a.excluded_countries)) setExcludedCountries(a.excluded_countries);
            if (Array.isArray(a.genders)) setGenders(a.genders.includes(1) ? '1' : a.genders.includes(2) ? '2' : 'all');
            if (a.age_min) setAgeMin(String(a.age_min));
            if (a.age_max) setAgeMax(String(a.age_max));
            if (Array.isArray(a.interests)) setSelectedInterests(a.interests);
            if (Array.isArray(a.behaviors)) setSelectedBehaviors(a.behaviors);
            if (Array.isArray(a.custom_audience_ids)) setSelectedCustomAudienceIDs(a.custom_audience_ids);
            if (Array.isArray(a.excluded_custom_audience_ids)) setExcludedCustomAudienceIDs(a.excluded_custom_audience_ids);
            if (a.adset_status) setAdSetStatus(a.adset_status);
            if (a.frequency_cap && a.frequency_cap > 0) setFrequencyCap(String(a.frequency_cap));
            if (a.frequency_cap_reset_period && a.frequency_cap_reset_period > 0) setFrequencyCapResetPeriod(String(a.frequency_cap_reset_period));
            if (Array.isArray(a.locales)) setLocales(a.locales);
            setFacebookAttributionModel(a.is_incremental_attribution_enabled ? 'incremental' : 'standard');
            if (a.tags && Object.keys(a.tags).length > 0) {
              setAdsetTags(a.tags as Record<string, string>);
            }
            // Pre-fill some adset context that step3 might need (e.g., pixel)
            if (a.pixel_id) setSelectedPixelID(a.pixel_id);
            if (a.custom_event_type) setCustomEventType(a.custom_event_type);
            if (a.product_catalog_id) {
              setShowCatalog(true);
              setSelectedCatalogID(a.product_catalog_id);
            }
            if (a.product_set_id) setSelectedProductSetID(a.product_set_id);
          }
        }
      } catch (e) {
        showToast(e instanceof Error ? e.message : '加载父级数据失败', 'error');
      } finally {
        setParentDataLoaded(true);
      }
    };
    void loadParentData();
  }, [brandId, wizardMode, existingCampaignId, existingAdSetId]);

  useEffect(() => {
    accountsApi.getFacebookCreateConfig()
      .then((cfg) => {
        const backendObjectives = Array.isArray(cfg.campaign_objectives) && cfg.campaign_objectives.length > 0
          ? cfg.campaign_objectives
          : OBJECTIVES.map((o) => o.value);
        const knownObjectives = OBJECTIVES.map((o) => o.value);
        const objectiveList = backendObjectives.filter((v) => knownObjectives.includes(v));
        const effectiveObjectives = objectiveList.length > 0 ? objectiveList : backendObjectives;
        setObjectiveValues(effectiveObjectives);

        const matrix = cfg.goal_matrix && typeof cfg.goal_matrix === 'object' ? cfg.goal_matrix : {};
        const mapped: Record<string, { value: string; label: string }[]> = {};
        Object.entries(matrix).forEach(([objective, goals]) => {
          const list = Array.isArray(goals) ? goals : [];
          mapped[objective] = list.map((goal) => ({
            value: goal,
            label: goalLabelByValue(goal),
          }));
        });
        if (Object.keys(mapped).length > 0) {
          setGoalOptionsByObjective(mapped);
        }

        setCampaignObjective((prev) => {
          if (effectiveObjectives.includes(prev)) return prev;
          const fallback = (cfg.default_objective && effectiveObjectives.includes(cfg.default_objective))
            ? cfg.default_objective
            : effectiveObjectives[0];
          return fallback || prev;
        });
      })
      .catch(() => {});
  }, []);

  // ── Step 1: Campaign ──────────────────────────────────────────────────────
  const [campaignName, setCampaignName] = useState('');
  const [campaignObjective, setCampaignObjective] = useState('OUTCOME_SALES');
  const [objectiveValues, setObjectiveValues] = useState<string[]>(OBJECTIVES.map((o) => o.value));
  const [campaignStatus, setCampaignStatus] = useState('ACTIVE');
  const [cboEnabled, setCboEnabled] = useState(false); // Campaign Budget Optimization toggle
  const [salesBudgetScope, setSalesBudgetScope] = useState<'campaign' | 'adset'>('campaign');
  const [dailyBudget, setDailyBudget] = useState(''); // campaign-level budget (USD)
  const isSalesObjective = campaignObjective === 'OUTCOME_SALES';
  const isTrafficObjective = campaignObjective === 'OUTCOME_TRAFFIC';
  const supportsCBO = campaignObjective === 'OUTCOME_AWARENESS';
  const usesBudgetScopeSelector = isSalesObjective || isTrafficObjective;
  const usesCampaignBudget = usesBudgetScopeSelector ? salesBudgetScope === 'campaign' : cboEnabled;
  // Campaign extensions
  const [spendCap, setSpendCap] = useState('');
  const [campaignLifetimeBudget, setCampaignLifetimeBudget] = useState('');
  const [useCampaignLifetimeBudget, setUseCampaignLifetimeBudget] = useState(false);
  const campaignBudgetTypeLabel = useCampaignLifetimeBudget ? '总预算' : '日预算';
  const campaignBudgetAmount = useCampaignLifetimeBudget ? campaignLifetimeBudget : dailyBudget;
  const campaignBudgetSummary = `使用推广系列预算：${campaignBudgetTypeLabel} ${Number(campaignBudgetAmount || 0).toFixed(2)} USD`;

  // ── 动态广告标签 ──────────────────────────────────────────────────────────
  const [tagConfig, setTagConfig] = useState<TagDefinition[]>([]);
  const [campaignTags, setCampaignTags] = useState<Record<string, string>>({});
  const [adsetTags, setAdsetTags] = useState<Record<string, string>>({});
  const [adTags, setAdTags] = useState<Record<string, string>>({});
  const siteCode = brand === 'vooglam' ? 'v' : 'z';
  const channelCode = platform === 'facebook' ? 'fb' : platform === 'google' ? 'google' : platform === 'tiktok' ? 'tiktok' : '';

  // ── 标签配置拉取 ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!channelCode) { setTagConfig([]); return; }
    adTagsApi.getConfig(siteCode, channelCode)
      .then(res => setTagConfig(res?.data ?? []))
      .catch(() => setTagConfig([]));
  }, [siteCode, channelCode]);

  // ── Step 2: AdSet ─────────────────────────────────────────────────────────
  const [adsetName, setAdsetName] = useState('');
  const [budgetType, setBudgetType] = useState<'daily' | 'lifetime'>('daily');
  const [adsetDailyBudget, setAdsetDailyBudget] = useState('10');
  const [adsetLifetimeBudget, setAdsetLifetimeBudget] = useState('');
  const [billingEvent] = useState('');
  const [optimizationGoal, setOptimizationGoal] = useState('OFFSITE_CONVERSIONS');
  const [goalOptionsByObjective, setGoalOptionsByObjective] = useState<Record<string, { value: string; label: string }[]>>(DEFAULT_GOALS_BY_OBJECTIVE);
  const [destinationType, setDestinationType] = useState('WEBSITE');
  const [selectedCountries, setSelectedCountries] = useState<string[]>(DEFAULT_INCLUDED_COUNTRIES);
  const [excludedCountries, setExcludedCountries] = useState<string[]>(DEFAULT_EXCLUDED_COUNTRIES);
  const [geoLocations, setGeoLocations] = useState<FacebookGeoLocation[]>([]);
  const [geoLoading, setGeoLoading] = useState(false);
  const [ageMin, setAgeMin] = useState('18');
  const [ageMax, setAgeMax] = useState('65');
  const [genders, setGenders] = useState<'all' | '1' | '2'>('all');
  const [bidStrategy, setBidStrategy] = useState('LOWEST_COST_WITHOUT_CAP');
  const [bidAmount, setBidAmount] = useState('');
  const [startTime, setStartTime] = useState(() => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  });
  const [endTime, setEndTime] = useState('');
  // AdSet extensions
  const [adSetStatus, setAdSetStatus] = useState('ACTIVE');
  const [frequencyCap, setFrequencyCap] = useState('');
  const [frequencyCapResetPeriod, setFrequencyCapResetPeriod] = useState('7');
  const [facebookAttributionModel, setFacebookAttributionModel] = useState<FacebookAttributionModel>('standard');
  const [clickAttributionDays, setClickAttributionDays] = useState<'1' | '7'>('1');
  const [engagedViewAttributionDays, setEngagedViewAttributionDays] = useState<'0' | '1'>('0');
  const [viewAttributionDays, setViewAttributionDays] = useState<'0' | '1'>('0');
  const [excludedCustomAudienceIDs, setExcludedCustomAudienceIDs] = useState<string[]>([]);
  const [publisherPlatforms, setPublisherPlatforms] = useState<string[]>(() => getDefaultFacebookPlacementState().publisherPlatforms);
  const [facebookPositions, setFacebookPositions] = useState<string[]>(() => getDefaultFacebookPlacementState().facebookPositions);
  const [instagramPositions, setInstagramPositions] = useState<string[]>(() => getDefaultFacebookPlacementState().instagramPositions);
  const [devicePlatforms, setDevicePlatforms] = useState<string[]>(() => getDefaultFacebookPlacementState().devicePlatforms);
  const [userOS, setUserOS] = useState<string[]>(() => getDefaultFacebookPlacementState().userOS);
  const [placementMode, setPlacementMode] = useState<FacebookPlacementMode>('automatic');
  const [behaviorSectionCollapsed, setBehaviorSectionCollapsed] = useState(true);
  const [includeAudienceSectionCollapsed, setIncludeAudienceSectionCollapsed] = useState(true);
  const [excludeAudienceSectionCollapsed, setExcludeAudienceSectionCollapsed] = useState(true);
  const [locales, setLocales] = useState<number[]>(DEFAULT_FACEBOOK_LOCALES);
  const [bidConstraintsROAS, setBidConstraintsROAS] = useState('');

  // Interest search
  const [interestQuery, setInterestQuery] = useState('');
  const [interestOptions, setInterestOptions] = useState<FacebookInterest[]>([]);
  const [interestDropdownOpen, setInterestDropdownOpen] = useState(false);
  const [interestSearching, setInterestSearching] = useState(false);
  const [selectedInterests, setSelectedInterests] = useState<{ id: string; name: string }[]>([]);
  const [behaviorFilter, setBehaviorFilter] = useState('');
  const [behaviorOptions, setBehaviorOptions] = useState<FacebookBehavior[]>([]);
  const [behaviorLoading, setBehaviorLoading] = useState(false);
  const [selectedBehaviors, setSelectedBehaviors] = useState<{ id: string; name: string }[]>([]);
  const interestTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const interestDropdownRef = useRef<HTMLDivElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const formCardRef = useRef<HTMLDivElement>(null);

  // Conversion tracking (SALES only)
  const [pixels, setPixels] = useState<FacebookPixel[]>([]);
  const [selectedPixelID, setSelectedPixelID] = useState('');
  const [manualPixelID, setManualPixelID] = useState('');
  const [customEventType, setCustomEventType] = useState('PURCHASE');
  const [catalogs, setCatalogs] = useState<FacebookCatalog[]>([]);
  const [productSets, setProductSets] = useState<FacebookProductSet[]>([]);
  const [selectedCatalogID, setSelectedCatalogID] = useState('');
  const [selectedProductSetID, setSelectedProductSetID] = useState('');
  const [showCatalog, setShowCatalog] = useState(false);
  const [syncingCatalogs, setSyncingCatalogs] = useState(false);
  const [syncingProductSets, setSyncingProductSets] = useState(false);
  const productSetsCacheRef = useRef<Record<string, FacebookProductSet[]>>({});
  const getProductSetsCacheKey = useCallback((accountID: string, catalogID: string) => `${accountID}::${catalogID}`, []);
  const catalogOptions = useMemo(
    () => catalogs.map((catalog) => ({ value: catalog.id, label: catalog.name || catalog.id })),
    [catalogs],
  );
  const productSetOptions = useMemo(
    () => productSets.map((set) => ({
      value: set.id,
      label: `${set.name || set.id}${typeof set.product_count === 'number' ? ` (${set.product_count} 件)` : ''}`,
    })),
    [productSets],
  );
  const pickDefaultCatalogID = useCallback((items: FacebookCatalog[], currentID: string) => {
    if (currentID && items.some((item) => item.id === currentID)) return currentID;
    const exact = items.find((item) => item.name.trim().toLowerCase() === DEFAULT_FACEBOOK_CATALOG_NAME.toLowerCase());
    if (exact) return exact.id;
    const partial = items.find((item) => item.name.toLowerCase().includes(DEFAULT_FACEBOOK_CATALOG_NAME.toLowerCase()));
    return partial?.id ?? items[0]?.id ?? '';
  }, []);
  const pickDefaultPixelID = useCallback((items: FacebookPixel[], currentID: string) => {
    const exact = items.find((item) => item.name.trim().toLowerCase() === DEFAULT_FACEBOOK_PIXEL_NAME.toLowerCase());
    if (exact) return exact.id;
    const partial = items.find((item) => item.name.toLowerCase().includes(DEFAULT_FACEBOOK_PIXEL_NAME.toLowerCase()));
    if (partial) return partial.id;
    if (currentID && items.some((item) => item.id === currentID)) return currentID;
    return currentID;
  }, []);
  const pickDefaultPageID = useCallback((items: FacebookPage[], currentID: string) => {
    return pickDefaultFacebookPageID(items, currentID, brand);
  }, [brand]);
  const pickDefaultIgActorID = useCallback((items: FacebookInstagramAccount[], currentID: string) => {
    return pickDefaultFacebookInstagramActorID(items, currentID, brand);
  }, [brand]);
  const [syncingGeo, setSyncingGeo] = useState(false);
  const [syncingBehaviors, setSyncingBehaviors] = useState(false);

  const showConversion = campaignObjective === 'OUTCOME_SALES';
  const showDestination = campaignObjective === 'OUTCOME_TRAFFIC' || campaignObjective === 'OUTCOME_SALES';
  const showPixelSelector = campaignObjective === 'OUTCOME_SALES' || (campaignObjective === 'OUTCOME_TRAFFIC' && optimizationGoal === 'LANDING_PAGE_VIEWS');
  const showFacebookAttributionModel = platform === 'facebook' && (campaignObjective === 'OUTCOME_TRAFFIC' || showConversion);
  const isIncrementalAttribution = showFacebookAttributionModel && facebookAttributionModel === 'incremental';
  const pixelRequired = optimizationGoal === 'OFFSITE_CONVERSIONS' || optimizationGoal === 'VALUE';
  const frequencyModeOptions = [
    { value: 'target', label: '目标' },
    { value: 'cap', label: '上限' },
  ] as const;
  const [frequencyMode, setFrequencyMode] = useState<'target' | 'cap'>('target');
  const bidStrategyOptions = useMemo(() => {
    if (campaignObjective === 'OUTCOME_SALES') {
      return [
        { value: 'LOWEST_COST_WITHOUT_CAP', label: '最高数量或价值' },
        { value: 'COST_CAP', label: '单次成效费用目标' },
        { value: 'LOWEST_COST_WITH_MIN_ROAS', label: '广告花费回报目标' },
      ];
    }
    return [
      { value: 'LOWEST_COST_WITHOUT_CAP', label: '最高数量' },
      { value: 'COST_CAP', label: '单次成效费用目标' },
    ];
  }, [campaignObjective]);

  useEffect(() => {
    if (!supportsCBO) {
      setCboEnabled(false);
    }
    if (!usesBudgetScopeSelector) {
      setSalesBudgetScope('adset');
    }
    if (campaignObjective !== 'OUTCOME_SALES') {
      setShowCatalog(false);
      setSelectedCatalogID('');
      setSelectedProductSetID('');
    }
    if (campaignObjective !== 'OUTCOME_SALES' && bidStrategy === 'LOWEST_COST_WITH_MIN_ROAS') {
      setBidStrategy('LOWEST_COST_WITHOUT_CAP');
      setBidConstraintsROAS('');
    }
    // AWARENESS without CBO: reset bid strategy to default
    if (campaignObjective === 'OUTCOME_AWARENESS' && !cboEnabled && bidStrategy !== 'LOWEST_COST_WITHOUT_CAP') {
      setBidStrategy('LOWEST_COST_WITHOUT_CAP');
      setBidAmount('');
      setBidConstraintsROAS('');
    }
  }, [campaignObjective, supportsCBO, bidStrategy, cboEnabled]);

  // Objective changed: clear stale optimization goal that is not supported by the new objective.
  useEffect(() => {
    if (!optimizationGoal) return;
    const supportedGoals = goalOptionsByObjective[campaignObjective] || [];
    const supported = supportedGoals.some((g) => g.value === optimizationGoal);
    if (!supported) {
      setOptimizationGoal('');
    }
  }, [campaignObjective, optimizationGoal, goalOptionsByObjective]);

  useEffect(() => {
    if (platform !== 'facebook') return;
    if (campaignObjective !== 'OUTCOME_SALES') return;
    if (bidStrategy === 'COST_CAP' && optimizationGoal !== 'OFFSITE_CONVERSIONS') {
      setOptimizationGoal('OFFSITE_CONVERSIONS');
      return;
    }
    if (bidStrategy === 'LOWEST_COST_WITH_MIN_ROAS' && optimizationGoal !== 'VALUE') {
      setOptimizationGoal('VALUE');
    }
  }, [platform, campaignObjective, bidStrategy, optimizationGoal]);

  // Load pixels when entering step 2 (all objectives may need pixel)
  useEffect(() => {
    if (platform !== 'facebook') return;
    if (step !== 2 || !selectedAccountID || !showPixelSelector) return;
    let cancelled = false;
    const accountID = selectedAccountID;
    accountsApi.listFacebookPixels({ brand_id: brandId, site_id: siteId, account_id: selectedAccountID })
      .then((r) => {
        if (cancelled || accountID !== selectedAccountID) return;
        const list = Array.isArray(r.list) ? r.list : [];
        setPixels(list);
        setSelectedPixelID((current) => pickDefaultPixelID(list, current));
      })
      .catch(() => { if (!cancelled) setPixels([]); });
    setCustomAudiencesLoading(true);
    accountsApi.listFacebookCustomAudiences({ brand_id: brandId, site_id: siteId, account_id: selectedAccountID })
      .then((r) => {
        if (cancelled || accountID !== selectedAccountID) return;
        setCustomAudiences(Array.isArray(r.list) ? r.list : []);
      })
      .catch(() => { if (!cancelled) setCustomAudiences([]); })
      .finally(() => { if (!cancelled) setCustomAudiencesLoading(false); });
    return () => { cancelled = true; };
  }, [platform, step, selectedAccountID, brandId, siteId, showPixelSelector, pickDefaultPixelID]);

  // Load catalogs immediately when user enables catalog ads (step 1 checkbox)
  useEffect(() => {
    if (platform !== 'facebook') return;
    if (!showCatalog || !selectedAccountID) { setCatalogs([]); setSelectedCatalogID(''); setProductSets([]); setSelectedProductSetID(''); return; }
    let cancelled = false;
    const accountID = selectedAccountID;
    accountsApi.listFacebookCatalogs({ brand_id: brandId, site_id: siteId, account_id: selectedAccountID })
      .then((r) => {
        if (cancelled || accountID !== selectedAccountID) return;
        const list = Array.isArray(r.list) ? r.list : [];
        setCatalogs(list);
        setSelectedCatalogID((current) => pickDefaultCatalogID(list, current));
      })
      .catch(() => {
        if (cancelled || accountID !== selectedAccountID) return;
        setCatalogs([]);
        setSelectedCatalogID('');
      });
    return () => { cancelled = true; };
  }, [platform, showCatalog, selectedAccountID, brandId, siteId, pickDefaultCatalogID]);

  // Load product sets when catalog changes
  useEffect(() => {
    if (platform !== 'facebook') return;
    if (!selectedCatalogID || !selectedAccountID) { setProductSets([]); setSelectedProductSetID(''); return; }
    const cacheKey = getProductSetsCacheKey(selectedAccountID, selectedCatalogID);
    const accountID = selectedAccountID;
    const catalogID = selectedCatalogID;
    const cached = productSetsCacheRef.current[cacheKey];
    if (cached) {
      setProductSets(cached);
      setSelectedProductSetID((current) => (current && cached.some((item) => item.id === current) ? current : ''));
      return;
    }
    let cancelled = false;
    setProductSets([]);
    accountsApi.listFacebookProductSets({ brand_id: brandId, site_id: siteId, account_id: selectedAccountID, catalog_id: selectedCatalogID })
      .then((r) => {
        if (cancelled || accountID !== selectedAccountID || catalogID !== selectedCatalogID) return;
        const list = Array.isArray(r.list) ? r.list : [];
        productSetsCacheRef.current[cacheKey] = list;
        setProductSets(list);
        setSelectedProductSetID((current) => (current && list.some((item) => item.id === current) ? current : ''));
      })
      .catch(() => {
        if (cancelled || accountID !== selectedAccountID || catalogID !== selectedCatalogID) return;
        setProductSets([]);
        setSelectedProductSetID('');
      });
    return () => { cancelled = true; };
  }, [platform, selectedCatalogID, selectedAccountID, brandId, siteId, getProductSetsCacheKey]);

  // Load geo locations when account selected
  useEffect(() => {
    if (platform !== 'facebook') return;
    if (!selectedAccountID) { setGeoLocations([]); setGeoLoading(false); return; }
    setGeoLoading(true);
    accountsApi.listFacebookGeoLocations({ brand_id: brandId, site_id: siteId, account_id: selectedAccountID })
      .then((r) => setGeoLocations(Array.isArray(r.list) ? r.list : []))
      .catch(() => setGeoLocations([]))
      .finally(() => setGeoLoading(false));
  }, [platform, selectedAccountID, brandId]);

  useEffect(() => {
    if (platform !== 'facebook') return;
    if (step !== 2 || !selectedAccountID) { setBehaviorOptions([]); setBehaviorLoading(false); return; }
    setBehaviorLoading(true);
    accountsApi.listFacebookBehaviors({ brand_id: brandId, site_id: siteId, account_id: selectedAccountID })
      .then((r) => setBehaviorOptions(Array.isArray(r.list) ? r.list : []))
      .catch(() => setBehaviorOptions([]))
      .finally(() => setBehaviorLoading(false));
  }, [platform, step, selectedAccountID, brandId, siteId]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (interestDropdownRef.current && !interestDropdownRef.current.contains(e.target as Node)) {
        setInterestDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Interest search with debounce
  useEffect(() => {
    if (platform !== 'facebook') return;
    if (interestTimer.current) clearTimeout(interestTimer.current);
    if (!interestQuery.trim() || interestQuery.length < 2) {
      setInterestOptions([]);
      setInterestDropdownOpen(false);
      setInterestSearching(false);
      return;
    }
    setInterestSearching(true);
    interestTimer.current = setTimeout(() => {
      accountsApi.searchFacebookInterests({ brand_id: brandId, site_id: siteId, account_id: selectedAccountID, q: interestQuery.trim() })
        .then((r) => setInterestOptions(Array.isArray(r.list) ? r.list : []))
        .catch(() => setInterestOptions([]))
        .finally(() => setInterestSearching(false));
    }, 400);
  }, [platform, interestQuery, brandId, selectedAccountID]);

  const toggleInterest = (i: FacebookInterest) => {
    setSelectedInterests((prev) => (
      prev.some((x) => x.id === i.id)
        ? prev.filter((x) => x.id !== i.id)
        : [...prev, { id: i.id, name: i.name }]
    ));
  };
  const removeInterest = (id: string) => setSelectedInterests((prev) => prev.filter((x) => x.id !== id));
  const addBehavior = (b: FacebookBehavior) => {
    if (!selectedBehaviors.find((x) => x.id === b.id)) {
      setSelectedBehaviors((prev) => [...prev, { id: b.id, name: b.name }]);
    }
    setBehaviorFilter('');
  };
  const removeBehavior = (id: string) => setSelectedBehaviors((prev) => prev.filter((x) => x.id !== id));

  const handleSyncCatalogs = useCallback(async () => {
    if (!selectedAccountID || syncingCatalogs) return;
    setSyncingCatalogs(true);
    try {
      const r = await accountsApi.listFacebookCatalogs({ brand_id: brandId, site_id: siteId, account_id: selectedAccountID, refresh: true });
      const list = Array.isArray(r.list) ? r.list : [];
      setCatalogs(list);
      setSelectedCatalogID((current) => pickDefaultCatalogID(list, current));
    } finally {
      setSyncingCatalogs(false);
    }
  }, [brandId, selectedAccountID, syncingCatalogs, siteId, pickDefaultCatalogID]);

  const handleSyncProductSets = useCallback(async () => {
    if (!selectedAccountID || !selectedCatalogID || syncingProductSets) return;
    setSyncingProductSets(true);
    try {
      const r = await accountsApi.listFacebookProductSets({ brand_id: brandId, site_id: siteId, account_id: selectedAccountID, catalog_id: selectedCatalogID, refresh: true });
      const list = Array.isArray(r.list) ? r.list : [];
      productSetsCacheRef.current[getProductSetsCacheKey(selectedAccountID, selectedCatalogID)] = list;
      setProductSets(list);
      setSelectedProductSetID((current) => (current && list.some((item) => item.id === current) ? current : ''));
    } finally {
      setSyncingProductSets(false);
    }
  }, [brandId, selectedAccountID, selectedCatalogID, syncingProductSets, siteId, getProductSetsCacheKey]);

  const handleSyncGeo = useCallback(async () => {
    if (!selectedAccountID || syncingGeo) return;
    setSyncingGeo(true);
    setGeoLoading(true);
    try {
      const r = await accountsApi.listFacebookGeoLocations({ brand_id: brandId, site_id: siteId, account_id: selectedAccountID, refresh: true });
      setGeoLocations(Array.isArray(r.list) ? r.list : []);
    } finally {
      setGeoLoading(false);
      setSyncingGeo(false);
    }
  }, [brandId, selectedAccountID, syncingGeo, siteId]);

  const handleSyncBehaviors = useCallback(async () => {
    if (!selectedAccountID || syncingBehaviors) return;
    setSyncingBehaviors(true);
    setBehaviorLoading(true);
    try {
      const r = await accountsApi.listFacebookBehaviors({ brand_id: brandId, site_id: siteId, account_id: selectedAccountID, refresh: true });
      setBehaviorOptions(Array.isArray(r.list) ? r.list : []);
    } finally {
      setBehaviorLoading(false);
      setSyncingBehaviors(false);
    }
  }, [brandId, selectedAccountID, syncingBehaviors, siteId]);

  const sortedGeoLocations = useMemo(() => {
    const items = [...geoLocations];
    items.sort((a, b) => {
      const ac = (a.country_code || a.key || '').toUpperCase();
      const bc = (b.country_code || b.key || '').toUpperCase();
      const ai = FACEBOOK_GEO_PRIORITY.indexOf(ac);
      const bi = FACEBOOK_GEO_PRIORITY.indexOf(bc);
      if (ai !== -1 || bi !== -1) {
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      }
      return a.name.localeCompare(b.name, 'zh-CN');
    });
    return items;
  }, [geoLocations]);
  const geoOptions = useMemo(() => {
    const seen = new Set<string>();
    const options = sortedGeoLocations
      .map((g) => {
        const code = (g.country_code || g.key || '').toUpperCase();
        if (!code || seen.has(code)) return null;
        seen.add(code);
        return { value: code, label: `${g.name || FACEBOOK_COUNTRY_FALLBACK_LABELS[code] || code} (${code})` };
      })
      .filter((item): item is { value: string; label: string } => item !== null);
    [...DEFAULT_INCLUDED_COUNTRIES, ...DEFAULT_EXCLUDED_COUNTRIES].forEach((code) => {
      if (!seen.has(code)) {
        options.push({ value: code, label: `${FACEBOOK_COUNTRY_FALLBACK_LABELS[code] || code} (${code})` });
        seen.add(code);
      }
    });
    return options;
  }, [sortedGeoLocations]);

  const [syncingAssets, setSyncingAssets] = useState(false);
  const [customAudiences, setCustomAudiences] = useState<FacebookCustomAudience[]>([]);
  const [customAudiencesLoading, setCustomAudiencesLoading] = useState(false);
  const [includeAudienceFilter, setIncludeAudienceFilter] = useState('');
  const [excludeAudienceFilter, setExcludeAudienceFilter] = useState('');
  const [selectedCustomAudienceIDs, setSelectedCustomAudienceIDs] = useState<string[]>([]);
  const customAudienceByID = useMemo(() => {
    const map = new Map<string, FacebookCustomAudience>();
    customAudiences.forEach((audience) => map.set(audience.id, audience));
    return map;
  }, [customAudiences]);

  const formatCustomAudienceLabel = useCallback((audience: FacebookCustomAudience) => {
    const size = audience.approximate_count != null && audience.approximate_count > 0
      ? audience.approximate_count >= 1000000
        ? `${(audience.approximate_count / 1000000).toFixed(1)}M`
        : `${(audience.approximate_count / 1000).toFixed(0)}K`
      : '';
    return `${audience.name}${size ? ` (${size})` : ''}`;
  }, []);

  const filterCustomAudiences = useCallback((keyword: string) => {
    const q = keyword.trim().toLowerCase();
    if (!q) return customAudiences;
    return customAudiences.filter((audience) => (
      audience.name.toLowerCase().includes(q)
      || audience.id.toLowerCase().includes(q)
      || (audience.description || '').toLowerCase().includes(q)
      || (audience.subtype || '').toLowerCase().includes(q)
    ));
  }, [customAudiences]);

  const includeAudienceOptions = useMemo(
    () => filterCustomAudiences(includeAudienceFilter),
    [filterCustomAudiences, includeAudienceFilter],
  );
  const excludeAudienceOptions = useMemo(
    () => filterCustomAudiences(excludeAudienceFilter),
    [filterCustomAudiences, excludeAudienceFilter],
  );

  const toggleIncludedCustomAudience = useCallback((id: string) => {
    setSelectedCustomAudienceIDs((prev) => (
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    ));
    setExcludedCustomAudienceIDs((prev) => prev.filter((item) => item !== id));
  }, []);

  const toggleExcludedCustomAudience = useCallback((id: string) => {
    setExcludedCustomAudienceIDs((prev) => (
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    ));
    setSelectedCustomAudienceIDs((prev) => prev.filter((item) => item !== id));
  }, []);

  // ── Step 3: Ad ────────────────────────────────────────────────────────────
  const [adName, setAdName] = useState('');
  const [adStatus, setAdStatus] = useState('ACTIVE');

  // Page ID
  const [pages, setPages] = useState<FacebookPage[]>([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [pageID, setPageID] = useState('');
  const [manualPageID, setManualPageID] = useState('');

  // Instagram actor account
  const [igAccounts, setIgAccounts] = useState<FacebookInstagramAccount[]>([]);
  const [igAccountsLoading, setIgAccountsLoading] = useState(false);
  const [igActorID, setIgActorID] = useState('');
  const [manualIgActorID, setManualIgActorID] = useState('');
  const [showManualIgActorID, setShowManualIgActorID] = useState(false);
  // Partnership Ads (合创广告)
  const [partnershipCode, setPartnershipCode] = useState('');
  const [partnershipIGUserID, setPartnershipIGUserID] = useState('');
  const [partnershipPageID, setPartnershipPageID] = useState('');
  const [partnershipPages, setPartnershipPages] = useState<FacebookPartnershipPage[]>([]);
  const [partnershipInstagramAccounts, setPartnershipInstagramAccounts] = useState<FacebookPartnershipInstagramAccount[]>([]);
  const [partnershipPagesLoading, setPartnershipPagesLoading] = useState(false);
  const [partnershipInstagramAccountsLoading, setPartnershipInstagramAccountsLoading] = useState(false);
  const [partnershipPageSearch, setPartnershipPageSearch] = useState('');
  const [partnershipInstagramSearch, setPartnershipInstagramSearch] = useState('');
  const [partnershipActorName, setPartnershipActorName] = useState('');
  const [partnershipLoading, setPartnershipLoading] = useState(false);
  const [partnershipInfo, setPartnershipInfo] = useState<FacebookPartnershipInfo | null>(null);
  const [partnershipError, setPartnershipError] = useState('');
  const [showPartnership, setShowPartnership] = useState(false);
  const [partnershipContentSource, setPartnershipContentSource] = useState<PartnershipContentSource>('input_code');
  const [partnershipIdentityOrder, setPartnershipIdentityOrder] = useState<PartnershipIdentityOrder>('brand_first');
  const [partnershipHeadlineIdentity, setPartnershipHeadlineIdentity] = useState<PartnershipHeadlineIdentity>('dynamic_identity');

  // Ad type
  const [adType, setAdType] = useState<AdType>('image');
  const [existingCreativeIDInput, setExistingCreativeIDInput] = useState('');
  const [existingCreativeID, setExistingCreativeID] = useState('');
  const [postPickerOpen, setPostPickerOpen] = useState(false);
  const [postPickerTab, setPostPickerTab] = useState<'facebook' | 'instagram'>('facebook');
  const [postSearch, setPostSearch] = useState('');
  const [postLoading, setPostLoading] = useState(false);
  const [postOptions, setPostOptions] = useState<FacebookExistingPost[]>([]);
  const [selectedExistingPost, setSelectedExistingPost] = useState<FacebookExistingPost | null>(null);
  const [manualPostID, setManualPostID] = useState('');
  const [showManualPostID, setShowManualPostID] = useState(false);

  useEffect(() => {
    if (campaignObjective === 'OUTCOME_SALES' && showCatalog) {
      setAdType('catalog');
    } else if (adType === 'catalog' && !showCatalog) {
      setAdType('image');
    }
  }, [campaignObjective, showCatalog, adType]);

  const isSalesCatalogAd = campaignObjective === 'OUTCOME_SALES' && showCatalog;
  const isCatalogDestinationLocked = showDestination && isSalesCatalogAd;
  const partnershipUsesExistingPost = showPartnership && partnershipContentSource === 'existing_post';
  const partnershipUsesAuthCode = showPartnership && partnershipContentSource === 'input_code';
  const partnershipUsesNewCreative = showPartnership && partnershipContentSource === 'new_creative';
  const partnershipUsesPostCreative = showPartnership && (partnershipUsesExistingPost || partnershipUsesAuthCode);
  const partnershipPartnerLocked = showPartnership && partnershipUsesAuthCode && !!partnershipCode.trim();
  const partnershipPostLocked = partnershipPartnerLocked;

  useEffect(() => {
    if (isCatalogDestinationLocked) {
      setDestinationType(FACEBOOK_CATALOG_DESTINATION);
    }
  }, [isCatalogDestinationLocked]);

  useEffect(() => {
    if (!showPartnership) return;
    if ((partnershipContentSource === 'existing_post' || partnershipContentSource === 'input_code') && adType !== 'post') {
      setAdType('post');
      return;
    }
    if (partnershipContentSource === 'new_creative' && adType === 'post') {
      setAdType(isSalesCatalogAd ? 'catalog' : 'image');
    }
  }, [adType, isSalesCatalogAd, partnershipContentSource, showPartnership]);

  // Media (image/video)
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageHash, setImageHash] = useState('');
  const [imagePreviewURL, setImagePreviewURL] = useState('');
  const [imageS3URL, setImageS3URL] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadPct, setImageUploadPct] = useState(0);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoID, setVideoID] = useState('');
  const [videoS3URL, setVideoS3URL] = useState('');
  const [videoReady, setVideoReady] = useState(false);  // Facebook confirmed encoding complete
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoUploadPct, setVideoUploadPct] = useState(0);
  const [useExistingVideoID, setUseExistingVideoID] = useState(false);
  const [existingVideoIDInput, setExistingVideoIDInput] = useState('');
  const [existingVideoIDChecking, setExistingVideoIDChecking] = useState(false);
  const [thumbFile, setThumbFile] = useState<File | null>(null);
  const [thumbHash, setThumbHash] = useState('');
  const [thumbPreviewURL, setThumbPreviewURL] = useState('');
  const [thumbUploading, setThumbUploading] = useState(false);
  const [thumbUploadPct, setThumbUploadPct] = useState(0);

  // Common creative fields
  const [message, setMessage] = useState('');
  const [headline, setHeadline] = useState('');
  const [description, setDescription] = useState('');
  const [link, setLink] = useState('');
  const [caption, setCaption] = useState('');
  const [ctaType, setCtaType] = useState('SHOP_NOW');
  const [urlTags, setUrlTags] = useState('');
  const [showUTM, setShowUTM] = useState(false);
  const [offerEnabled, setOfferEnabled] = useState(false);
  const [offerSource, setOfferSource] = useState<'auto' | 'manual'>('manual');
  const [offerCode, setOfferCode] = useState('');
  const [offerDescription, setOfferDescription] = useState('');
  const [offerEmailSubscriptionEnabled, setOfferEmailSubscriptionEnabled] = useState(false);
  const [offerCardImageHash, setOfferCardImageHash] = useState('');
  const [creativeEnhancementFeatures, setCreativeEnhancementFeatures] = useState<Record<CreativeEnhancementKey, boolean>>(getDefaultCreativeEnhancements);

  // Carousel
  const [carouselCards, setCarouselCards] = useState<(CarouselCardRequest & { _key: number; _imageFile?: File | null; _imageUploading?: boolean; _previewURL?: string })[]>([
    { _key: 1, image_hash: '', video_id: '', headline: '', description: '', link: '', cta_type: '' },
    { _key: 2, image_hash: '', video_id: '', headline: '', description: '', link: '', cta_type: '' },
  ]);

  const previousAccountRef = useRef('');

  // Preview
  const [previewHTML, setPreviewHTML] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const partnershipPageSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const partnershipInstagramSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const facebookAttributionSpec = useMemo(
    () => buildFacebookAttributionSpec(clickAttributionDays, engagedViewAttributionDays, viewAttributionDays),
    [clickAttributionDays, engagedViewAttributionDays, viewAttributionDays],
  );

  const facebookAttributionSummary = useMemo(
    () => formatFacebookAttributionSummary(clickAttributionDays, engagedViewAttributionDays, viewAttributionDays),
    [clickAttributionDays, engagedViewAttributionDays, viewAttributionDays],
  );

  const adsetPlacementForSave = useMemo(() => {
    if (placementMode === 'automatic') {
      return {
        publisher_platforms: [] as string[],
        facebook_positions: [] as string[],
        instagram_positions: [] as string[],
        device_platforms: [] as string[],
        user_os: [] as string[],
      };
    }
    const normalized = normalizeFacebookPlacementState({
      publisherPlatforms,
      facebookPositions,
      instagramPositions,
      devicePlatforms,
      userOS,
    });
    return {
      publisher_platforms: normalized.publisherPlatforms,
      facebook_positions: normalized.facebookPositions,
      instagram_positions: normalized.instagramPositions,
      device_platforms: normalized.devicePlatforms,
      user_os: normalized.userOS,
    };
  }, [placementMode, publisherPlatforms, facebookPositions, instagramPositions, devicePlatforms, userOS]);

  const ctaOptions = useMemo(
    () => CTA_BY_OBJ[campaignObjective] || CTA_BY_OBJ['_default'],
    [campaignObjective]
  );

  const partnershipPageOptions = useMemo(
    () => partnershipPages.map((page) => ({
      value: page.id,
      label: `${page.name} (${page.id})${page.status_label ? ` · ${page.status_label}` : ''}`,
      disabled: page.selectable === false,
    })),
    [partnershipPages],
  );
  const partnershipIgOptions = useMemo(
    () => partnershipInstagramAccounts.map((account) => ({
      value: account.id,
      label: `${account.username || account.name} (${account.id})${account.status_label ? ` · ${account.status_label}` : ''}`,
      disabled: account.selectable === false,
    })),
    [partnershipInstagramAccounts],
  );

  useEffect(() => {
    if (partnershipPartnerLocked) return;
    if (!partnershipPageID) return;
    const matched = partnershipPages.find((page) => page.id === partnershipPageID);
    if (matched?.name) setPartnershipActorName(matched.name);
  }, [partnershipPages, partnershipPageID, partnershipPartnerLocked]);

  const loadPartnershipPages = useCallback(async (keyword = '') => {
    if (PARTNERSHIP_PARTNER_SELECTOR_DISABLED || platform !== 'facebook' || !selectedAccountID || !showPartnership || !pageID) {
      setPartnershipPages([]);
      return;
    }
    setPartnershipPagesLoading(true);
    try {
      const r = await accountsApi.listFacebookPartnershipPages({
        brand_id: brandId,
        site_id: siteId,
        account_id: selectedAccountID,
        page_id: pageID,
        instagram_account_id: igActorID || undefined,
        q: keyword.trim() || undefined,
      });
      setPartnershipPages(Array.isArray(r.list) ? r.list : []);
    } catch {
      setPartnershipPages([]);
    } finally {
      setPartnershipPagesLoading(false);
    }
  }, [platform, selectedAccountID, showPartnership, pageID, igActorID, brandId, siteId]);

  const loadPartnershipInstagramAccounts = useCallback(async (keyword = '') => {
    if (PARTNERSHIP_PARTNER_SELECTOR_DISABLED || platform !== 'facebook' || !selectedAccountID || !showPartnership || !igActorID) {
      setPartnershipInstagramAccounts([]);
      return;
    }
    setPartnershipInstagramAccountsLoading(true);
    try {
      const r = await accountsApi.listFacebookPartnershipInstagramAccounts({
        brand_id: brandId,
        site_id: siteId,
        account_id: selectedAccountID,
        instagram_account_id: igActorID,
        q: keyword.trim() || undefined,
      });
      setPartnershipInstagramAccounts(Array.isArray(r.list) ? r.list : []);
    } catch {
      setPartnershipInstagramAccounts([]);
    } finally {
      setPartnershipInstagramAccountsLoading(false);
    }
  }, [platform, selectedAccountID, showPartnership, igActorID, brandId, siteId]);
  const creativeFeatureEnrollments = useMemo(() => {
    const enrollments: Record<string, 'OPT_IN'> = {};
    CREATIVE_ENHANCEMENT_OPTIONS.forEach((option) => {
      if (creativeEnhancementFeatures[option.key]) {
        enrollments[option.key] = 'OPT_IN';
      }
    });
    return enrollments;
  }, [creativeEnhancementFeatures]);

  const allCreativeEnhancementsEnabled = useMemo(
    () => CREATIVE_ENHANCEMENT_OPTIONS.every((option) => !!creativeEnhancementFeatures[option.key]),
    [creativeEnhancementFeatures],
  );

  const toggleAllCreativeEnhancements = useCallback((checked: boolean) => {
    const next = {} as Record<CreativeEnhancementKey, boolean>;
    CREATIVE_ENHANCEMENT_OPTIONS.forEach((option) => {
      next[option.key] = checked;
    });
    setCreativeEnhancementFeatures(next);
  }, []);

  const toggleCreativeEnhancement = useCallback((key: CreativeEnhancementKey, checked: boolean) => {
    setCreativeEnhancementFeatures((prev) => ({ ...prev, [key]: checked }));
  }, []);

  const parentJustLoadedRef = useRef(false);
  useEffect(() => {
    if (parentDataLoaded && wizardMode) parentJustLoadedRef.current = true;
  }, [parentDataLoaded, wizardMode]);

  useEffect(() => {
    if (platform !== 'facebook') return;
    const prev = previousAccountRef.current;
    if (!selectedAccountID) {
      previousAccountRef.current = '';
      return;
    }
    if (!prev) {
      previousAccountRef.current = selectedAccountID;
      return;
    }
    if (prev === selectedAccountID) return;

    previousAccountRef.current = selectedAccountID;
    // 跳步模式下加载父级数据时会预填 account，此时不应清空素材
    if (!parentDataLoaded) return;
    // 新增广告组/广告时，账户由父级数据自动设置，不提示切换
    if (parentJustLoadedRef.current) {
      parentJustLoadedRef.current = false;
      return;
    }

    setCatalogs([]);
    setSelectedCatalogID('');
    setProductSets([]);
    setSelectedProductSetID('');
    setPixels([]);
    setSelectedPixelID('');
    setManualPixelID('');
    setPages([]);
    setPageID('');
    setManualPageID('');
    setIgAccounts([]);
    setIgActorID('');
    setManualIgActorID('');
    setCustomAudiences([]);
    setSelectedCustomAudienceIDs([]);
    setExcludedCustomAudienceIDs([]);
    setSelectedInterests([]);
    setSelectedBehaviors([]);
    setInterestOptions([]);
    setBehaviorOptions([]);
    setPartnershipInfo(null);
    setPartnershipError('');
    setSelectedExistingPost(null);
    setPostOptions([]);
    setManualPostID('');
    setShowManualPostID(false);

    setImageHash('');
    setImageFile(null);
    setImagePreviewURL('');
    setVideoID('');
    setVideoFile(null);
    setVideoReady(false);
    setThumbHash('');
    setThumbFile(null);
    setThumbPreviewURL('');
    setCarouselCards((prevCards) => prevCards.map((c) => ({ ...c, image_hash: '', video_id: '' })));
    showToast('已切换广告账户，请重新选择该账户下的主页、IG、目录和素材', 'warning');
  }, [platform, selectedAccountID, showToast, parentDataLoaded]);

  // Load pages + Instagram accounts when entering step 3
  useEffect(() => {
    if (platform !== 'facebook') return;
    if ((step !== 2 && step !== 3) || !selectedAccountID) return;
    let cancelled = false;
    const accountID = selectedAccountID;
    setPagesLoading(true);
    accountsApi.listFacebookPages({ brand_id: brandId, site_id: siteId, account_id: selectedAccountID })
      .then((r) => {
        if (cancelled || accountID !== selectedAccountID) return;
        const list = filterFacebookPagesByBrand(Array.isArray(r.list) ? r.list : [], brand);
        setPages(list);
        setPageID((current) => pickDefaultPageID(list, current));
      })
      .catch(() => { if (!cancelled) setPages([]); })
      .finally(() => { if (!cancelled) setPagesLoading(false); });

    setIgAccountsLoading(true);
    accountsApi.listInstagramAccounts({ brand_id: brandId, site_id: siteId, account_id: selectedAccountID })
      .then((r) => {
        if (cancelled || accountID !== selectedAccountID) return;
        const list = filterFacebookInstagramAccountsByBrand(Array.isArray(r.list) ? r.list : [], brand);
        setIgAccounts(list);
        setIgActorID((current) => pickDefaultIgActorID(list, current));
      })
      .catch(() => { if (!cancelled) setIgAccounts([]); })
      .finally(() => { if (!cancelled) setIgAccountsLoading(false); });
    return () => { cancelled = true; };
  }, [platform, step, selectedAccountID, brandId, siteId, pickDefaultPageID, pickDefaultIgActorID, brand]);

  const handleRefreshIgAccounts = useCallback(async () => {
    if (platform !== 'facebook') return;
    if (!selectedAccountID || igAccountsLoading) return;
    setIgAccountsLoading(true);
    try {
      const r = await accountsApi.listInstagramAccounts({ brand_id: brandId, site_id: siteId, account_id: selectedAccountID, refresh: true });
      const list = filterFacebookInstagramAccountsByBrand(Array.isArray(r.list) ? r.list : [], brand);
      setIgAccounts(list);
      setIgActorID((current) => pickDefaultIgActorID(list, current));
    } catch {
      // keep existing list on error
    } finally {
      setIgAccountsLoading(false);
    }
  }, [platform, selectedAccountID, brandId, siteId, igAccountsLoading, pickDefaultIgActorID, brand]);

  const handleSyncAssets = useCallback(async () => {
    if (platform !== 'facebook') return;
    if (!selectedAccountID || syncingAssets) return;
    setSyncingAssets(true);
    try {
      await accountsApi.syncFacebookAssets({ brand_id: brandId, site_id: siteId, account_id: selectedAccountID });
      // Reload all asset lists from fresh cache
      const [pagesRes, igRes, pixelsRes] = await Promise.all([
        accountsApi.listFacebookPages({ brand_id: brandId, site_id: siteId, account_id: selectedAccountID }),
        accountsApi.listInstagramAccounts({ brand_id: brandId, site_id: siteId, account_id: selectedAccountID }),
        accountsApi.listFacebookPixels({ brand_id: brandId, site_id: siteId, account_id: selectedAccountID }),
      ]);
      const pageList = filterFacebookPagesByBrand(Array.isArray(pagesRes.list) ? pagesRes.list : [], brand);
      setPages(pageList);
      setPageID((current) => pickDefaultPageID(pageList, current));
      const igList = filterFacebookInstagramAccountsByBrand(Array.isArray(igRes.list) ? igRes.list : [], brand);
      setIgAccounts(igList);
      setIgActorID((current) => pickDefaultIgActorID(igList, current));
      const pixelList = Array.isArray(pixelsRes.list) ? pixelsRes.list : [];
      setPixels(pixelList);
      setSelectedPixelID((current) => pickDefaultPixelID(pixelList, current));
    } catch {
      // ignore — assets that were already loaded remain usable
    } finally {
      setSyncingAssets(false);
    }
  }, [platform, selectedAccountID, brandId, siteId, syncingAssets, pickDefaultPixelID, pickDefaultPageID, pickDefaultIgActorID, brand]);

  const lookupPartnership = useCallback(async () => {
    if (platform !== 'facebook') return;
    const code = partnershipCode.trim();
    if (!code || !partnershipUsesAuthCode) return;
    setPartnershipLoading(true);
    setPartnershipError('');
    setPartnershipInfo(null);
    try {
      const info = await accountsApi.getPartnershipInfo({ brand_id: brandId, site_id: siteId, account_id: selectedAccountID, page_id: pageID || undefined, code });
      setPartnershipInfo(info);
      setPartnershipActorName(info.actor_name || '');
      setPartnershipPageID('');
      if (info.actor_id) {
        setPartnershipIGUserID(info.actor_id);
      }
      if (info.media_id) {
        setAdType('existing');
        setExistingCreativeID('');
      }
      if (info.catalog_id) {
        setSelectedCatalogID(info.catalog_id);
        setShowCatalog(true);
      }
      if (info.media_id) {
        showToast('已验证帖子授权，可直接用于合创广告', 'success');
      } else if (info.actor_name) {
        showToast(`已获取红人信息：${info.actor_name}`, 'success');
      } else {
        showToast('授权码已保存，将在创建广告时验证', 'success');
      }
    } catch (e) {
      setPartnershipError(e instanceof Error ? e.message : '查询失败');
    } finally {
      setPartnershipLoading(false);
    }
  }, [platform, brandId, siteId, selectedAccountID, partnershipCode, partnershipUsesAuthCode, showToast]);

  useEffect(() => {
    if (partnershipCode.trim()) return;
    setPartnershipInfo(null);
    setPartnershipError('');
  }, [partnershipCode]);

  useEffect(() => {
    if (platform !== 'facebook' || !selectedAccountID || !showPartnership) {
      setPartnershipPages([]);
      setPartnershipInstagramAccounts([]);
      setPartnershipPageSearch('');
      setPartnershipInstagramSearch('');
      return;
    }

    if (!pageID) {
      setPartnershipPages([]);
    } else {
      setPartnershipPageSearch('');
      void loadPartnershipPages('');
    }

    if (!igActorID) {
      setPartnershipInstagramAccounts([]);
    } else {
      setPartnershipInstagramSearch('');
      void loadPartnershipInstagramAccounts('');
    }
  }, [platform, selectedAccountID, showPartnership, pageID, igActorID, loadPartnershipPages, loadPartnershipInstagramAccounts]);

  useEffect(() => {
    if (partnershipPageSearchTimer.current) clearTimeout(partnershipPageSearchTimer.current);
    if (platform !== 'facebook' || !selectedAccountID || !showPartnership || !pageID) return;
    const keyword = partnershipPageSearch.trim();
    if (!keyword) return;
    if (keyword.length < 2) return;
    partnershipPageSearchTimer.current = setTimeout(() => {
      void loadPartnershipPages(keyword);
    }, 600);
  }, [platform, selectedAccountID, showPartnership, pageID, partnershipPageSearch, loadPartnershipPages]);

  useEffect(() => {
    if (partnershipInstagramSearchTimer.current) clearTimeout(partnershipInstagramSearchTimer.current);
    if (platform !== 'facebook' || !selectedAccountID || !showPartnership || !igActorID) return;
    const keyword = partnershipInstagramSearch.trim();
    if (!keyword) return;
    if (keyword.length < 2) return;
    partnershipInstagramSearchTimer.current = setTimeout(() => {
      void loadPartnershipInstagramAccounts(keyword);
    }, 600);
  }, [platform, selectedAccountID, showPartnership, igActorID, partnershipInstagramSearch, loadPartnershipInstagramAccounts]);

  // Trigger FB API preview (best-effort, failures don't affect local preview)
  const triggerPreview = useCallback(() => {
    if (platform !== 'facebook') return;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      if (!selectedAccountID || !brandId) return;
      const effectivePageID = pageID || '0'; // FB API requires page_id; use '0' to attempt anyway
      let creativeSpec: Record<string, unknown> | null = null;

      if (adType === 'existing' && existingCreativeID) {
        creativeSpec = { creative_id: existingCreativeID };
      } else if (adType === 'post' && selectedExistingPost) {
        if (selectedExistingPost.platform === 'instagram') {
          creativeSpec = { object_id: effectivePageID, source_instagram_media_id: selectedExistingPost.id };
        } else {
          creativeSpec = { object_story_id: selectedExistingPost.id };
        }
      } else if (adType === 'image' && imageHash) {
        const targetLink = link || 'https://example.com';
        const linkData: Record<string, unknown> = { link: targetLink, image_hash: imageHash };
        if (message) linkData.message = message;
        if (headline) linkData.name = headline;
        if (description) linkData.description = description;
        if (ctaType) linkData.call_to_action = { type: ctaType, value: { link: targetLink } };
        creativeSpec = { object_story_spec: { page_id: effectivePageID, link_data: linkData } };
      } else if (adType === 'video' && videoID) {
        const videoData: Record<string, unknown> = { video_id: videoID };
        if (message) videoData.message = message;
        if (headline) videoData.title = headline;
        if (thumbHash) videoData.image_hash = thumbHash;
        if (ctaType && link) videoData.call_to_action = { type: ctaType, value: { link } };
        creativeSpec = { object_story_spec: { page_id: effectivePageID, video_data: videoData } };
      } else if (adType === 'carousel' && carouselCards.some((c) => c.image_hash)) {
        const children = carouselCards.filter((c) => c.image_hash).map((c) => {
          const att: Record<string, unknown> = { link: c.link || link || 'https://example.com' };
          if (c.image_hash) att.image_hash = c.image_hash;
          if (c.headline) att.name = c.headline;
          if (c.description) att.description = c.description;
          if (c.cta_type) att.call_to_action = { type: c.cta_type, value: { link: c.link || link || 'https://example.com' } };
          return att;
        });
        creativeSpec = { object_story_spec: { page_id: effectivePageID, link_data: { child_attachments: children, link: link || 'https://example.com', message: message || undefined, multi_share_optimized: true } } };
      } else if (adType === 'catalog' && (link || headline)) {
        const targetLink = link || 'https://example.com';
        const templateData: Record<string, unknown> = { link: targetLink, name: headline || '{{product.name | titleize}}' };
        if (message) templateData.message = message;
        if (description) templateData.description = description;
        if (ctaType) templateData.call_to_action = { type: ctaType, value: { link: targetLink } };
        creativeSpec = { object_story_spec: { page_id: effectivePageID, template_data: templateData } };
      }

      if (!creativeSpec) return;
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const res = await accountsApi.generateAdPreview(brandId, {
          account_id: selectedAccountID,
          creative_spec: creativeSpec,
          ad_format: 'MOBILE_FEED_STANDARD',
        });
        const first = res.previews?.[0]?.body;
        if (first) {
          setPreviewHTML(first);
          setPreviewError(null);
        } else {
          setPreviewHTML(null);
          setPreviewError('Facebook 未返回预览内容');
        }
      } catch (e: unknown) {
        setPreviewHTML(null);
        const raw = e instanceof Error ? e.message : String(e);
        const msg = raw.includes('subcode=33') || raw.includes('missing permissions')
          ? 'Facebook 预览需要广告账号的 Advertiser 权限，或 App 未开通 Ads Management Standard Access'
          : (raw || 'Facebook 预览接口请求失败');
        setPreviewError(msg);
      } finally {
        setPreviewLoading(false);
      }
    }, 900);
  }, [platform, adType, pageID, imageHash, videoID, thumbHash, message, headline, description, link, ctaType, existingCreativeID, selectedExistingPost, carouselCards, brandId, selectedAccountID]);

  useEffect(() => { if (step === 3) triggerPreview(); }, [step, triggerPreview]);
  useEffect(() => { if (step === 3) { setPreviewHTML(null); setPreviewError(null); triggerPreview(); } }, [adType]);
  useEffect(() => { if (step === 3 && partnershipUsesAuthCode) { setPreviewHTML(null); triggerPreview(); } }, [partnershipInfo, partnershipUsesAuthCode]);
  // Upload helper — accepts a File (local upload) or a URL string (backend downloads directly)
  const uploadMedia = useCallback(async (fileOrUrl: File | string, type: 'image' | 'video', onProgress?: (pct: number) => void) => {
    if (platform !== 'facebook') {
      throw new Error('当前平台不需要 Facebook 素材上传');
    }
    const fd = new FormData();
    fd.append('account_id', selectedAccountID);
    fd.append('material_type', type);
    if (typeof fileOrUrl === 'string') {
      fd.append('source_url', fileOrUrl);
      const urlName = fileOrUrl.split('/').pop()?.split('?')[0] || 'remote-asset';
      fd.append('material_name', urlName.replace(/\.[^/.]+$/, ''));
    } else {
      fd.append('material_name', fileOrUrl.name.replace(/\.[^/.]+$/, ''));
      fd.append('file', fileOrUrl);
    }
    return accountsApi.uploadFacebookMaterial(brandId, fd, onProgress);
  }, [platform, brandId, selectedAccountID]);

  const handleUploadImage = useCallback(async (fileOrUrl: File | string) => {
    if (typeof fileOrUrl !== 'string') {
      setImageFile(fileOrUrl);
      setImagePreviewURL(URL.createObjectURL(fileOrUrl));
    } else {
      setImageFile(null as unknown as File);
      setImagePreviewURL(fileOrUrl);
    }
    setImageUploading(true);
    setImageUploadPct(0);
    try {
      const m = await uploadMedia(fileOrUrl, 'image', typeof fileOrUrl !== 'string' ? setImageUploadPct : undefined);
      setImageHash(m.facebook_media_id);
      if (m.preview_url) setImageS3URL(m.preview_url);
      showToast('图片上传成功', 'success');
      setTimeout(triggerPreview, 100);
    } catch (e) {
      showToast(e instanceof Error ? e.message : '上传失败', 'error');
    } finally {
      setImageUploading(false);
    }
  }, [uploadMedia, showToast, triggerPreview]);

  const clearImageMaterial = useCallback(() => {
    if (imagePreviewURL.startsWith('blob:')) URL.revokeObjectURL(imagePreviewURL);
    setImageFile(null);
    setImageHash('');
    setImagePreviewURL('');
    setImageS3URL('');
    setImageUploadPct(0);
    setTimeout(triggerPreview, 100);
  }, [imagePreviewURL, triggerPreview]);

  const handleUploadVideo = useCallback(async (fileOrUrl: File | string) => {
    if (typeof fileOrUrl !== 'string') setVideoFile(fileOrUrl);
    setVideoUploading(true);
    setVideoUploadPct(0);
    try {
      const m = await uploadMedia(fileOrUrl, 'video', typeof fileOrUrl !== 'string' ? setVideoUploadPct : undefined);
      setVideoID(m.facebook_media_id);
      if (m.preview_url) setVideoS3URL(m.preview_url);
      setVideoReady(m.video_ready ?? false);
      if (m.thumb_hash) {
        setThumbHash(m.thumb_hash);
        showToast('视频上传成功（封面已自动获取）', 'success');
      } else if (m.video_ready) {
        showToast('视频上传成功，Facebook 编码已完成', 'success');
      } else {
        showToast('视频已上传，Facebook 仍在处理中，发布时将自动等待', 'warning');
      }
      setTimeout(triggerPreview, 100);
    } catch (e) {
      showToast(e instanceof Error ? e.message : '上传失败', 'error');
    } finally {
      setVideoUploading(false);
    }
  }, [uploadMedia, showToast, triggerPreview]);

  const handleApplyExistingCreativeID = useCallback(() => {
    const nextID = existingCreativeIDInput.trim();
    if (!nextID) {
      showToast('请先输入已有创意 ID', 'warning');
      return;
    }
    setExistingCreativeID(nextID);
    setPreviewHTML(null);
    setPreviewError(null);
    showToast('已有创意 ID 已应用，正在刷新预览', 'success');
    setTimeout(triggerPreview, 100);
  }, [existingCreativeIDInput, showToast, triggerPreview]);

  const loadExistingPosts = useCallback(async (tab: 'facebook' | 'instagram' = postPickerTab, keyword = postSearch) => {
    if (!selectedAccountID) {
      showToast('请先选择广告账户', 'warning');
      return;
    }
    setPostLoading(true);
    try {
      const res = await accountsApi.listFacebookPosts({
        brand_id: brandId,
        site_id: siteId,
        account_id: selectedAccountID,
        platform: tab,
        q: keyword.trim() || undefined,
        ig_user_id: tab === 'instagram' ? igActorID || undefined : undefined,
      });
      setPostOptions(Array.isArray(res.list) ? res.list : []);
    } catch (e) {
      setPostOptions([]);
      showToast(e instanceof Error ? e.message : '帖子列表获取失败', 'error');
    } finally {
      setPostLoading(false);
    }
  }, [postPickerTab, postSearch, selectedAccountID, brandId, siteId, igActorID, showToast]);

  const applyExistingPost = useCallback((post: FacebookExistingPost) => {
    setSelectedExistingPost(post);
    if (post.platform === 'facebook' && post.page_id) setPageID(post.page_id);
    if (post.platform === 'instagram' && post.ig_user_id) setIgActorID(post.ig_user_id);
    if (post.message) setMessage((prev) => prev || post.message || '');
    if (post.link) setLink((prev) => prev || post.link || '');
    if (post.cta_type) setCtaType((prev) => (!prev || prev === 'SHOP_NOW') ? post.cta_type || prev : prev);
    setPostPickerOpen(false);
    setPreviewHTML(null);
    setPreviewError(null);
    showToast('已选择帖子', 'success');
    setTimeout(triggerPreview, 100);
  }, [showToast, triggerPreview]);

  const handleApplyManualPostID = useCallback(async () => {
    const postID = manualPostID.trim();
    if (!postID || !selectedAccountID) return;
    setPostLoading(true);
    try {
      const post = await accountsApi.getFacebookPostInfo({
        brand_id: brandId,
        site_id: siteId,
        account_id: selectedAccountID,
        platform: postPickerTab,
        post_id: postID,
        page_id: pageID || undefined,
        ig_user_id: postPickerTab === 'instagram' ? igActorID || undefined : undefined,
      });
      applyExistingPost(post);
    } catch (e) {
      showToast(e instanceof Error ? e.message : '帖子编号查询失败', 'error');
    } finally {
      setPostLoading(false);
    }
  }, [manualPostID, selectedAccountID, brandId, siteId, postPickerTab, pageID, igActorID, applyExistingPost, showToast]);

  useEffect(() => {
    if (postPickerOpen) {
      void loadExistingPosts(postPickerTab, postSearch);
    }
  }, [postPickerOpen, postPickerTab]);

  const handleUseExistingVideoID = useCallback(async () => {
    if (platform !== 'facebook') return;
    const vid = existingVideoIDInput.trim();
    if (!vid || !selectedAccountID) return;
    setExistingVideoIDChecking(true);
    try {
      const info = await accountsApi.getVideoInfo({ brand_id: brandId, site_id: siteId, account_id: selectedAccountID, video_id: vid });
      setVideoID(info.video_id);
      setVideoReady(info.ready);
      if (info.thumb_hash) setThumbHash(info.thumb_hash);
      if (info.ready) {
        showToast(info.thumb_hash ? '视频就绪，封面已自动获取' : '视频已就绪', 'success');
      } else {
        showToast('视频仍在处理中，发布时将自动等待', 'warning');
      }
      setTimeout(triggerPreview, 100);
    } catch (e) {
      showToast(e instanceof Error ? e.message : '查询失败，请检查 Video ID', 'error');
    } finally {
      setExistingVideoIDChecking(false);
    }
  }, [platform, existingVideoIDInput, selectedAccountID, brandId, showToast, triggerPreview]);

  const handleUploadThumb = useCallback(async (fileOrUrl: File | string) => {
    if (typeof fileOrUrl !== 'string') {
      setThumbFile(fileOrUrl);
      setThumbPreviewURL(URL.createObjectURL(fileOrUrl));
    } else {
      setThumbPreviewURL(fileOrUrl);
    }
    setThumbUploading(true);
    setThumbUploadPct(0);
    try {
      const m = await uploadMedia(fileOrUrl, 'image', typeof fileOrUrl !== 'string' ? setThumbUploadPct : undefined);
      setThumbHash(m.facebook_media_id);
      showToast('封面上传成功', 'success');
    } catch (e) {
      showToast(e instanceof Error ? e.message : '上传失败', 'error');
    } finally {
      setThumbUploading(false);
    }
  }, [uploadMedia, showToast]);

  const handleUploadCarouselCard = useCallback(async (idx: number, fileOrUrl: File | string) => {
    const previewURL = typeof fileOrUrl === 'string' ? fileOrUrl : URL.createObjectURL(fileOrUrl);
    setCarouselCards((prev) => prev.map((c, i) => i === idx ? { ...c, _imageFile: typeof fileOrUrl !== 'string' ? fileOrUrl : undefined, _previewURL: previewURL, _imageUploading: true } : c));
    try {
      const m = await uploadMedia(fileOrUrl, 'image');
      setCarouselCards((prev) => prev.map((c, i) => i === idx ? { ...c, image_hash: m.facebook_media_id, _imageUploading: false } : c));
      showToast(`第 ${idx + 1} 张卡片图片上传成功`, 'success');
    } catch (e) {
      setCarouselCards((prev) => prev.map((c, i) => i === idx ? { ...c, _imageUploading: false } : c));
      showToast(e instanceof Error ? e.message : '上传失败', 'error');
    }
  }, [uploadMedia, showToast]);

  const applyManualPageID = useCallback(() => {
    const pid = manualPageID.trim();
    if (!pid) return;
    setPageID(pid);
    setTimeout(triggerPreview, 100);
  }, [manualPageID, triggerPreview]);

  const applyManualIgActorID = useCallback(() => {
    const id = manualIgActorID.trim();
    if (!id) return;
    setIgActorID(id);
  }, [manualIgActorID]);

  // ── Validation ────────────────────────────────────────────────────────────
  const validateStep = (): string | null => {
    if (step === 1) {
      if (!selectedAccountID) return '请选择授权账号';
      if (!campaignName.trim()) return '请填写广告系列名称';
      if (spendCap && Number(spendCap) > 0 && Number(spendCap) < 100) return '广告系列花费上限不能低于 $100';
      if (usesCampaignBudget && useCampaignLifetimeBudget && Number(campaignLifetimeBudget) <= 0) return '请填写有效的广告系列总预算';
      if (usesCampaignBudget && !useCampaignLifetimeBudget && Number(dailyBudget) <= 0) return '请填写有效的广告系列单日预算';
      // CBO 终生预算不强制结束时间 — Facebook 允许不设 end_time
    }
    if (step === 2) {
      if (!adsetName.trim()) return '请填写广告组名称';
      if (platform === 'google') {
        if (Number(dailyBudget) <= 0) return 'Google 广告请填写有效的系列日预算';
        return null;
      }
      if (selectedCountries.length === 0) return '请至少选择一个目标国家/地区';
      const supportedGoals = goalOptionsByObjective[campaignObjective] || [];
      const effectiveGoal = optimizationGoal || supportedGoals[0]?.value || '';
      if (optimizationGoal) {
        if (!supportedGoals.some((g) => g.value === optimizationGoal)) {
          return '所选成效目标与广告目标不匹配，请重新选择成效目标';
        }
      }
      if (!usesCampaignBudget && budgetType === 'daily' && Number(adsetDailyBudget) <= 0) return '请填写有效的日预算';
      if (!usesCampaignBudget && budgetType === 'lifetime' && Number(adsetLifetimeBudget) <= 0) return '请填写有效的总预算';
      if (budgetType === 'lifetime' && !endTime) return '使用总预算时必须设置结束时间';
      if (showPixelSelector && pixelRequired && !selectedPixelID) return '当前优化目标需要配置 Facebook Pixel';
      if (campaignObjective === 'OUTCOME_AWARENESS' && bidStrategy === 'COST_CAP' && effectiveGoal !== 'REACH') {
        return '知名度选择单次成效费用目标时，成效目标只能是广告覆盖人数最大化';
      }
      if (campaignObjective === 'OUTCOME_TRAFFIC' && effectiveGoal !== 'LANDING_PAGE_VIEWS') {
        return '流量目标当前仅支持落地页浏览量最大化';
      }
      if (campaignObjective === 'OUTCOME_SALES' && (effectiveGoal === 'OFFSITE_CONVERSIONS' || effectiveGoal === 'VALUE')) {
        const allowed = showCatalog ? [FACEBOOK_CATALOG_DESTINATION] : ['WEBSITE'];
        if (!allowed.includes(destinationType)) {
          return `销售目标下当前转化位置仅支持 ${showCatalog ? FACEBOOK_CATALOG_DESTINATION : 'WEBSITE'}`;
        }
      }
      if (bidStrategy === 'LOWEST_COST_WITH_BID_CAP' && !(Number(bidAmount) > 0)) return '使用出价上限策略时必须填写有效的出价上限金额';
      if (bidStrategy === 'COST_CAP' && !(Number(bidAmount) > 0)) return '使用 COST_CAP 策略时必须填写有效的目标费用金额';
      if (bidStrategy === 'LOWEST_COST_WITH_MIN_ROAS' && !(Number(bidConstraintsROAS) > 0)) return '使用最低 ROAS 策略时必须填写最低 ROAS 值';
      if (bidStrategy === 'LOWEST_COST_WITH_MIN_ROAS' && optimizationGoal !== 'VALUE') return `使用最低 ROAS 策略时，优化目标必须选择「${goalLabelByValue('VALUE')}」`;
      if (campaignObjective === 'OUTCOME_SALES') {
        if (bidStrategy === 'COST_CAP' && effectiveGoal !== 'OFFSITE_CONVERSIONS') {
          return `CPA（COST_CAP）策略下，成效目标需选择「${goalLabelByValue('OFFSITE_CONVERSIONS')}」`;
        }
        if (bidStrategy === 'LOWEST_COST_WITH_MIN_ROAS' && effectiveGoal !== 'VALUE') {
          return `ROAS 策略下，成效目标需选择「${goalLabelByValue('VALUE')}」`;
        }
      }
      if (Number(frequencyCap) > 0 && effectiveGoal !== 'REACH') {
        return `频次上限仅支持优化目标「${goalLabelByValue('REACH')}」`;
      }
      if (campaignObjective === 'OUTCOME_AWARENESS' && effectiveGoal === 'REACH' && bidStrategy === 'COST_CAP' && !(Number(frequencyCap) > 0) && frequencyMode === 'cap') {
        return '选择频次上限时，请填写 n 次每 n 天中的次数';
      }
      if (placementMode === 'manual' && adsetPlacementForSave.publisher_platforms.length > 0 && adsetPlacementForSave.facebook_positions.length > 0 && !adsetPlacementForSave.publisher_platforms.includes('facebook')) {
        return '已选择 Facebook 版位，但发布平台未包含 Facebook';
      }
      if (placementMode === 'manual' && adsetPlacementForSave.publisher_platforms.length > 0 && adsetPlacementForSave.instagram_positions.length > 0 && !adsetPlacementForSave.publisher_platforms.includes('instagram')) {
        return '已选择 Instagram 版位，但发布平台未包含 Instagram';
      }
      if (placementMode === 'manual' && adsetPlacementForSave.user_os.length > 0 && adsetPlacementForSave.device_platforms.length > 0 && !adsetPlacementForSave.device_platforms.includes('mobile')) {
        return '设置操作系统筛选时，设备类型必须包含 mobile';
      }
    }
    if (step === 3) {
      if (!adName.trim()) return '请填写广告名称';
      if (platform === 'google') {
        if (!link.trim()) return 'Google 广告请填写落地页链接';
        try {
          const parsed = new URL(link.trim());
          if (!parsed.protocol.startsWith('http')) return 'Google 落地页链接需为 http/https';
        } catch {
          return 'Google 落地页链接格式无效';
        }
        return null;
      }
      const isPartnership = partnershipUsesAuthCode && !!partnershipCode.trim();
      if (isPartnership) {
        if (!pageID) return '合创广告必须选择 Facebook 主页 ID';
        if (!igActorID) return '合创广告必须绑定品牌 Instagram 账号';
        if (!partnershipCode.trim()) return '请填写合创授权码';
        if (!link.trim()) return '合创广告必须填写目标链接';
        return null;
      }
      if (partnershipUsesExistingPost && !selectedExistingPost) return '请选择或输入帖子编号';
      if (campaignObjective === 'OUTCOME_AWARENESS' && !pageID) return '知名度广告需要选择 Facebook 公共主页';
      if (!pageID) return '请选择或输入 Facebook 主页 ID';
      if (showCatalog && !selectedProductSetID) return '目录广告请先选择商品系列';
      if (isSalesCatalogAd && !link.trim()) return '目录广告请填写目标链接';
      if (offerEnabled && offerSource === 'manual' && !offerCode.trim()) return '请填写优惠码';
      if (isSalesCatalogAd) return null;
      if (adType === 'existing' && !existingCreativeID.trim()) return '请输入并应用已有创意 ID';
      if (adType === 'post' && !selectedExistingPost) return '请选择或输入帖子编号';
      if (adType === 'image' && !imageHash) return '请上传广告图片';
      if (adType === 'video' && !videoID) return '请上传广告视频';
      if (adType === 'carousel') {
        if (carouselCards.length < 2) return '轮播至少需要 2 张卡片';
        const missing = carouselCards.findIndex((c) => !c.image_hash);
        if (missing >= 0) return `第 ${missing + 1} 张卡片缺少图片`;
      }
    }
    return null;
  };

  // Scroll to error banner when a validation error appears
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  const scrollFormToTop = () => {
    if (formCardRef.current) {
      formCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNext = async () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    setError(null);
    if (step < 3) {
      await saveManualDraftSilently();
      const nextStep = (step + 1) as WizardStep;
      setStep(nextStep);
      setMaxVisitedStep((prev) => (prev > nextStep ? prev : nextStep));
      scrollFormToTop();
    }
  };

  const handlePrev = () => {
    setError(null);
    if (step > 1) {
      setStep((s) => (s - 1) as WizardStep);
      scrollFormToTop();
    } else {
      void handleExitToList();
    }
  };

  const handleStepJump = (targetStep: WizardStep) => {
    if (targetStep > maxVisitedStep || targetStep === step) return;
    setError(null);
    setStep(targetStep);
    scrollFormToTop();
  };

  const handleToolbarBack = () => {
    if (activePlatform.available && platform === 'facebook' && parentDataLoaded && step > 1) {
      handlePrev();
      return;
    }
    void handleExitToList();
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (userLevel < 2) {
      showToast('只读角色无法创建广告', 'error');
      return;
    }
    if (!hasPermission('ad-mgmt:campaigns:create')) {
      showToast('暂无创建广告权限', 'error');
      return;
    }
    const err = validateStep();
    if (err) { setError(err); return; }
    setError(null);

    // ── Pre-flight: validate via Facebook validate_only API ────────────────
    const genderVals: number[] = genders === '1' ? [1] : genders === '2' ? [2] : [];
    const payload = {
        account_id: selectedAccountID,
        campaign_name: campaignName.trim(),
        campaign_objective: campaignObjective,
        campaign_status: campaignStatus,
        daily_budget: usesCampaignBudget && !useCampaignLifetimeBudget ? Number(dailyBudget) || undefined : undefined,
        campaign_budget_scope: usesBudgetScopeSelector ? salesBudgetScope : (cboEnabled ? 'campaign' : 'adset'),
        adset_name: adsetName.trim(),
        budget_type: budgetType,
        adset_daily_budget: usesCampaignBudget ? undefined : (budgetType === 'daily' ? Number(adsetDailyBudget) || 10 : undefined),
        adset_lifetime_budget: usesCampaignBudget ? undefined : (budgetType === 'lifetime' ? Number(adsetLifetimeBudget) || undefined : undefined),
        billing_event: billingEvent || undefined,
        optimization_goal: optimizationGoal || undefined,
        destination_type: isCatalogDestinationLocked ? FACEBOOK_CATALOG_DESTINATION : (showDestination ? destinationType || undefined : undefined),
        genders: genderVals.length ? genderVals : undefined,
        countries: selectedCountries,
        excluded_countries: excludedCountries.length ? excludedCountries : undefined,
        age_min: Number(ageMin) || 18,
        age_max: Number(ageMax) || 65,
        locales: locales.length ? locales : undefined,
        interests: selectedInterests.length ? selectedInterests : undefined,
        behaviors: selectedBehaviors.length ? selectedBehaviors : undefined,
        custom_audience_ids: selectedCustomAudienceIDs.length ? selectedCustomAudienceIDs : undefined,
        pixel_id: showPixelSelector ? selectedPixelID || undefined : undefined,
        custom_event_type: showConversion && selectedPixelID ? customEventType || undefined : undefined,
        product_catalog_id: showConversion && showCatalog ? selectedCatalogID || undefined : undefined,
        product_set_id: showConversion && showCatalog && selectedCatalogID ? selectedProductSetID || undefined : undefined,
        bid_strategy: (bidStrategy && bidStrategy !== 'LOWEST_COST_WITHOUT_CAP' && Number(bidAmount) > 0) ? bidStrategy : 'LOWEST_COST_WITHOUT_CAP',
        bid_amount: Number(bidAmount) > 0 ? Number(bidAmount) : undefined,
        start_time: startTime || undefined,
        end_time: endTime || undefined,
        ad_name: adName.trim(),
        ad_status: adStatus,
        page_id: pageID || undefined,
        instagram_actor_id: igActorID || undefined,
        ad_type: isSalesCatalogAd ? 'catalog' : ((partnershipUsesAuthCode && partnershipCode.trim()) ? 'partnership' : adType),
        existing_creative_id: !isSalesCatalogAd && adType === 'existing' ? existingCreativeID.trim() || undefined : undefined,
        existing_post_platform: !isSalesCatalogAd && adType === 'post' ? selectedExistingPost?.platform : undefined,
        existing_post_id: !isSalesCatalogAd && adType === 'post' ? selectedExistingPost?.id : undefined,
        existing_post_page_id: !isSalesCatalogAd && adType === 'post' ? selectedExistingPost?.page_id || pageID || undefined : undefined,
        partnership_code: (partnershipUsesAuthCode && partnershipCode.trim()) ? partnershipCode.trim() : undefined,
        partnership_ig_user_id: (partnershipUsesAuthCode && partnershipCode.trim() && partnershipIGUserID) ? partnershipIGUserID : undefined,
        image_hash: isSalesCatalogAd ? undefined : (imageHash || undefined),
        image_url: imageS3URL || undefined,
        video_id: isSalesCatalogAd ? undefined : (videoID || undefined),
        video_url: videoS3URL || undefined,
        thumb_hash: thumbHash || undefined,
        message: message.trim() || undefined,
        headline: headline.trim() || undefined,
        description: description.trim() || undefined,
        link: link.trim() || undefined,
        caption: caption.trim() || undefined,
        cta_type: ctaType || undefined,
        url_tags: urlTags.trim() || undefined,
        creative_feature_enrollments: Object.keys(creativeFeatureEnrollments).length ? creativeFeatureEnrollments : undefined,
        offer_enabled: offerEnabled || undefined,
        offer_source: offerEnabled ? offerSource : undefined,
        offer_code: offerEnabled ? offerCode.trim() || undefined : undefined,
        offer_description: offerEnabled ? offerDescription.trim() || undefined : undefined,
        offer_email_subscription_enabled: offerEnabled ? offerEmailSubscriptionEnabled : undefined,
        offer_card_image_hash: offerEnabled ? offerCardImageHash.trim() || undefined : undefined,
        carousel_cards: !isSalesCatalogAd && adType === 'carousel' ? carouselCards.map((c) => ({
          image_hash: c.image_hash,
          video_id: c.video_id,
          headline: c.headline,
          description: c.description,
          link: c.link,
          cta_type: c.cta_type,
        })) : undefined,
        multi_share_optimized: true,
        spend_cap: Number(spendCap) > 0 ? Number(spendCap) : undefined,
        campaign_lifetime_budget: usesCampaignBudget && useCampaignLifetimeBudget && Number(campaignLifetimeBudget) > 0 ? Number(campaignLifetimeBudget) : undefined,
        adset_status: adSetStatus,
        frequency_cap: Number(frequencyCap) > 0 ? Number(frequencyCap) : undefined,
        frequency_cap_reset_period: Number(frequencyCapResetPeriod) > 0 ? Number(frequencyCapResetPeriod) : undefined,
        attribution_spec: isIncrementalAttribution ? undefined : (campaignObjective === 'OUTCOME_TRAFFIC'
          ? '7D_CLICK'
          : (showConversion ? facebookAttributionSpec : undefined)),
        is_incremental_attribution_enabled: isIncrementalAttribution || undefined,
        excluded_custom_audience_ids: excludedCustomAudienceIDs.length ? excludedCustomAudienceIDs : undefined,
        publisher_platforms: adsetPlacementForSave.publisher_platforms.length ? adsetPlacementForSave.publisher_platforms : undefined,
        facebook_positions: adsetPlacementForSave.facebook_positions.length ? adsetPlacementForSave.facebook_positions : undefined,
        instagram_positions: adsetPlacementForSave.instagram_positions.length ? adsetPlacementForSave.instagram_positions : undefined,
        device_platforms: adsetPlacementForSave.device_platforms.length ? adsetPlacementForSave.device_platforms : undefined,
        user_os: adsetPlacementForSave.user_os.length ? adsetPlacementForSave.user_os : undefined,
        bid_constraints_roas: bidStrategy === 'LOWEST_COST_WITH_MIN_ROAS' && Number(bidConstraintsROAS) > 0 ? Number(bidConstraintsROAS) : undefined,
        // 复用已有层级
        existing_campaign_id: existingCampaignId || undefined,
        existing_adset_id: existingAdSetId || undefined,
        site_id: siteId || undefined,
        // 广告标签
        tags: Object.keys(campaignTags).length > 0 ? campaignTags : undefined,
        adset_tags: Object.keys(adsetTags).length > 0 ? adsetTags : undefined,
        ad_tags: Object.keys(adTags).length > 0 ? adTags : undefined,
    // payload 使用 CreateFacebookFullRequest 约束字段完整性；
    // 实际提交时通过 genericPayload 加上 platform 字段走通用适配器接口
    } as Parameters<typeof accountsApi.createFacebookFull>[1];

    // 通用请求加上 platform 字段，走统一适配器路由
    const genericPayload = { platform, ...payload };
    const platformLabel = activePlatform.label;

    setValidating(true);
    try {
      const vr = await accountsApi.validateAdFull(brandId, genericPayload);
      if (!vr.valid) {
        const postCreativeOnlyError = adType === 'post'
          && !!vr.creative_error
          && !vr.campaign_error
          && !vr.adset_error;
        if (postCreativeOnlyError) {
          showToast('帖子创意预验证未通过，将直接尝试创建并以 Meta 发布结果为准', 'warning');
        } else if (vr.campaign_error && !existingCampaignId && !existingAdSetId) {
          setError(`广告系列验证失败：${vr.campaign_error}`);
          setStep(1);
          return;
        } else if (vr.adset_error && !existingAdSetId) {
          setError(`广告组验证失败：${vr.adset_error}`);
          setStep(Math.max(2, initialStep) as WizardStep);
          return;
        } else if (vr.creative_error) {
          setError(`广告创意验证失败：${vr.creative_error}`);
          setStep(3);
          return;
        } else {
          setError(`${platformLabel} 参数验证未通过，请检查各项设置`);
          return;
        }
      }
    } catch {
      showToast('预验证请求失败，将直接尝试创建', 'warning');
    } finally {
      setValidating(false);
    }

    setSubmitting(true);
    try {
      const result = await accountsApi.createAdFull(brandId, genericPayload);

      const hasErrors = result.campaign_error || result.adset_error;
      const adStatus2 = result.ad?.publish_status;
      if (adStatus2 === 'published') {
        showToast(`广告创建成功并已发布到 ${platformLabel}`, 'success');
      } else if (hasErrors) {
        showToast(`部分步骤 ${platformLabel} 侧失败，已保存到本地，可在广告列表重试`, 'warning');
      } else {
        showToast(`广告已创建，正在发布到 ${platformLabel}`, 'info');
      }
      setTimeout(() => navigate(`/campaigns/${platform}`), 1800);
    } catch (e) {
      setError(e instanceof Error ? e.message : '创建失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Carousel helpers ──────────────────────────────────────────────────────
  const addCarouselCard = () => {
    if (carouselCards.length >= 10) { showToast('轮播最多 10 张卡片', 'warning'); return; }
    setCarouselCards((prev) => [...prev, { _key: Date.now(), image_hash: '', video_id: '', headline: '', description: '', link: '', cta_type: '' }]);
  };
  const removeCarouselCard = (idx: number) => {
    if (carouselCards.length <= 2) { showToast('轮播最少 2 张卡片', 'warning'); return; }
    setCarouselCards((prev) => prev.filter((_, i) => i !== idx));
  };
  const updateCard = (idx: number, field: string, val: string) =>
    setCarouselCards((prev) => prev.map((c, i) => i === idx ? { ...c, [field]: val } : c));

  // ─────────────────────────────────────────────────────────────────────────

  const inputSX: React.CSSProperties = { marginBottom: 0 };
  const rowSX: React.CSSProperties = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 };
  const draftKey = useMemo(
    () => `facebook:create:${wizardMode || 'full'}:${existingCampaignId || 0}:${existingAdSetId || 0}`,
    [existingAdSetId, existingCampaignId, wizardMode],
  );
  const draftLoadedRef = useRef(true);
  const lastTempDraftSignatureRef = useRef('');

  const buildDraftPayload = useCallback((): CreateDraftPayload => ({
    platform,
    step,
    maxVisitedStep,
    draftParentCampaignID: existingCampaignId || undefined,
    draftParentAdSetID: existingAdSetId || undefined,
    selectedAccountID,
    campaignName,
    campaignObjective,
    campaignStatus,
    cboEnabled,
    salesBudgetScope,
    dailyBudget,
    spendCap,
    campaignLifetimeBudget,
    useCampaignLifetimeBudget,
    campaignTags,
    adsetTags,
    adTags,
    adsetName,
    budgetType,
    adsetDailyBudget,
    adsetLifetimeBudget,
    optimizationGoal,
    destinationType,
    selectedCountries,
    excludedCountries,
    ageMin,
    ageMax,
    genders,
    bidStrategy,
    bidAmount,
    startTime,
    endTime,
    adSetStatus,
    frequencyCap,
    frequencyCapResetPeriod,
    clickAttributionDays,
    engagedViewAttributionDays,
    viewAttributionDays,
    excludedCustomAudienceIDs,
    publisherPlatforms,
    facebookPositions,
    instagramPositions,
    devicePlatforms,
    userOS,
    placementMode,
    locales,
    bidConstraintsROAS,
    selectedInterests,
    selectedBehaviors,
    selectedCustomAudienceIDs,
    selectedPixelID,
    customEventType,
    selectedCatalogID,
    selectedProductSetID,
    showCatalog,
    adName,
    adStatus,
    pageID,
    igActorID,
    partnershipCode,
    partnershipIGUserID,
    partnershipPageID,
    showPartnership,
    partnershipContentSource,
    adType,
    existingCreativeID,
    imageHash,
    imagePreviewURL,
    imageS3URL,
    videoID,
    videoS3URL,
    thumbHash,
    thumbPreviewURL,
    message,
    headline,
    description,
    link,
    caption,
    ctaType,
    urlTags,
    carouselCards,
  }), [
    platform, step, maxVisitedStep, existingCampaignId, existingAdSetId, selectedAccountID, campaignName, campaignObjective, campaignStatus, cboEnabled,
    salesBudgetScope, dailyBudget, spendCap, campaignLifetimeBudget, useCampaignLifetimeBudget, campaignTags, adsetTags,
    adTags, adsetName, budgetType, adsetDailyBudget, adsetLifetimeBudget, optimizationGoal, destinationType,
    selectedCountries, excludedCountries, ageMin, ageMax, genders, bidStrategy, bidAmount, startTime, endTime,
    adSetStatus, frequencyCap, frequencyCapResetPeriod, clickAttributionDays, engagedViewAttributionDays,
    viewAttributionDays, excludedCustomAudienceIDs, publisherPlatforms, facebookPositions, instagramPositions,
    devicePlatforms, userOS, placementMode, locales, bidConstraintsROAS, selectedInterests, selectedBehaviors,
    selectedCustomAudienceIDs, selectedPixelID, customEventType, selectedCatalogID, selectedProductSetID, showCatalog,
    adName, adStatus, pageID, igActorID, partnershipCode, partnershipIGUserID, showPartnership, partnershipContentSource, adType,
    partnershipPageID, existingCreativeID, imageHash, imagePreviewURL, imageS3URL, videoID, videoS3URL, thumbHash, thumbPreviewURL,
    message, headline, description, link, caption, ctaType, urlTags, carouselCards,
  ]);
  const currentDraftEntityType = useMemo<'campaign' | 'adset' | 'ad'>(() => {
    if (step === 1) return 'campaign';
    if (step === 2) return 'adset';
    return 'ad';
  }, [step]);
  const buildDraftRequest = useCallback((payload: CreateDraftPayload): DraftPayloadRequest => ({
    platform: 'facebook',
    scene: 'create',
    entity_type: currentDraftEntityType,
    draft_key: draftKey,
    draft_name: wizardMode || 'facebook-create',
    site_id: siteId || undefined,
    payload,
  }), [currentDraftEntityType, draftKey, siteId, wizardMode]);

  const saveManualDraftSilently = useCallback(async (payloadOverride?: CreateDraftPayload) => {
    if (!brandId || !draftLoadedRef.current || platform !== 'facebook') return;
    const payload = payloadOverride ?? buildDraftPayload();
    try {
      await accountsApi.saveManualDraft(brandId, buildDraftRequest(payload));
      lastTempDraftSignatureRef.current = JSON.stringify(payload);
      return true;
    } catch {
      return null;
    }
  }, [brandId, buildDraftPayload, buildDraftRequest, platform]);

  const handleCloseAction = useCallback(async () => {
    if (step === 3) {
      const shouldSave = await confirmDialog({
        title: '保存草稿',
        message: '关闭前是否保存当前广告数据为草稿？保存草稿不会校验表单，也不会创建或同步到 Facebook。',
        confirmText: '保存并关闭',
      });
      if (shouldSave) {
        await saveManualDraftSilently();
      }
      navigate(`/campaigns/${platform}`);
      return;
    }
    await saveManualDraftSilently();
    navigate(`/campaigns/${platform}`);
  }, [navigate, platform, saveManualDraftSilently, step]);

  const handleExitToList = useCallback(async () => {
    await saveManualDraftSilently();
    navigate(`/campaigns/${platform}`);
  }, [navigate, platform, saveManualDraftSilently]);

  const hierarchyDraftItems = [
    {
      step: 1 as WizardStep,
      title: '广告系列',
      value: campaignName.trim() || (existingCampaignId ? `#${existingCampaignId}` : '未填写'),
      meta: OBJECTIVES.find((o) => o.value === campaignObjective)?.label || campaignObjective,
    },
    {
      step: 2 as WizardStep,
      title: '广告组',
      value: adsetName.trim() || (existingAdSetId ? `#${existingAdSetId}` : '未填写'),
      meta: selectedCountries.length > 0 ? selectedCountries.join(', ') : '受众未完成',
    },
    {
      step: 3 as WizardStep,
      title: '广告',
      value: adName.trim() || '未填写',
      meta: adType === 'catalog' || isSalesCatalogAd ? '目录广告' : adType === 'video' ? '视频广告' : adType === 'carousel' ? '轮播广告' : '图片广告',
    },
  ];

  return (
    <div className="page-section visible">
      {/* Toolbar */}
      <div className="toolbar" style={{ justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div className="breadcrumb">
          <span style={{ cursor: 'pointer', color: 'var(--primary-light)' }} onClick={() => { void handleCloseAction(); }}>广告计划</span>
          <span className="bc-sep">/</span>
          <span style={{ color: 'var(--text2)' }}>{wizardMode === 'add_adset' ? '新增广告组' : wizardMode === 'add_ad' ? '新增广告' : createUnder ? (createUnder.entity === 'campaign' ? '新增广告组' : '新增广告') : '新建广告'}</span>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={handleToolbarBack}>
          {activePlatform.available && platform === 'facebook' && parentDataLoaded && step > 1 ? '← 返回上一级' : '← 返回列表'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 320px', gap: 20, alignItems: 'start' }}>

        {/* ── Left: Form ──────────────────────────────────────────────────── */}
        <div ref={formCardRef} className="card" style={{ padding: 24 }}>
          {/* ── Platform selector (hidden in skip-step mode) ────────────── */}
          {!platformLocked && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 16 }}>
            {AD_PLATFORMS.map((p) => (
              <button
                key={p.key}
                onClick={() => {
                  setPlatform(p.key);
                  setError(null);
                  setStep(1);
                  setMaxVisitedStep(1);
                  if (p.key === 'tiktok') {
                    setTikTokStep(1);
                    setTikTokObjective('TRAFFIC');
                    setTikTokAdFormat('SINGLE_VIDEO');
                  }
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '7px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  fontWeight: 600, fontSize: 13, transition: 'all .15s',
                  background: platform === p.key ? 'var(--primary)' : 'var(--surface2)',
                  color: platform === p.key ? '#fff' : p.available ? 'var(--text)' : 'var(--text3)',
                  opacity: p.available ? 1 : 0.75,
                  position: 'relative',
                }}
              >
                <span>{p.icon}</span>
                <span>{p.label}</span>
                {p.available && (
                  <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'rgba(34,197,94,.14)', color: '#22c55e', marginLeft: 2 }}>
                    可创建广告
                  </span>
                )}
                {!p.available && (
                  <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: 'var(--surface3)', color: 'var(--text3)', marginLeft: 2 }}>
                    即将上线
                  </span>
                )}
              </button>
            ))}
          </div>
          )}

          {/* ── Coming-soon panel for unavailable platforms ───────────────── */}
          {!activePlatform.available && (() => {
            const p = activePlatform;
            return (
              <div style={{ textAlign: 'center', padding: '60px 24px' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>{p.icon}</div>
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: 'var(--text)' }}>
                  {p.label} 广告创建即将上线
                </div>
                <div style={{ fontSize: 14, color: 'var(--text3)', maxWidth: 360, margin: '0 auto', lineHeight: 1.7 }}>
                  我们正在对接 {p.label} 广告接口，功能开放后将在此页面直接创建{p.label}广告系列、广告组和广告。
                </div>
              </div>
            );
          })()}

          {/* ── Wizard for available platforms ─────────────────────────────── */}
          {activePlatform.available && platform === 'google' && (
            <CreateGoogleSearchAdWizard
              brandId={brandId}
              siteId={siteId}
              onCancel={() => { void handleCloseAction(); }}
              onCreated={() => {
                showToast('Google 广告已创建，正在发布', 'success');
                setTimeout(() => navigate(`/campaigns/${platform}`), 900);
              }}
            />
          )}

          {activePlatform.available && platform === 'tiktok' && (
            <CreateTikTokAdWizard
              brandId={brandId}
              siteId={siteId}
              createUnder={createUnder}
              onCancel={() => { void handleCloseAction(); }}
              initialAccountID={urlAccountId}
              onStepChange={setTikTokStep}
              onObjectiveChange={setTikTokObjective}
              onAdFormatChange={setTikTokAdFormat}
              onCreated={() => {
                showToast('TikTok 广告已创建成功', 'success');
                setTimeout(() => navigate(`/campaigns/${platform}`), 900);
              }}
            />
          )}

          {activePlatform.available && platform !== 'google' && platform !== 'tiktok' && <>

          {/* Step progress bar */}
          <HorizontalStepBar step={step} maxStep={maxVisitedStep} onStepClick={handleStepJump} />

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
            gap: 8,
            marginBottom: 16,
          }}>
            {hierarchyDraftItems.map((item) => {
              const reachable = item.step <= maxVisitedStep;
              const active = item.step === step;
              return (
                <button
                  key={item.step}
                  type="button"
                  disabled={!reachable}
                  onClick={() => handleStepJump(item.step)}
                  style={{
                    minWidth: 0,
                    textAlign: 'left',
                    padding: '9px 10px',
                    borderRadius: 8,
                    border: `1px solid ${active ? 'rgba(18,16,184,.42)' : 'var(--border)'}`,
                    background: active ? 'var(--primary-bg)' : 'var(--surface2)',
                    cursor: reachable ? 'pointer' : 'default',
                    opacity: reachable ? 1 : 0.6,
                  }}
                  title={reachable ? `查看/修改${item.title}` : undefined}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: active ? 'var(--primary-light)' : 'var(--text2)' }}>{item.title}</span>
                    {reachable && <span style={{ fontSize: 10, color: 'var(--text3)' }}>编辑</span>}
                  </div>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>{item.value}</div>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{item.meta}</div>
                </button>
              );
            })}
          </div>

          {/* Loading parent data overlay */}
          {!parentDataLoaded && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 24px', gap: 12 }}>
              <div className="spinner" style={{ width: 28, height: 28 }} />
              <span style={{ fontSize: 14, color: 'var(--text3)' }}>正在加载{wizardMode === 'add_adset' ? '广告系列' : '广告组'}数据...</span>
            </div>
          )}

          {parentDataLoaded && <>
          {/* Error banner */}
          {error && (() => {
            const fb = parseFbError(error);
            return (
              <div ref={errorRef} style={{
                marginBottom: 16, borderRadius: 8, overflow: 'hidden',
                border: '1px solid rgba(239,68,68,.4)',
              }}>
                {/* Header row */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '8px 14px', background: 'rgba(239,68,68,.12)',
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" style={{ flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#ef4444' }}>
                    {fb.step ? `${fb.step}参数错误` : 'Facebook 返回错误'}
                  </span>
                  {fb.code && (
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>
                      {fb.code}
                    </span>
                  )}
                </div>
                {/* Message */}
                <div style={{ padding: '8px 14px 4px', fontSize: 13, color: 'var(--text)', lineHeight: 1.5 }}>
                  {fb.message || error}
                </div>
                {/* Hint */}
                {fb.hint && (
                  <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: 6,
                    padding: '6px 14px 10px', fontSize: 12, color: '#d97706',
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 2 }}>
                      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>建议：{fb.hint}</span>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── Step 1: Campaign ─────────────────────────────────────────── */}
          {step === 1 && (
            <div>
              <SectionCard title="账号与名称">
                <FormGroup>
                  <Label required>授权账号</Label>
                  <MultiSelect
                    single
                    searchable
                    options={accountOptions.map((o) => ({ value: o.account_id, label: o.account_name || o.account_id }))}
                    value={selectedAccountID ? [selectedAccountID] : []}
                    onChange={(values) => setSelectedAccountID(values[0] ?? '')}
                    placeholder={`请选择 ${activePlatform.label} 授权账号`}
                  />
                </FormGroup>
                <FormGroup>
                  <Label required>广告系列名称</Label>
                  <input className="form-input" style={inputSX} placeholder="例：Zeelool_Titanium_US_2026Q1" value={campaignName} onChange={(e) => setCampaignName(e.target.value)} />
                </FormGroup>
              </SectionCard>

              <SectionCard title="推广目标">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {OBJECTIVES.filter((obj) => objectiveValues.includes(obj.value)).map((obj) => (
                    <label key={obj.value} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                      padding: '10px 12px', borderRadius: 8,
                      border: `1px solid ${campaignObjective === obj.value ? 'rgba(18,16,184,.45)' : 'var(--border)'}`,
                      background: campaignObjective === obj.value ? 'var(--primary-bg)' : 'var(--surface2)',
                    }}>
                      <input type="radio" name="objective" value={obj.value} checked={campaignObjective === obj.value}
                        onChange={() => setCampaignObjective(obj.value)} style={{ marginTop: 3, accentColor: 'var(--primary)' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6 }}>
                          {obj.label}
                          {campaignObjective === obj.value && (
                            <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'rgba(18,16,184,.15)', color: 'var(--primary-light)' }}>
                              {obj.billingHint}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{obj.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </SectionCard>

              <SectionCard title="预算与状态">
                <div style={rowSX}>
                  <FormGroup>
                    <Label>初始状态</Label>
                    <MultiSelect
                      single
                      options={[
                        { value: 'PAUSED', label: '暂停（推荐）' },
                        { value: 'ACTIVE', label: '立即投放' },
                      ]}
                      value={[campaignStatus]}
                      onChange={(values) => setCampaignStatus(values[0] ?? 'PAUSED')}
                    />
                  </FormGroup>
                  <FormGroup>
                    {usesBudgetScopeSelector ? (
                      <>
                        <Label>预算策略</Label>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                          {([
                            { value: 'campaign', label: '广告系列预算' },
                            { value: 'adset', label: '广告组预算' },
                          ] as const).map((item) => (
                            <label key={item.value} style={{
                              display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 12,
                              padding: '5px 12px', borderRadius: 6,
                              border: `1px solid ${salesBudgetScope === item.value ? 'rgba(18,16,184,.4)' : 'var(--border)'}`,
                              background: salesBudgetScope === item.value ? 'var(--primary-bg)' : 'var(--surface2)',
                              color: salesBudgetScope === item.value ? 'var(--primary-light)' : 'var(--text2)',
                            }}>
                              <input
                                type="radio"
                                checked={salesBudgetScope === item.value}
                                onChange={() => setSalesBudgetScope(item.value)}
                                style={{ accentColor: 'var(--primary)' }}
                              />
                              {item.label}
                            </label>
                          ))}
                        </div>
                      </>
                    ) : supportsCBO ? (
                      <>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', marginBottom: 8 }}>
                          <div
                            onClick={() => { setCboEnabled(!cboEnabled); if (cboEnabled) { setDailyBudget(''); setCampaignLifetimeBudget(''); } }}
                            style={{
                              width: 36, height: 20, borderRadius: 10, position: 'relative', flexShrink: 0,
                              background: cboEnabled ? 'var(--primary)' : 'var(--border)',
                              transition: 'background .2s', cursor: 'pointer',
                            }}>
                            <div style={{
                              position: 'absolute', top: 2, left: cboEnabled ? 18 : 2, width: 16, height: 16,
                              borderRadius: '50%', background: '#fff', transition: 'left .2s',
                            }} />
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: cboEnabled ? 'var(--text)' : 'var(--text2)' }}>广告系列预算优化（CBO）</span>
                        </label>
                        {!cboEnabled && (
                          <Hint style={{ color: 'var(--text3)', marginBottom: 8 }}>关闭：预算在广告组层级单独设置</Hint>
                        )}
                      </>
                    ) : (
                      <>
                        <Label>预算策略</Label>
                        <Hint style={{ color: 'var(--text3)', marginBottom: 8 }}>当前使用广告组预算</Hint>
                      </>
                    )}
                    {usesCampaignBudget ? (
                      <>
                        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                          {([{ v: false, lbl: '日预算' }, { v: true, lbl: '终生预算' }]).map(({ v, lbl }) => {
                            const active = useCampaignLifetimeBudget === v;
                            return (
                              <label key={String(v)} style={{
                                display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 12,
                                padding: '4px 10px', borderRadius: 6,
                                border: `1px solid ${active ? 'rgba(18,16,184,.4)' : 'var(--border)'}`,
                                background: active ? 'var(--primary-bg)' : 'var(--surface2)',
                                color: active ? 'var(--primary-light)' : 'var(--text2)',
                              }}>
                                <input type="radio" checked={active}
                                  onChange={() => setUseCampaignLifetimeBudget(v)}
                                  style={{ accentColor: 'var(--primary)' }} />
                                {lbl}
                              </label>
                            );
                          })}
                        </div>
                        {useCampaignLifetimeBudget ? (
                          <>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <div style={{ position: 'relative', flex: 1 }}>
                                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', fontSize: 13 }}>$</span>
                                <input className="form-input" style={{ paddingLeft: 22 }} type="number" min="1" step="1" placeholder="终生总预算" value={campaignLifetimeBudget} onChange={(e) => setCampaignLifetimeBudget(e.target.value)} />
                              </div>
                              <span style={{ display: 'flex', alignItems: 'center', fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>USD 总额</span>
                            </div>
                            <Hint>终生预算在整个投放期间使用，需设置结束时间</Hint>
                          </>
                        ) : (
                          <>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <div style={{ position: 'relative', flex: 1 }}>
                                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', fontSize: 13 }}>$</span>
                                <input className="form-input" style={{ paddingLeft: 22 }} type="number" min="1" step="1" placeholder="每日总预算" value={dailyBudget} onChange={(e) => setDailyBudget(e.target.value)} />
                              </div>
                              <span style={{ display: 'flex', alignItems: 'center', fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>USD / 天</span>
                            </div>
                            <Hint>由系列统一调配，Meta 自动分配到效果最佳的广告组</Hint>
                          </>
                        )}
                      </>
                    ) : (
                      <Hint style={{ color: 'var(--text3)' }}>
                        {supportsCBO ? '关闭：预算在广告组层级单独设置' : '当前使用广告组预算，预算类型和金额在广告组中填写'}
                      </Hint>
                    )}
                  </FormGroup>
                </div>
                <div style={rowSX}>
                  {(campaignObjective !== 'OUTCOME_AWARENESS' || cboEnabled) && (
                    <FormGroup>
                      <Label>广告系列竞价策略</Label>
                      <MultiSelect
                        single
                        options={bidStrategyOptions}
                        value={bidStrategy ? [bidStrategy] : []}
                        onChange={(values) => setBidStrategy(values[0] ?? '')}
                      />
                    </FormGroup>
                  )}
                  <FormGroup>
                    <Label>花费上限 (USD)</Label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', fontSize: 13 }}>$</span>
                      <input className="form-input" style={{ paddingLeft: 22 }} type="number" min="100" step="1" placeholder="" value={spendCap} onChange={(e) => setSpendCap(e.target.value)} />
                    </div>
                    <Hint>默认留空，不限制广告系列总花费上限</Hint>
                  </FormGroup>
                </div>
                {showConversion && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <Label>目录广告</Label>
                      <button
                        type="button"
                        onClick={handleSyncCatalogs}
                        disabled={syncingCatalogs || !selectedAccountID}
                        style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg2)', cursor: syncingCatalogs ? 'not-allowed' : 'pointer', color: 'var(--text2)', opacity: syncingCatalogs ? 0.6 : 1 }}
                      >
                        {syncingCatalogs ? '同步中...' : '↻ 同步'}
                      </button>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', marginBottom: 8 }}>
                      <input type="checkbox" checked={showCatalog} onChange={(e) => {
                        setShowCatalog(e.target.checked);
                        if (!e.target.checked) {
                          setSelectedCatalogID('');
                          setSelectedProductSetID('');
                          setProductSets([]);
                        }
                      }} style={{ accentColor: 'var(--primary)' }} />
                      <span style={{ fontSize: 13, color: 'var(--text2)' }}>添加目录广告</span>
                    </label>
                    {showCatalog && (
                      <div style={{ marginTop: 8 }}>
                        <MultiSelect
                          single
                          searchable
                          clearable
                          options={catalogOptions}
                          value={selectedCatalogID ? [selectedCatalogID] : []}
                          onChange={(vals) => {
                            setSelectedCatalogID(vals[0] ?? '');
                            setSelectedProductSetID('');
                            setProductSets([]);
                          }}
                          placeholder={!selectedAccountID ? '请先选择广告账户' : syncingCatalogs ? '目录同步中...' : `默认选择 ${DEFAULT_FACEBOOK_CATALOG_NAME}`}
                        />
                      </div>
                    )}
                  </div>
                )}
              </SectionCard>

              {/* ── 动态广告标签 ─────────────────────────────────────────── */}
              {tagConfig.filter(t => t.level === 'campaign').length > 0 && (
                <SectionCard title="广告标签">
                  <div className="tag-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px 10px' }}>
                    {tagConfig.filter(t => t.level === 'campaign').map(td => {
                      const flatOpts = td.options.flatMap(o =>
                        o.children && o.children.length > 0
                          ? o.children.map(c => ({ value: c.value, label: `${o.label} · ${c.label}` }))
                          : [{ value: o.value, label: o.label }]
                      );
                      return (
                        <div key={td.key}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>{td.label}</div>
                          {isPlacementTag(td) ? (() => {
                            return (
                              <MultiSelect
                                searchable
                                clearable
                                tagDisplay
                                placeholder="请选择版位（可多选）"
                                options={flatOpts}
                                value={parseMultiTagValue(campaignTags[td.key])}
                                onChange={(vals) => setCampaignTags(prev => setTagValue(prev, td.key, serializeMultiTagValue(vals)))}
                              />
                            );
                          })() : (
                            <MultiSelect single placeholder="请选择"
                              options={flatOpts}
                              value={campaignTags[td.key] ? [campaignTags[td.key]] : []}
                              onChange={(vals) => setCampaignTags(prev => ({ ...prev, [td.key]: vals[0] ?? '' }))}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </SectionCard>
              )}
            </div>
          )}

          {/* ── Step 2: AdSet ────────────────────────────────────────────── */}
          {step === 2 && (
            <div>
              <SectionCard title="基本信息">
                <div style={{ padding: '6px 10px', borderRadius: 6, background: 'var(--surface2)', border: '1px solid var(--border)', fontSize: 11, color: 'var(--text2)', marginBottom: 12 }}>
                  所属系列：<strong style={{ color: 'var(--primary-light)' }}>{campaignName}</strong>
                  <span style={{ marginLeft: 8, padding: '1px 6px', borderRadius: 4, background: 'rgba(18,16,184,.12)', color: 'var(--primary-light)', fontSize: 10 }}>
                    {OBJECTIVES.find((o) => o.value === campaignObjective)?.label}
                  </span>
                  {usesCampaignBudget && (
                    <span style={{ marginLeft: 8, padding: '1px 6px', borderRadius: 4, background: 'rgba(34,197,94,.12)', color: 'var(--green)', fontSize: 10 }}>
                      {useCampaignLifetimeBudget ? `系列总预算 $${campaignLifetimeBudget || '0'}` : `系列日预算 $${dailyBudget || '0'}/天`}
                    </span>
                  )}
                </div>
                <FormGroup>
                  <Label required>广告组名称</Label>
                  <input className="form-input" style={inputSX} placeholder="例：US_Female_18-45_Titanium" value={adsetName} onChange={(e) => setAdsetName(e.target.value)} />
                </FormGroup>
                {campaignObjective === 'OUTCOME_AWARENESS' && (
                  <FormGroup>
                    <Label required>Facebook 公共主页</Label>
                    {pagesLoading ? (
                      <div style={{ fontSize: 12, color: 'var(--text3)', padding: '8px 0' }}>正在获取主页列表...</div>
                    ) : pages.length > 0 ? (
                      <MultiSelect
                        single
                        searchable
                        options={pages.map((p) => ({ value: p.id, label: `${p.name} (${p.id})` }))}
                        value={pageID ? [pageID] : []}
                        onChange={(vals) => setPageID(vals[0] ?? '')}
                        placeholder="搜索并选择 Facebook 公共主页"
                      />
                    ) : (
                      <input className="form-input" placeholder="输入 Facebook 主页 ID（数字）"
                        value={pageID} onChange={(e) => setPageID(e.target.value)} />
                    )}
                  </FormGroup>
                )}
                {campaignObjective === 'OUTCOME_SALES' && showCatalog && (
                  <FormGroup>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <Label required>商品系列</Label>
                      <button
                        type="button"
                        onClick={handleSyncProductSets}
                        disabled={syncingProductSets || !selectedAccountID || !selectedCatalogID}
                        style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg2)', cursor: syncingProductSets ? 'not-allowed' : 'pointer', color: 'var(--text2)', opacity: syncingProductSets ? 0.6 : 1 }}
                      >
                        {syncingProductSets ? '同步中...' : '↻ 同步'}
                      </button>
                    </div>
                    {selectedCatalogID ? (
                      <>
                        <MultiSelect
                          single
                          searchable
                          clearable
                          options={productSetOptions}
                          value={selectedProductSetID ? [selectedProductSetID] : []}
                          onChange={(vals) => setSelectedProductSetID(vals[0] ?? '')}
                          placeholder={syncingProductSets ? '商品系列同步中...' : '搜索并选择商品系列'}
                        />
                        <Hint>
                          仅显示当前目录「{catalogs.find((item) => item.id === selectedCatalogID)?.name || selectedCatalogID}」下的商品系列，共 {productSets.length} 个。
                        </Hint>
                      </>
                    ) : (
                      <div className="form-input" style={{ ...inputSX, display: 'flex', alignItems: 'center', color: 'var(--text3)', background: 'var(--surface2)' }}>
                        请先在广告系列中选择目录
                      </div>
                    )}
                  </FormGroup>
                )}
              </SectionCard>

              <SectionCard title="转化">
                {showDestination && (
                  <FormGroup>
                    <Label>转化位置</Label>
                    {isCatalogDestinationLocked ? (
                      <>
                        <div className="form-input" style={{ display: 'flex', alignItems: 'center', color: 'var(--text2)', background: 'var(--surface2)' }}>
                          {FACEBOOK_CATALOG_DESTINATION}
                        </div>
                        <Hint>目录广告固定使用该转化位置枚举，不支持手动切换。</Hint>
                      </>
                    ) : (
                      <MultiSelect
                        single
                        options={[{ value: 'WEBSITE', label: '网站' }]}
                        value={[destinationType]}
                        onChange={(values) => setDestinationType(values[0] ?? 'WEBSITE')}
                      />
                    )}
                  </FormGroup>
                )}
                <FormGroup>
                  <Label>优化目标</Label>
                  <MultiSelect
                    single
                    options={[
                      { value: '', label: '自动选择' },
                      ...(goalOptionsByObjective[campaignObjective] || []),
                    ]}
                    value={[optimizationGoal]}
                    onChange={(values) => setOptimizationGoal(values[0] ?? '')}
                  />
                </FormGroup>
                {showPixelSelector && (
                  <FormGroup>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <Label required={pixelRequired}>数据集（Pixel）</Label>
                      <button
                        type="button"
                        onClick={handleSyncAssets}
                        disabled={syncingAssets || !selectedAccountID}
                        style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg2)', cursor: syncingAssets ? 'not-allowed' : 'pointer', color: 'var(--text2)', opacity: syncingAssets ? 0.6 : 1 }}
                        title="同步 Pixel 列表"
                      >
                        {syncingAssets ? '同步中...' : '↻ 同步'}
                      </button>
                    </div>
                    {pixels.length > 0 ? (
                      <MultiSelect
                        single
                        searchable
                        options={pixels.map((p) => ({ value: p.id, label: p.name || p.id }))}
                        value={selectedPixelID ? [selectedPixelID] : []}
                        onChange={(values) => setSelectedPixelID(values[0] ?? '')}
                        placeholder="选择 Pixel"
                      />
                    ) : (
                      <>
                        <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(245,158,11,.06)', border: '1px solid rgba(245,158,11,.25)', fontSize: 12, color: 'var(--amber)', marginBottom: 8 }}>
                          未找到已授权 Pixel。如需转化追踪，请在 Business Manager 中创建并分配给此账号，或直接输入 Pixel ID。
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input className="form-input" style={{ flex: 1 }} placeholder="直接输入 Pixel ID（数字）"
                            value={manualPixelID} onChange={(e) => setManualPixelID(e.target.value)}
                            onBlur={() => { if (manualPixelID.trim()) setSelectedPixelID(manualPixelID.trim()); }} />
                          <button className="btn btn-primary btn-sm" onClick={() => { if (manualPixelID.trim()) setSelectedPixelID(manualPixelID.trim()); }} style={{ whiteSpace: 'nowrap' }}>
                            使用此 ID
                          </button>
                        </div>
                        {selectedPixelID && <Hint style={{ color: 'var(--green)' }}>已设置 Pixel ID：{selectedPixelID}</Hint>}
                      </>
                    )}
                  </FormGroup>
                )}
                {selectedPixelID && showConversion && (
                  <FormGroup>
                    <Label>转化事件</Label>
                    <MultiSelect
                      single
                      options={CONVERSION_EVENTS}
                      value={[customEventType]}
                      onChange={(values) => setCustomEventType(values[0] ?? customEventType)}
                    />
                    <Hint>默认购物。转化计数方式：所有转化</Hint>
                  </FormGroup>
                )}
                {showFacebookAttributionModel && (
                  <FormGroup>
                    <Label>归因模型</Label>
                    <MultiSelect
                      single
                      options={FACEBOOK_ATTRIBUTION_MODEL_OPTIONS}
                      value={[facebookAttributionModel]}
                      onChange={(values) => setFacebookAttributionModel((values[0] ?? facebookAttributionModel) as FacebookAttributionModel)}
                    />
                    {isIncrementalAttribution && (
                      <Hint>增量归因不支持手动归因设置，系统会根据该模型自动优化。</Hint>
                    )}
                  </FormGroup>
                )}
                {showFacebookAttributionModel && (
                  <FormGroup>
                    <Label>归因设置</Label>
                    <div style={{
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1px solid var(--border)',
                      background: 'var(--surface2)',
                    }}>
                      <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 10 }}>
                        {isIncrementalAttribution ? '增量归因' : (showConversion ? facebookAttributionSummary : '标准归因')}
                      </div>
                      {isIncrementalAttribution ? (
                        <Hint>系统会按增量归因模型自动设置。</Hint>
                      ) : showConversion ? (
                        <div style={{ display: 'grid', gap: 10 }}>
                          <div>
                            <Label>点击</Label>
                            <MultiSelect
                              single
                              options={[
                                { value: '1', label: '1天' },
                                { value: '7', label: '7天' },
                              ]}
                              value={[clickAttributionDays]}
                              onChange={(values) => setClickAttributionDays((values[0] ?? '7') as '1' | '7')}
                            />
                          </div>
                          <div>
                            <Label>互动转化</Label>
                            <MultiSelect
                              single
                              options={[
                                { value: '0', label: '无' },
                                { value: '1', label: '1天' },
                              ]}
                              value={[engagedViewAttributionDays]}
                              onChange={(values) => setEngagedViewAttributionDays((values[0] ?? '0') as '0' | '1')}
                            />
                          </div>
                          <div>
                            <Label>浏览</Label>
                            <MultiSelect
                              single
                              options={[
                                { value: '0', label: '无' },
                                { value: '1', label: '1天' },
                              ]}
                              value={[viewAttributionDays]}
                              onChange={(values) => setViewAttributionDays((values[0] ?? '0') as '0' | '1')}
                            />
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </FormGroup>
                )}
                <div style={rowSX}>
                  <FormGroup>
                    <Label>系列竞价策略</Label>
                    <div className="form-input" style={{ ...inputSX, display: 'flex', alignItems: 'center', color: 'var(--text2)', background: 'var(--surface2)' }}>
                      {bidStrategyOptions.find((option) => option.value === bidStrategy)?.label || '最高数量'}
                    </div>
                  </FormGroup>
                  <FormGroup>
                    <Label>广告组状态</Label>
                    <MultiSelect
                      single
                      options={[
                        { value: 'PAUSED', label: '暂停' },
                        { value: 'ACTIVE', label: '立即投放' },
                      ]}
                      value={[adSetStatus]}
                      onChange={(values) => setAdSetStatus(values[0] ?? 'PAUSED')}
                    />
                  </FormGroup>
                </div>
                {bidStrategy === 'LOWEST_COST_WITHOUT_CAP' && (
                  <FormGroup>
                    <Label>策略说明</Label>
                    <Hint>{campaignObjective === 'OUTCOME_SALES' ? '不需要设置目标值。销量目标下会根据优化目标自动按最高数量或最高价值投放。' : '不需要设置目标值，系统会按最高数量争取结果。'}</Hint>
                  </FormGroup>
                )}
                {bidStrategy === 'LOWEST_COST_WITH_BID_CAP' && (
                  <FormGroup>
                    <Label required>出价上限 (USD)</Label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', fontSize: 13 }}>$</span>
                      <input className="form-input" style={{ paddingLeft: 22 }} type="number" min="0.01" step="0.01" placeholder="10.00" value={bidAmount} onChange={(e) => setBidAmount(e.target.value)} />
                    </div>
                    <Hint>每次竞价的最高出价金额，系统不会超过此上限</Hint>
                  </FormGroup>
                )}
                {bidStrategy === 'COST_CAP' && (
                  <FormGroup>
                    <Label required>目标费用上限 (USD)</Label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', fontSize: 13 }}>$</span>
                      <input className="form-input" style={{ paddingLeft: 22 }} type="number" min="0.01" step="0.01" placeholder="10.00" value={bidAmount} onChange={(e) => setBidAmount(e.target.value)} />
                    </div>
                    <Hint>Facebook 会尽量将平均每次转化成本控制在此金额以内</Hint>
                  </FormGroup>
                )}
                {bidStrategy === 'LOWEST_COST_WITH_MIN_ROAS' && (
                  <FormGroup>
                    <Label>最低 ROAS 目标</Label>
                    <input className="form-input" type="number" min="0.1" step="0.1" placeholder="例：2.0（即 200% ROAS）" value={bidConstraintsROAS} onChange={(e) => setBidConstraintsROAS(e.target.value)} />
                    <Hint>{`该策略下成效目标应选择「${goalLabelByValue('VALUE')}」`}</Hint>
                  </FormGroup>
                )}
                {campaignObjective === 'OUTCOME_AWARENESS' && (
                  <div style={rowSX}>
                    <FormGroup>
                      <Label>频次上限（次数）</Label>
                      <input className="form-input" type="number" min="0" step="1" placeholder="不限（留空）" value={frequencyCap} onChange={(e) => setFrequencyCap(e.target.value)} />
                      <Hint>每人在重置周期内最多看到几次广告</Hint>
                    </FormGroup>
                    <FormGroup>
                      <Label>重置周期（天）</Label>
                      <input className="form-input" type="number" min="1" step="1" placeholder="7" value={frequencyCapResetPeriod} onChange={(e) => setFrequencyCapResetPeriod(e.target.value)} />
                    </FormGroup>
                  </div>
              )}
              </SectionCard>

              {/* ── 预算与排期 ─────────────────────────────────────── */}
              <SectionCard title="预算与排期">
                {usesCampaignBudget ? (
                  <FormGroup>
                    <Label>预算</Label>
                    <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.8 }}>
                      {campaignBudgetSummary}
                    </div>
                  </FormGroup>
                ) : (
                  <div style={{ marginBottom: 12 }}>
                    <Label style={{ marginBottom: 8, display: 'block' }}>预算类型</Label>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                      {(['daily', 'lifetime'] as const).map((t) => (
                        <label key={t} style={{
                          display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 12,
                          padding: '5px 14px', borderRadius: 6,
                          border: `1px solid ${budgetType === t ? 'rgba(18,16,184,.4)' : 'var(--border)'}`,
                          background: budgetType === t ? 'var(--primary-bg)' : 'var(--surface2)',
                          color: budgetType === t ? 'var(--primary-light)' : 'var(--text2)',
                        }}>
                          <input type="radio" checked={budgetType === t} onChange={() => setBudgetType(t)} style={{ accentColor: 'var(--primary)' }} />
                          {t === 'daily' ? '日预算' : '总预算（排期）'}
                        </label>
                      ))}
                    </div>
                    {budgetType === 'daily' ? (
                      <FormGroup>
                        <Label required>日预算 (USD)</Label>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <div style={{ position: 'relative', flex: 1 }}>
                            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', fontSize: 13 }}>$</span>
                            <input className="form-input" style={{ paddingLeft: 22 }} type="number" min="1" step="1" placeholder="10" value={adsetDailyBudget} onChange={(e) => setAdsetDailyBudget(e.target.value)} />
                          </div>
                          <span style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>USD / 天</span>
                        </div>
                      </FormGroup>
                    ) : (
                      <FormGroup>
                        <Label required>总预算 (USD)</Label>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <div style={{ position: 'relative', flex: 1 }}>
                            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)', fontSize: 13 }}>$</span>
                            <input className="form-input" style={{ paddingLeft: 22 }} type="number" min="1" step="1" placeholder="500" value={adsetLifetimeBudget} onChange={(e) => setAdsetLifetimeBudget(e.target.value)} />
                          </div>
                          <span style={{ fontSize: 12, color: 'var(--text3)', whiteSpace: 'nowrap' }}>USD 总额</span>
                        </div>
                        <Hint style={{ color: 'var(--amber)' }}>总预算需设置结束时间</Hint>
                      </FormGroup>
                    )}
                  </div>
                )}

                <div style={rowSX}>
                  <FormGroup>
                    <Label>开始时间</Label>
                    <SharedDateTimePicker value={startTime} onChange={setStartTime} />
                  </FormGroup>
                  <FormGroup>
                    <Label required={budgetType === 'lifetime' && !usesCampaignBudget}>结束时间</Label>
                    <SharedDateTimePicker value={endTime} onChange={setEndTime} placeholder="持续投放（留空）" />
                    {budgetType === 'lifetime' && !usesCampaignBudget
                      ? <Hint style={{ color: 'var(--amber)' }}>总预算时必填</Hint>
                      : <Hint>留空则持续投放</Hint>}
                  </FormGroup>
                </div>
              </SectionCard>

              {/* 版位与设备 */}
              <SectionCard title="版位与设备">
                <FacebookPlacementSelector
                  mode={placementMode}
                  onModeChange={setPlacementMode}
                  state={{ publisherPlatforms, facebookPositions, instagramPositions, devicePlatforms, userOS }}
                  onChange={(next) => {
                    setPublisherPlatforms(next.publisherPlatforms);
                    setFacebookPositions(next.facebookPositions);
                    setInstagramPositions(next.instagramPositions);
                    setDevicePlatforms(next.devicePlatforms);
                    setUserOS(next.userOS);
                  }}
                />
              </SectionCard>

              {/* Audience */}
              <SectionCard title="受众定向">
                <FormGroup>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <Label required>地区</Label>
                    <button
                      type="button"
                      onClick={handleSyncGeo}
                      disabled={syncingGeo || !selectedAccountID}
                      style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg2)', cursor: syncingGeo ? 'not-allowed' : 'pointer', color: 'var(--text2)', opacity: syncingGeo ? 0.6 : 1 }}
                    >
                      {syncingGeo ? '同步中...' : '↻ 同步'}
                    </button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>包含地区</div>
                      <MultiSelect
                        options={geoOptions}
                        value={selectedCountries}
                        onChange={(values) => {
                          const next = Array.from(new Set(values));
                          setSelectedCountries(next);
                          setExcludedCountries((prev) => prev.filter((code) => !next.includes(code)));
                        }}
                        placeholder={!selectedAccountID ? '请先选择授权账号' : geoLoading ? '加载地区中...' : '搜索并选择地区'}
                        searchable
                        clearable
                        tagDisplay
                      />
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>排除地区</div>
                      <MultiSelect
                        options={geoOptions}
                        value={excludedCountries}
                        onChange={(values) => {
                          const next = Array.from(new Set(values));
                          setExcludedCountries(next);
                          setSelectedCountries((prev) => prev.filter((code) => !next.includes(code)));
                        }}
                        placeholder={!selectedAccountID ? '请先选择授权账号' : geoLoading ? '加载地区中...' : '搜索并选择排除地区'}
                        searchable
                        clearable
                        tagDisplay
                      />
                    </div>
                  </div>
                </FormGroup>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 12 }}>
                  <FormGroup>
                    <Label>年龄下限</Label>
                    <input className="form-input" style={inputSX} type="number" min="18" max="64" value={ageMin} onChange={(e) => setAgeMin(e.target.value)} />
                  </FormGroup>
                  <FormGroup>
                    <Label>最大年龄</Label>
                    <input className="form-input" style={inputSX} type="number" min="19" max="65" value={ageMax} onChange={(e) => setAgeMax(e.target.value)} />
                  </FormGroup>
                  <FormGroup>
                    <Label>性别</Label>
                    <MultiSelect
                      single
                      options={[
                        { value: 'all', label: '不限' },
                        { value: '1', label: '男性' },
                        { value: '2', label: '女性' },
                      ]}
                      value={[genders]}
                      onChange={(values) => setGenders((values[0] ?? 'all') as 'all' | '1' | '2')}
                    />
                  </FormGroup>
                </div>
                <FormGroup>
                  <Label>语言</Label>
                  <MultiSelect
                    single
                    searchable
                    options={[
                      { value: '', label: '不限' },
                      ...FACEBOOK_LANGUAGE_OPTIONS.map((item) => ({ value: String(item.value), label: item.label })),
                    ]}
                    value={[locales[0] ? String(locales[0]) : '']}
                    onChange={(values) => setLocales(values[0] ? [Number(values[0])] : [])}
                  />
                </FormGroup>

                {/* Interest search */}
                <FormGroup>
                  <Label>细分兴趣</Label>
                  {selectedInterests.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 6 }}>
                      {selectedInterests.map((i) => (
                        <span key={i.id} style={{
                          display: 'flex', alignItems: 'center', gap: 4, fontSize: 11,
                          padding: '3px 8px', borderRadius: 20, background: 'var(--primary-bg)', border: '1px solid rgba(18,16,184,.3)', color: 'var(--primary-light)',
                        }}>
                          {i.name}
                          <button type="button" onClick={() => removeInterest(i.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 0, fontSize: 12, lineHeight: 1 }}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div ref={interestDropdownRef} style={{ position: 'relative' }}>
                    <input className="form-input" style={inputSX} placeholder="搜索兴趣标签（如：网购、健身）"
                      value={interestQuery}
                      onChange={(e) => {
                        const value = e.target.value;
                        setInterestQuery(value);
                        setInterestDropdownOpen(value.trim().length >= 2);
                      }}
                      onFocus={() => {
                        if (interestQuery.trim().length >= 2) setInterestDropdownOpen(true);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') setInterestDropdownOpen(false);
                      }}
                    />
                    {interestSearching && (
                      <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
                        <div className="spinner" style={{ width: 12, height: 12 }} />
                      </div>
                    )}
                    {interestDropdownOpen && interestOptions.length > 0 && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
                        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
                        marginTop: 2, maxHeight: 200, overflowY: 'auto', padding: 4,
                        boxShadow: '0 4px 16px rgba(0,0,0,.15)',
                      }}>
                        {interestOptions.map((i) => {
                          const selected = selectedInterests.some((item) => item.id === i.id);
                          return (
                          <div key={i.id} onClick={() => toggleInterest(i)} style={{
                            padding: '7px 10px', cursor: 'pointer', borderRadius: 5, fontSize: 12,
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            background: selected ? 'var(--primary-bg)' : 'transparent',
                          }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = selected ? 'var(--primary-bg)' : 'var(--surface2)')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = selected ? 'var(--primary-bg)' : 'transparent')}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ width: 14, height: 14, borderRadius: 3, border: `1.5px solid ${selected ? 'var(--primary)' : 'var(--border)'}`, background: selected ? 'var(--primary)' : 'transparent', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, lineHeight: 1 }}>{selected ? '✓' : ''}</span>
                              {i.name}
                            </span>
                            {i.audience_size_low ? (
                              <span style={{ color: 'var(--text3)', fontSize: 10 }}>{(i.audience_size_low / 1000000).toFixed(1)}M</span>
                            ) : null}
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </FormGroup>

                <FormGroup>
                  <Label>受众行为</Label>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <button
                      type="button"
                      onClick={() => setBehaviorSectionCollapsed((value) => !value)}
                      style={{ background: 'transparent', border: 0, padding: 0, color: 'var(--primary)', fontSize: 12, cursor: 'pointer' }}
                    >
                      {behaviorSectionCollapsed ? '展开受众行为' : '收起受众行为'}
                    </button>
                    <button
                      type="button"
                      onClick={handleSyncBehaviors}
                      disabled={syncingBehaviors || !selectedAccountID}
                      style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg2)', cursor: syncingBehaviors || !selectedAccountID ? 'not-allowed' : 'pointer', color: 'var(--text2)', opacity: syncingBehaviors || !selectedAccountID ? 0.6 : 1 }}
                    >
                      {syncingBehaviors ? '同步中...' : '↻ 同步'}
                    </button>
                  </div>
                  {!behaviorSectionCollapsed && selectedBehaviors.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 6 }}>
                      {selectedBehaviors.map((b) => (
                        <span key={b.id} style={{
                          display: 'flex', alignItems: 'center', gap: 4, fontSize: 11,
                          padding: '3px 8px', borderRadius: 20, background: 'var(--primary-bg)', border: '1px solid rgba(18,16,184,.3)', color: 'var(--primary-light)',
                        }}>
                          {b.name}
                          <button type="button" onClick={() => removeBehavior(b.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 0, fontSize: 12, lineHeight: 1 }}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  {!behaviorSectionCollapsed && (
                  <div style={{ position: 'relative' }}>
                    <input
                      className="form-input"
                      style={inputSX}
                      placeholder={!selectedAccountID ? '请先选择授权账号' : behaviorLoading ? '加载行为中...' : '搜索受众行为'}
                      value={behaviorFilter}
                      onChange={(e) => setBehaviorFilter(e.target.value)}
                      disabled={!selectedAccountID || behaviorLoading}
                    />
                    {(behaviorLoading || syncingBehaviors) && (
                      <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
                        <div className="spinner" style={{ width: 12, height: 12 }} />
                      </div>
                    )}
                    {behaviorOptions.length > 0 && (
                      <div style={{
                        marginTop: 6, maxHeight: 200, overflowY: 'auto', padding: 4,
                        border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)',
                      }}>
                        {behaviorOptions
                          .filter((b) => {
                            if (!behaviorFilter.trim()) return true;
                            const q = behaviorFilter.trim().toLowerCase();
                            return b.name.toLowerCase().includes(q) || (b.description || '').toLowerCase().includes(q);
                          })
                          .slice(0, 80)
                          .map((b) => (
                            <div
                              key={b.id}
                              onClick={() => addBehavior(b)}
                              style={{ padding: '7px 10px', cursor: 'pointer', borderRadius: 5, fontSize: 12 }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface2)')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                            >
                              <div>{b.name}</div>
                              {b.description && <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{b.description}</div>}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                  )}
                </FormGroup>

                {/* Custom audiences */}
                <FormGroup>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <Label>包含受众</Label>
                      <button
                        type="button"
                        onClick={() => setIncludeAudienceSectionCollapsed((value) => !value)}
                        style={{ background: 'transparent', border: 0, padding: 0, color: 'var(--primary)', fontSize: 12, cursor: 'pointer' }}
                      >
                        {includeAudienceSectionCollapsed ? '展开' : '收起'}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={handleSyncAssets}
                      disabled={syncingAssets || !selectedAccountID}
                      style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg2)', cursor: syncingAssets ? 'not-allowed' : 'pointer', color: 'var(--text2)', opacity: syncingAssets ? 0.6 : 1 }}
                      title="同步包含/排除受众等资产"
                    >
                      {syncingAssets ? '同步中...' : '↻ 同步'}
                    </button>
                  </div>
                  {customAudiencesLoading ? (
                    <div style={{ fontSize: 12, color: 'var(--text3)' }}>加载中…</div>
                  ) : customAudiences.length === 0 ? (
                    <div style={{ fontSize: 12, color: 'var(--text3)' }}>暂无自定义受众（需在 Facebook Ads Manager 中创建）</div>
                  ) : includeAudienceSectionCollapsed ? (
                    <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                      已选 {selectedCustomAudienceIDs.length} 个包含受众
                    </div>
                  ) : (
                    <div>
                      {selectedCustomAudienceIDs.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 6 }}>
                          {selectedCustomAudienceIDs.map((id) => {
                            const audience = customAudienceByID.get(id);
                            return (
                              <span key={id} style={{
                                display: 'flex', alignItems: 'center', gap: 4, fontSize: 11,
                                padding: '3px 8px', borderRadius: 20, background: 'var(--primary-bg)', border: '1px solid rgba(18,16,184,.3)', color: 'var(--primary-light)',
                              }}>
                                {audience ? formatCustomAudienceLabel(audience) : id}
                                <button type="button" onClick={() => setSelectedCustomAudienceIDs((prev) => prev.filter((item) => item !== id))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 0, fontSize: 12, lineHeight: 1 }}>×</button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                      <input
                        className="form-input"
                        style={{ ...inputSX, marginBottom: 6 }}
                        placeholder="搜索并多选包含受众"
                        value={includeAudienceFilter}
                        onChange={(e) => setIncludeAudienceFilter(e.target.value)}
                      />
                      <div style={{ maxHeight: 220, overflowY: 'auto', padding: 4, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }}>
                        {includeAudienceOptions.length === 0 ? (
                          <div style={{ padding: '14px 0', textAlign: 'center', fontSize: 12, color: 'var(--text3)' }}>暂无匹配受众</div>
                        ) : includeAudienceOptions.slice(0, 120).map((audience) => {
                          const checked = selectedCustomAudienceIDs.includes(audience.id);
                          const disabled = excludedCustomAudienceIDs.includes(audience.id);
                          return (
                            <label key={audience.id} style={{
                              display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 8px', borderRadius: 6,
                              cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
                              background: checked ? 'var(--primary-bg)' : 'transparent',
                            }}>
                              <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggleIncludedCustomAudience(audience.id)} style={{ marginTop: 2 }} />
                              <span style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ display: 'block', fontSize: 12, color: 'var(--text)' }}>{formatCustomAudienceLabel(audience)}</span>
                                {(audience.subtype || audience.description) && (
                                  <span style={{ display: 'block', fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{[audience.subtype, audience.description].filter(Boolean).join(' · ')}</span>
                                )}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </FormGroup>

                {/* Excluded audiences */}
                <FormGroup>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                    <Label>排除受众</Label>
                    <button
                      type="button"
                      onClick={() => setExcludeAudienceSectionCollapsed((value) => !value)}
                      style={{ background: 'transparent', border: 0, padding: 0, color: 'var(--primary)', fontSize: 12, cursor: 'pointer' }}
                    >
                      {excludeAudienceSectionCollapsed ? '展开' : '收起'}
                    </button>
                  </div>
                  {customAudiencesLoading ? (
                    <div style={{ fontSize: 12, color: 'var(--text3)' }}>加载中…</div>
                  ) : customAudiences.length === 0 ? (
                    <div style={{ fontSize: 12, color: 'var(--text3)' }}>暂无自定义受众（需在 Facebook Ads Manager 中创建）</div>
                  ) : excludeAudienceSectionCollapsed ? (
                    <div style={{ fontSize: 12, color: 'var(--text3)' }}>
                      已选 {excludedCustomAudienceIDs.length} 个排除受众
                    </div>
                  ) : (
                    <div>
                      {excludedCustomAudienceIDs.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 6 }}>
                          {excludedCustomAudienceIDs.map((id) => {
                            const audience = customAudienceByID.get(id);
                            return (
                              <span key={id} style={{
                                display: 'flex', alignItems: 'center', gap: 4, fontSize: 11,
                                padding: '3px 8px', borderRadius: 20, background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.25)', color: 'var(--danger)',
                              }}>
                                {audience ? formatCustomAudienceLabel(audience) : id}
                                <button type="button" onClick={() => setExcludedCustomAudienceIDs((prev) => prev.filter((item) => item !== id))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 0, fontSize: 12, lineHeight: 1 }}>×</button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                      <input
                        className="form-input"
                        style={{ ...inputSX, marginBottom: 6 }}
                        placeholder="搜索并多选排除受众"
                        value={excludeAudienceFilter}
                        onChange={(e) => setExcludeAudienceFilter(e.target.value)}
                      />
                      <div style={{ maxHeight: 220, overflowY: 'auto', padding: 4, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface)' }}>
                        {excludeAudienceOptions.length === 0 ? (
                          <div style={{ padding: '14px 0', textAlign: 'center', fontSize: 12, color: 'var(--text3)' }}>暂无匹配受众</div>
                        ) : excludeAudienceOptions.slice(0, 120).map((audience) => {
                          const checked = excludedCustomAudienceIDs.includes(audience.id);
                          const disabled = selectedCustomAudienceIDs.includes(audience.id);
                          return (
                            <label key={audience.id} style={{
                              display: 'flex', alignItems: 'flex-start', gap: 8, padding: '7px 8px', borderRadius: 6,
                              cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
                              background: checked ? 'rgba(239,68,68,.08)' : 'transparent',
                            }}>
                              <input type="checkbox" checked={checked} disabled={disabled} onChange={() => toggleExcludedCustomAudience(audience.id)} style={{ marginTop: 2 }} />
                              <span style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ display: 'block', fontSize: 12, color: 'var(--text)' }}>{formatCustomAudienceLabel(audience)}</span>
                                {(audience.subtype || audience.description) && (
                                  <span style={{ display: 'block', fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{[audience.subtype, audience.description].filter(Boolean).join(' · ')}</span>
                                )}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <Hint>被排除的受众不会看到此广告组的广告</Hint>
                </FormGroup>
              </SectionCard>

              {campaignObjective === 'OUTCOME_AWARENESS' && optimizationGoal === 'REACH' && bidStrategy === 'COST_CAP' && (
                <SectionCard title="频次控制">
                  <div style={rowSX}>
                    <FormGroup>
                      <Label>频次控制</Label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        {frequencyModeOptions.map((item) => (
                          <label key={item.value} style={{
                            display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', fontSize: 12,
                            padding: '4px 10px', borderRadius: 6,
                            border: `1px solid ${frequencyMode === item.value ? 'rgba(18,16,184,.4)' : 'var(--border)'}`,
                            background: frequencyMode === item.value ? 'var(--primary-bg)' : 'var(--surface2)',
                            color: frequencyMode === item.value ? 'var(--primary-light)' : 'var(--text2)',
                          }}>
                            <input type="radio" checked={frequencyMode === item.value} onChange={() => setFrequencyMode(item.value)} style={{ accentColor: 'var(--primary)' }} />
                            {item.label}
                          </label>
                        ))}
                      </div>
                    </FormGroup>
                    <FormGroup>
                      <Label>{frequencyMode === 'target' ? '目标频次' : '频次上限'}</Label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input className="form-input" type="number" min="1" step="1" placeholder="3" value={frequencyCap} onChange={(e) => setFrequencyCap(e.target.value)} />
                        <span style={{ fontSize: 12, color: 'var(--text2)' }}>次 / 每</span>
                        <input className="form-input" type="number" min="1" step="1" placeholder="7" value={frequencyCapResetPeriod} onChange={(e) => setFrequencyCapResetPeriod(e.target.value)} />
                        <span style={{ fontSize: 12, color: 'var(--text2)' }}>天</span>
                      </div>
                    </FormGroup>
                  </div>
                </SectionCard>
              )}

              {/* ── 广告组标签 ─────────────────────────────────────────── */}
              {tagConfig.filter(t => t.level === 'adset').length > 0 && (
                <SectionCard title="广告组标签（可选）">
                  <div className="tag-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px 10px' }}>
                    {tagConfig.filter(t => t.level === 'adset').map(td => {
                      const flatOpts = td.options.flatMap(o =>
                        o.children && o.children.length > 0
                          ? o.children.map(c => ({ value: c.value, label: `${o.label} · ${c.label}` }))
                          : [{ value: o.value, label: o.label }]
                      );
                      return (
                        <div key={td.key}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>{td.label}</div>
                          {isPlacementTag(td) ? (() => {
                            return (
                              <MultiSelect
                                searchable
                                clearable
                                tagDisplay
                                placeholder="请选择版位（可多选）"
                                options={flatOpts}
                                value={parseMultiTagValue(adsetTags[td.key])}
                                onChange={(vals) => setAdsetTags(prev => setTagValue(prev, td.key, serializeMultiTagValue(vals)))}
                              />
                            );
                          })() : (
                            <MultiSelect single placeholder="请选择"
                              options={flatOpts}
                              value={adsetTags[td.key] ? [adsetTags[td.key]] : []}
                              onChange={(vals) => setAdsetTags(prev => ({ ...prev, [td.key]: vals[0] ?? '' }))}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </SectionCard>
              )}
            </div>
          )}

          {/* ── Step 3: Ad ───────────────────────────────────────────────── */}
          {step === 3 && (
            <div>
              <div style={{ padding: '6px 10px', borderRadius: 6, background: 'var(--surface2)', border: '1px solid var(--border)', fontSize: 11, color: 'var(--text2)', marginBottom: 14 }}>
                <strong style={{ color: 'var(--primary-light)' }}>{campaignName}</strong>
                <span style={{ margin: '0 5px', color: 'var(--text3)' }}>›</span>
                <strong style={{ color: 'var(--primary-light)' }}>{adsetName}</strong>
              </div>

              <SectionCard title="基本信息">
                <div style={rowSX}>
                  <FormGroup>
                    <Label required>广告名称</Label>
                    <input className="form-input" style={inputSX} placeholder="例：TitaniumFrames_US_Image_A" value={adName} onChange={(e) => setAdName(e.target.value)} />
                  </FormGroup>
                  <FormGroup>
                    <Label>初始状态</Label>
                    <MultiSelect
                      single
                      options={[
                        { value: 'PAUSED', label: '暂停' },
                        { value: 'ACTIVE', label: '投放' },
                      ]}
                      value={[adStatus]}
                      onChange={(values) => setAdStatus(values[0] ?? 'PAUSED')}
                    />
                  </FormGroup>
                </div>

              </SectionCard>

              <SectionCard title="品牌身份">
                {/* Page ID */}
                <FormGroup>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <Label required style={{ margin: 0 }}>Facebook 公共主页</Label>
                    <button type="button" onClick={handleSyncAssets} disabled={syncingAssets}
                      style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg2)', cursor: syncingAssets ? 'not-allowed' : 'pointer', color: 'var(--text2)', opacity: syncingAssets ? 0.6 : 1 }}>
                      {syncingAssets ? '同步中...' : '↻ 同步'}
                    </button>
                  </div>
                  {pagesLoading ? (
                    <div style={{ fontSize: 12, color: 'var(--text3)', padding: '8px 0' }}>正在获取主页列表...</div>
                  ) : pages.length > 0 ? (
                    <MultiSelect
                      single
                      searchable
                      options={pages.map((p) => ({ value: p.id, label: `${p.name} (${p.id})` }))}
                      value={pageID ? [pageID] : []}
                      onChange={vals => { setPageID(vals[0] ?? ''); setTimeout(triggerPreview, 100); }}
                      placeholder="搜索并选择 Facebook 公共主页"
                    />
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input className="form-input" style={{ flex: 1 }} placeholder="输入 Facebook 主页 ID（数字）"
                        value={manualPageID} onChange={(e) => setManualPageID(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && applyManualPageID()} />
                      <button className="btn btn-primary btn-sm" onClick={applyManualPageID} disabled={!manualPageID.trim()} style={{ whiteSpace: 'nowrap' }}>
                        应用
                      </button>
                    </div>
                  )}
                  {pageID && pages.some((p) => p.id === pageID) && (
                    <Hint style={{ color: 'var(--green)' }}>已选主页：{pages.find((p) => p.id === pageID)?.name || pageID}</Hint>
                  )}
                  {!pageID && pages.length === 0 && !pagesLoading && (
                    <Hint>当前授权不含 pages_show_list 权限，请直接输入主页 ID</Hint>
                  )}
                </FormGroup>

                {/* Instagram 账号 */}
                <FormGroup>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Label style={{ margin: 0 }}>Instagram 账号（可选）</Label>
                    <button type="button" onClick={handleRefreshIgAccounts} disabled={igAccountsLoading}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--primary)', padding: 0 }}>
                      {igAccountsLoading ? '获取中...' : '刷新'}
                    </button>
                  </div>

                  {igAccountsLoading ? (
                    <div style={{ fontSize: 12, color: 'var(--text3)', padding: '8px 0' }}>正在获取 Instagram 账号...</div>
                  ) : igAccounts.length > 0 ? (
                    <>
                      <MultiSelect
                        single
                        searchable
                        options={[
                          { value: '', label: '不使用 Instagram 账号' },
                          ...igAccounts.map((a) => ({ value: a.id, label: `${a.username ? `@${a.username}` : a.name} (${a.id})` })),
                        ]}
                        value={[igActorID]}
                        onChange={(values) => {
                          setIgActorID(values[0] ?? '');
                          setShowManualIgActorID(false);
                        }}
                      />
                      <button type="button" onClick={() => setShowManualIgActorID((v) => !v)}
                        style={{ marginTop: 4, background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--primary)', padding: 0 }}>
                        {showManualIgActorID ? '▾ 收起手动输入' : '▸ 手动输入 Instagram 账号 ID'}
                      </button>
                      {showManualIgActorID && (
                        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                          <input className="form-input" style={{ flex: 1 }} placeholder="输入 Instagram 账号 ID（数字）"
                            value={manualIgActorID} onChange={(e) => setManualIgActorID(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && applyManualIgActorID()} />
                          <button className="btn btn-primary btn-sm" onClick={applyManualIgActorID} disabled={!manualIgActorID.trim()} style={{ whiteSpace: 'nowrap' }}>
                            应用
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input className="form-input" style={{ flex: 1 }} placeholder="输入 Instagram 账号 ID（数字，可选）"
                        value={manualIgActorID} onChange={(e) => setManualIgActorID(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && applyManualIgActorID()} />
                      <button className="btn btn-primary btn-sm" onClick={applyManualIgActorID} disabled={!manualIgActorID.trim()} style={{ whiteSpace: 'nowrap' }}>
                        应用
                      </button>
                    </div>
                  )}
                  {igActorID && (
                    <Hint style={{ color: 'var(--green)' }}>已选 Instagram 账号 ID：{igActorID}</Hint>
                  )}
                  {!igActorID && igAccounts.length === 0 && !igAccountsLoading && (
                    <Hint>未找到关联的 Instagram 账号，可手动输入 ID 或留空</Hint>
                  )}
                </FormGroup>

                {platform === 'facebook' && (
                  <div style={{ marginTop: 8, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text2)', userSelect: 'none', marginBottom: showPartnership ? 14 : 0 }}>
                      <input
                        type="checkbox"
                        checked={showPartnership}
                        onChange={(e) => {
                          setShowPartnership(e.target.checked);
                          setPartnershipInfo(null);
                          setPartnershipError('');
                          setPartnershipActorName('');
                          if (e.target.checked) setPartnershipContentSource('input_code');
                        }}
                        style={{ accentColor: 'var(--primary)' }}
                      />
                      <span style={{ fontWeight: 700 }}>合创广告（Partnership Ads）</span>
                      <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 500 }}>红人 / KOL 授权投放其内容，或展示合作方身份</span>
                    </label>

                    {showPartnership && (
                      <div style={{ border: '1px solid rgba(67, 56, 202, 0.18)', borderRadius: 12, background: 'rgba(67, 56, 202, 0.04)', padding: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--primary)' }}>合创广告配置</div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: partnershipPostLocked ? 'var(--green)' : 'var(--text3)' }}>
                            {partnershipPostLocked ? '已锁定帖子与合作方身份' : partnershipInfo ? '已获取帖子信息' : '待获取广告代码'}
                          </div>
                        </div>

                        <FormGroup>
                          <Label required>内容来源</Label>
                          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
                            {[
                              { key: 'existing_post', label: '已有帖子', desc: '复用已有内容进行投放' },
                              { key: 'new_creative', label: '新广告创意', desc: '使用新素材与新文案创建广告' },
                              { key: 'input_code', label: '输入广告代码', desc: '通过创作者提供的广告代码获取投放授权' },
                            ].map((item) => {
                              const sourceKey = item.key as PartnershipContentSource;
                              const disabled = DISABLED_PARTNERSHIP_CONTENT_SOURCES.has(sourceKey);
                              return (
                                <SelectionCard
                                  key={item.key}
                                  title={item.label}
                                  description={disabled ? PARTNERSHIP_CONTENT_SOURCE_UNAVAILABLE_DESC : item.desc}
                                  active={partnershipContentSource === item.key}
                                  disabled={disabled}
                                  onClick={() => {
                                    if (disabled) return;
                                    setPartnershipContentSource(sourceKey);
                                    setPartnershipError('');
                                    if (item.key !== 'input_code') {
                                      setPartnershipInfo(null);
                                    }
                                  }}
                                />
                              );
                            })}
                          </div>
                        </FormGroup>

                        {partnershipContentSource === 'input_code' && (
                          <FormGroup>
                            <Label required>广告代码</Label>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <input
                                className="form-input"
                                style={{ flex: 1 }}
                                placeholder="输入广告代码，例如：DUuI9-HiUEG"
                                value={partnershipCode}
                                onChange={(e) => {
                                  const nextCode = e.target.value;
                                  setPartnershipCode(nextCode);
                                  setPartnershipError('');
                                  if (!nextCode.trim()) {
                                    setPartnershipInfo(null);
                                    setPartnershipActorName('');
                                  }
                                }}
                                onKeyDown={(e) => e.key === 'Enter' && lookupPartnership()}
                              />
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={lookupPartnership}
                                disabled={partnershipLoading || !partnershipCode.trim()}
                                style={{ minWidth: 96, whiteSpace: 'nowrap' }}
                              >
                                {partnershipLoading ? '获取中' : '获取'}
                              </button>
                            </div>
                            <Hint>获取成功后，广告创意会自动选择“使用现有帖子”，并回填合作方身份。</Hint>
                          </FormGroup>
                        )}

                        {partnershipError && (
                          <div style={{ fontSize: 12, color: 'var(--red)', padding: '8px 10px', borderRadius: 8, background: 'rgba(239,68,68,.06)', border: '1px solid rgba(239,68,68,.2)', marginBottom: 10 }}>
                            {partnershipError}
                          </div>
                        )}

                        {partnershipInfo && (
                          <div style={{ fontSize: 12, color: 'var(--text2)', padding: '10px 12px', borderRadius: 10, background: partnershipInfo.status === 'PENDING_VALIDATION' ? 'rgba(245,158,11,.05)' : 'rgba(34,197,94,.05)', border: partnershipInfo.status === 'PENDING_VALIDATION' ? '1px solid rgba(245,158,11,.24)' : '1px solid rgba(34,197,94,.24)', marginBottom: 12 }}>
                            {partnershipInfo.status === 'PENDING_VALIDATION' ? (
                              <div>
                                授权码 <code style={{ color: 'var(--text2)' }}>{partnershipInfo.code}</code> 已保存，将在创建广告时由 Facebook 验证。
                              </div>
                            ) : (
                              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                {partnershipInfo.media_url && (
                                  <img src={partnershipInfo.media_url} alt="创作者内容预览" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
                                )}
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontWeight: 700, color: 'var(--text)' }}>{partnershipInfo.actor_name || '已获取合作方帖子'}</div>
                                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>状态：{PARTNERSHIP_STATUS_LABEL[partnershipInfo.status] || partnershipInfo.status}</div>
                                  {partnershipInfo.permalink_url && (
                                    <a href={partnershipInfo.permalink_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', marginTop: 4, fontSize: 11, color: 'var(--primary)' }}>
                                      查看原帖
                                    </a>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        <div style={{ ...rowSX, marginBottom: 12 }}>
                          <FormGroup>
                            <Label required>身份顺序</Label>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                              <SelectionCard
                                compact
                                title="品牌为第一身份"
                                active={partnershipIdentityOrder === 'brand_first'}
                                onClick={() => setPartnershipIdentityOrder('brand_first')}
                              />
                              <SelectionCard
                                compact
                                title="合作方为第一身份"
                                active={partnershipIdentityOrder === 'partner_first'}
                                onClick={() => setPartnershipIdentityOrder('partner_first')}
                              />
                            </div>
                          </FormGroup>
                          <FormGroup>
                            <Label required>在标题中展示的身份</Label>
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
                              <SelectionCard compact title="动态身份" description="由 Meta 根据版位动态展示" active={partnershipHeadlineIdentity === 'dynamic_identity'} onClick={() => setPartnershipHeadlineIdentity('dynamic_identity')} />
                              <SelectionCard compact title="展示两个身份" description="标题区域同时展示品牌与合作方" active={partnershipHeadlineIdentity === 'brand_identity'} onClick={() => setPartnershipHeadlineIdentity('brand_identity')} />
                              <SelectionCard compact title="仅展示单一身份" description="只展示身份顺序中的第一身份" active={partnershipHeadlineIdentity === 'partner_identity'} onClick={() => setPartnershipHeadlineIdentity('partner_identity')} />
                            </div>
                          </FormGroup>
                        </div>

                        <div>
                          <Label required>合作方身份</Label>
                          <div style={{ ...rowSX, marginTop: 6 }}>
                            <FormGroup>
                              <Label style={{ marginBottom: 6 }}>Facebook 公共主页</Label>
                              {partnershipPartnerLocked ? (
                                <input
                                  className="form-input"
                                  placeholder="已由广告代码锁定"
                                  value={partnershipInfo?.actor_name ? `${partnershipInfo.actor_name}${partnershipInfo.actor_id ? ` (${partnershipInfo.actor_id})` : ''}` : ''}
                                  readOnly
                                  disabled
                                />
                              ) : (
                                <MultiSelect
                                  single
                                  searchable
                                  options={partnershipPageOptions}
                                  value={partnershipPageID ? [partnershipPageID] : []}
                                  searchValue={partnershipPageSearch}
                                  onSearchChange={(keyword) => {
                                    setPartnershipPageSearch(keyword);
                                    if (!keyword.trim()) void loadPartnershipPages('');
                                  }}
                                  searchPlaceholder="按名称、编号或状态搜索..."
                                  onChange={(vals) => {
                                    const nextID = vals[0] ?? '';
                                    setPartnershipPageID(nextID);
                                    const matched = partnershipPages.find((page) => page.id === nextID);
                                    setPartnershipActorName(matched?.name || '');
                                  }}
                                  placeholder={PARTNERSHIP_PARTNER_SELECTOR_DISABLED ? '合作方公共主页选择暂不可用' : (partnershipPagesLoading ? '正在加载合作方主页...' : '搜索并选择合作方 Facebook 公共主页')}
                                  disabled={PARTNERSHIP_PARTNER_SELECTOR_DISABLED}
                                />
                              )}
                            </FormGroup>
                            <FormGroup>
                              <Label style={{ marginBottom: 6 }}>Instagram 公共主页</Label>
                              {partnershipPartnerLocked ? (
                                <input
                                  className="form-input"
                                  placeholder="已由广告代码锁定"
                                  value={partnershipIGUserID}
                                  readOnly
                                  disabled
                                />
                              ) : (
                                <MultiSelect
                                  single
                                  searchable
                                  options={partnershipIgOptions}
                                  value={partnershipIGUserID ? [partnershipIGUserID] : []}
                                  searchValue={partnershipInstagramSearch}
                                  onSearchChange={(keyword) => {
                                    setPartnershipInstagramSearch(keyword);
                                    if (!keyword.trim()) void loadPartnershipInstagramAccounts('');
                                  }}
                                  searchPlaceholder="按名称、编号或状态搜索..."
                                  onChange={(vals) => setPartnershipIGUserID(vals[0] ?? '')}
                                  placeholder={PARTNERSHIP_PARTNER_SELECTOR_DISABLED ? '合作方 Instagram 主页选择暂不可用' : (partnershipInstagramAccountsLoading ? '正在加载合作方 Instagram 主页...' : '搜索并选择合作方 Instagram 公共主页')}
                                  disabled={PARTNERSHIP_PARTNER_SELECTOR_DISABLED}
                                />
                              )}
                            </FormGroup>
                          </div>
                          {partnershipActorName && (
                            <Hint style={{ color: 'var(--green)' }}>
                              当前合作方：{partnershipActorName}{partnershipIGUserID ? ` (${partnershipIGUserID})` : ''}
                            </Hint>
                          )}
                          <Hint>
                            {partnershipPartnerLocked
                              ? `广告代码已锁定合作方身份${partnershipInfo?.actor_name ? `：${partnershipInfo.actor_name}` : ''}，清空广告代码后可重新编辑。`
                              : '因缺少 FB 合作方公共主页接口权限，当前仅支持输入广告代码回填合作方身份。'}
                          </Hint>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </SectionCard>

              {campaignObjective === 'OUTCOME_SALES' && (
                <SectionCard title="广告源">
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>目录关联</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.6 }}>
                        让目录商品与当前广告创意一起展示
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleSyncCatalogs}
                      disabled={syncingCatalogs || !selectedAccountID}
                      style={{ fontSize: 11, padding: '4px 10px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg2)', cursor: syncingCatalogs ? 'not-allowed' : 'pointer', color: 'var(--text2)', opacity: syncingCatalogs ? 0.6 : 1, whiteSpace: 'nowrap' }}
                    >
                      {syncingCatalogs ? '同步中...' : '↻ 同步目录'}
                    </button>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', marginBottom: 10 }}>
                    <input type="checkbox" checked={showCatalog} onChange={(e) => {
                      setShowCatalog(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedCatalogID('');
                        setSelectedProductSetID('');
                        setProductSets([]);
                      }
                    }} style={{ accentColor: 'var(--primary)' }} />
                    <span style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 600 }}>关联商品目录</span>
                  </label>
                  {showCatalog && (
                    <div style={rowSX}>
                      <FormGroup>
                        <Label required>目录</Label>
                        <MultiSelect
                          single
                          searchable
                          clearable
                          options={catalogOptions}
                          value={selectedCatalogID ? [selectedCatalogID] : []}
                          onChange={(vals) => {
                            setSelectedCatalogID(vals[0] ?? '');
                            setSelectedProductSetID('');
                            setProductSets([]);
                          }}
                          placeholder={!selectedAccountID ? '请先选择广告账户' : syncingCatalogs ? '目录同步中...' : `默认选择 ${DEFAULT_FACEBOOK_CATALOG_NAME}`}
                        />
                      </FormGroup>
                      <FormGroup>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <Label required style={{ margin: 0 }}>商品系列</Label>
                          <button
                            type="button"
                            onClick={handleSyncProductSets}
                            disabled={syncingProductSets || !selectedAccountID || !selectedCatalogID}
                            style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg2)', cursor: syncingProductSets ? 'not-allowed' : 'pointer', color: 'var(--text2)', opacity: syncingProductSets ? 0.6 : 1 }}
                          >
                            {syncingProductSets ? '同步中...' : '↻ 同步'}
                          </button>
                        </div>
                        <MultiSelect
                          single
                          searchable
                          clearable
                          options={productSetOptions}
                          value={selectedProductSetID ? [selectedProductSetID] : []}
                          onChange={(vals) => setSelectedProductSetID(vals[0] ?? '')}
                          placeholder={!selectedAccountID ? '请先选择广告账户' : !selectedCatalogID ? '请先选择目录' : syncingProductSets ? '商品系列同步中...' : '搜索并选择商品系列'}
                        />
                      </FormGroup>
                    </div>
                  )}
                  {showCatalog && selectedCatalogID && (
                    <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 6, background: 'var(--surface2)', border: '1px solid var(--border)', fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>
                      目录：<strong>{catalogs.find((item) => item.id === selectedCatalogID)?.name || selectedCatalogID}</strong>
                      {selectedProductSetID && (
                        <span> · 商品系列：<strong>{productSets.find((item) => item.id === selectedProductSetID)?.name || selectedProductSetID}</strong></span>
                      )}
                    </div>
                  )}
                </SectionCard>
              )}

              {platform === 'facebook' && !(showPartnership && partnershipCode.trim()) && (
                <SectionCard title="实显优惠">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', marginBottom: 12 }}>
                    <input type="checkbox" checked={offerEnabled} onChange={(e) => setOfferEnabled(e.target.checked)} style={{ accentColor: 'var(--primary)' }} />
                    <span style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 700 }}>优惠码</span>
                  </label>
                  {offerEnabled && (
                    <>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                        {[
                          { value: 'auto', label: '自动获取优惠码' },
                          { value: 'manual', label: '手动选择优惠码' },
                        ].map((item) => (
                          <label key={item.value} style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12,
                            padding: '6px 10px', borderRadius: 6,
                            border: `1px solid ${offerSource === item.value ? 'rgba(18,16,184,.4)' : 'var(--border)'}`,
                            background: offerSource === item.value ? 'var(--primary-bg)' : 'var(--surface2)',
                            color: offerSource === item.value ? 'var(--primary-light)' : 'var(--text2)',
                          }}>
                            <input
                              type="radio"
                              checked={offerSource === item.value}
                              onChange={() => setOfferSource(item.value as 'auto' | 'manual')}
                              style={{ accentColor: 'var(--primary)' }}
                            />
                            {item.label}
                          </label>
                        ))}
                      </div>
                      <div style={rowSX}>
                        <FormGroup>
                          <Label required={offerSource === 'manual'}>优惠码</Label>
                          <input
                            className="form-input"
                            style={inputSX}
                            placeholder={offerSource === 'auto' ? '留空，由 Meta / Commerce 配置决定' : '例：META25'}
                            value={offerCode}
                            onChange={(e) => setOfferCode(e.target.value.toUpperCase())}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>优惠说明</Label>
                          <input
                            className="form-input"
                            style={inputSX}
                            placeholder="例：立减 15%，或 30% off"
                            value={offerDescription}
                            onChange={(e) => setOfferDescription(e.target.value)}
                          />
                        </FormGroup>
                      </div>
                      <div style={rowSX}>
                        <FormGroup>
                          <Label>优惠卡片图片 Hash（可选）</Label>
                          <input
                            className="form-input"
                            style={inputSX}
                            placeholder="用于目录轮播静态优惠卡"
                            value={offerCardImageHash}
                            onChange={(e) => setOfferCardImageHash(e.target.value.trim())}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>邮件订阅优惠</Label>
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 36, color: 'var(--text2)', fontSize: 12 }}>
                            <input
                              type="checkbox"
                              checked={offerEmailSubscriptionEnabled}
                              onChange={(e) => setOfferEmailSubscriptionEnabled(e.target.checked)}
                              style={{ accentColor: 'var(--primary)' }}
                            />
                            在线接收你网站的折扣并鼓励客户订阅邮件
                          </label>
                        </FormGroup>
                      </div>
                    </>
                  )}
                </SectionCard>
              )}

              {platform === 'facebook' && adType !== 'existing' && !(showPartnership && partnershipCode.trim()) && (
                <SectionCard title="进阶赋能型素材增强">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none', marginBottom: 12 }}>
                    <input
                      type="checkbox"
                      checked={allCreativeEnhancementsEnabled}
                      onChange={(e) => toggleAllCreativeEnhancements(e.target.checked)}
                      style={{ accentColor: 'var(--primary)' }}
                    />
                    <span style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 700 }}>所有优化功能</span>
                  </label>

                  <div style={{ marginBottom: 8, fontSize: 12, fontWeight: 700, color: 'var(--text2)' }}>进阶赋能型素材增强</div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 8, marginBottom: 14 }}>
                    {ADVANCED_CREATIVE_ENHANCEMENT_OPTIONS.map((option) => {
                      const checked = creativeEnhancementFeatures[option.key];
                      return (
                        <label key={option.key} style={{
                          display: 'flex', alignItems: 'flex-start', gap: 8, padding: '9px 10px',
                          border: `1px solid ${checked ? 'rgba(18,16,184,.28)' : 'var(--border)'}`,
                          borderRadius: 6, background: checked ? 'var(--primary-bg)' : 'var(--surface2)',
                          cursor: 'pointer',
                        }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => toggleCreativeEnhancement(option.key, e.target.checked)}
                            style={{ accentColor: 'var(--primary)', marginTop: 2 }}
                          />
                          <span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--text2)' }}>{option.label}</span>
                            <span style={{ display: 'block', fontSize: 11, color: 'var(--text3)', lineHeight: 1.5, marginTop: 2 }}>{option.desc}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>

                  <div style={{ marginBottom: 8, fontSize: 12, fontWeight: 700, color: 'var(--text2)' }}>必要的增强功能</div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 8 }}>
                    {REQUIRED_CREATIVE_ENHANCEMENT_OPTIONS.map((option) => {
                      const checked = creativeEnhancementFeatures[option.key];
                      return (
                        <label key={option.key} style={{
                          display: 'flex', alignItems: 'flex-start', gap: 8, padding: '9px 10px',
                          border: `1px solid ${checked ? 'rgba(18,16,184,.28)' : 'var(--border)'}`,
                          borderRadius: 6, background: checked ? 'var(--primary-bg)' : 'var(--surface2)',
                          cursor: 'pointer',
                        }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => toggleCreativeEnhancement(option.key, e.target.checked)}
                            style={{ accentColor: 'var(--primary)', marginTop: 2 }}
                          />
                          <span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--text2)' }}>{option.label}</span>
                            <span style={{ display: 'block', fontSize: 11, color: 'var(--text3)', lineHeight: 1.5, marginTop: 2 }}>{option.desc}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text3)' }}>
                    已开启 {CREATIVE_ENHANCEMENT_OPTIONS.filter((item) => creativeEnhancementFeatures[item.key]).length} / {CREATIVE_ENHANCEMENT_OPTIONS.length}：{CREATIVE_ENHANCEMENT_OPTIONS.filter((item) => creativeEnhancementFeatures[item.key]).map((item) => item.label).join('、') || '无'}
                  </div>
                </SectionCard>
              )}

              <SectionCard title="广告创意">
                {partnershipUsesPostCreative && (
                  <div style={{ borderRadius: 8, border: '1px solid rgba(18,16,184,.18)', background: 'var(--surface2)', padding: '14px 16px', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 18 }}>{partnershipUsesAuthCode ? '🤝' : '📝'}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
                        {partnershipUsesAuthCode ? '合创广告 - 使用广告代码获取的帖子' : '合创广告 - 使用已有帖子'}
                      </span>
                      <span style={{ marginLeft: 'auto', fontSize: 10, padding: '2px 7px', borderRadius: 4, background: partnershipUsesAuthCode ? 'rgba(34,197,94,.15)' : 'rgba(18,16,184,.12)', color: partnershipUsesAuthCode ? 'var(--green)' : 'var(--primary-light)', fontWeight: 600 }}>
                        {partnershipUsesAuthCode ? '帖子与合作方身份已联动' : '按原型固定入口'}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.7 }}>
                      {partnershipUsesAuthCode
                        ? '广告代码获取成功后，会自动回填并锁定合作方身份与帖子信息；广告创意继续沿用“使用现有帖子”入口。'
                        : '当前模式只保留“使用现有帖子”入口，请在下方选择帖子或手动输入帖子编号。'}
                    </div>
                  </div>
                )}
                {isSalesCatalogAd ? (<>
                  <div style={{ borderRadius: 8, border: '1px solid rgba(18,16,184,.18)', background: 'var(--surface2)', padding: '14px 16px', marginBottom: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 18 }}>🛍️</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>目录广告 — 默认轮播</span>
                      <span style={{ marginLeft: 'auto', fontSize: 10, padding: '2px 7px', borderRadius: 4, background: 'rgba(18,16,184,.12)', color: 'var(--primary-light)', fontWeight: 600 }}>无需上传素材</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.7 }}>
                      已按你选择的商品目录和商品系列创建目录广告。系统会自动使用商品目录中的商品图片和商品信息生成广告创意，不需要再上传图片或视频素材。
                    </div>
                  </div>
                  <div>
                    <div style={rowSX}>
                      <FormGroup><Label required>目标链接</Label><input className="form-input" style={inputSX} placeholder="https://example.com" value={link} onChange={(e) => { setLink(e.target.value); setTimeout(triggerPreview, 400); }} /><Hint>目录广告请填写目标链接</Hint></FormGroup>
                      <FormGroup><Label>行动号召按钮</Label>
                        <MultiSelect
                          single
                          options={[{ value: '', label: '无按钮' }, ...ctaOptions]}
                          value={[ctaType]}
                          onChange={(values) => { setCtaType(values[0] ?? ''); setTimeout(triggerPreview, 200); }}
                        />
                      </FormGroup>
                    </div>
                    <FormGroup><Label>主文案</Label><textarea className="form-textarea" placeholder="广告主文案（可选）" value={message} onChange={(e) => { setMessage(e.target.value); setTimeout(triggerPreview, 400); }} /></FormGroup>
                    <div style={rowSX}>
                      <FormGroup><Label>标题</Label><input className="form-input" style={inputSX} placeholder="广告标题（40 字符以内）" value={headline} onChange={(e) => { setHeadline(e.target.value); setTimeout(triggerPreview, 400); }} /></FormGroup>
                      <FormGroup><Label>描述</Label><input className="form-input" style={inputSX} placeholder="标题下方描述（30 字符以内）" value={description} onChange={(e) => setDescription(e.target.value)} /></FormGroup>
                    </div>
                  </div>
                </>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                    {AD_TYPES.map((t) => {
                      const disabled = (showPartnership && partnershipUsesPostCreative && t.key !== 'post')
                        || (partnershipUsesNewCreative && t.key === 'post');
                      return (
                      <button key={t.key} type="button"
                        className={`btn btn-sm ${adType === t.key ? 'btn-primary' : 'btn-outline'}`}
                        style={{ fontSize: 12, opacity: disabled ? 0.45 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
                        disabled={disabled}
                        onClick={() => {
                          if (disabled) return;
                          setAdType(t.key);
                          setError(null);
                        }}>
                        {t.label}
                      </button>
                    );})}
                  </div>
                )}
                {!isSalesCatalogAd && !showCatalog && (<div>

                {/* Image */}
                {adType === 'image' && (
                  <div>
                    <FormGroup>
                      <Label required>广告图片</Label>
                      <SharedUploadZone onFile={handleUploadImage} accept="image/*" hint="JPG/PNG，建议 1080×1080 (1:1) 或 1200×628 (1.91:1)"
                        fileName={imageFile?.name} uploading={imageUploading} uploadPct={imageUploadPct} previewURL={imagePreviewURL}
                        onClear={imageHash || imagePreviewURL ? clearImageMaterial : undefined} />
                      {imageHash && !imagePreviewURL && <Hint style={{ color: 'var(--green)' }}>已上传，hash: {imageHash.substring(0, 16)}...</Hint>}
                    </FormGroup>
                    <div style={rowSX}>
                      <FormGroup><Label required>目标链接</Label><input className="form-input" style={inputSX} placeholder="https://example.com" value={link} onChange={(e) => { setLink(e.target.value); setTimeout(triggerPreview, 400); }} /></FormGroup>
                      <FormGroup><Label>行动号召按钮</Label>
                        <MultiSelect
                          single
                          options={[{ value: '', label: '无按钮' }, ...ctaOptions]}
                          value={[ctaType]}
                          onChange={(values) => { setCtaType(values[0] ?? ''); setTimeout(triggerPreview, 200); }}
                        />
                      </FormGroup>
                    </div>
                    <FormGroup><Label>主文案</Label><textarea className="form-textarea" placeholder="广告主文案，125 字符以内效果最佳..." value={message} onChange={(e) => { setMessage(e.target.value); setTimeout(triggerPreview, 400); }} /></FormGroup>
                    <div style={rowSX}>
                      <FormGroup><Label>标题</Label><input className="form-input" style={inputSX} placeholder="广告标题（40 字符以内）" value={headline} onChange={(e) => { setHeadline(e.target.value); setTimeout(triggerPreview, 400); }} /></FormGroup>
                      <FormGroup><Label>描述</Label><input className="form-input" style={inputSX} placeholder="标题下方描述（30 字符以内）" value={description} onChange={(e) => setDescription(e.target.value)} /></FormGroup>
                    </div>
                    <FormGroup><Label>显示链接（可选）</Label><input className="form-input" style={inputSX} placeholder="example.com" value={caption} onChange={(e) => setCaption(e.target.value)} /></FormGroup>
                  </div>
                )}

                {/* Video */}
                {adType === 'video' && (
                  <div>
                    <FormGroup>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <Label style={{ margin: 0 }} required>广告视频</Label>
                        <button type="button"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--primary)', padding: 0 }}
                          onClick={() => setUseExistingVideoID(v => !v)}>
                          {useExistingVideoID ? '▸ 改为上传视频' : '▸ 已有 Facebook Video ID'}
                        </button>
                      </div>
                      {useExistingVideoID ? (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <input className="form-input" style={{ flex: 1 }}
                            placeholder="粘贴 Facebook Video ID（纯数字）"
                            value={existingVideoIDInput}
                            onChange={e => setExistingVideoIDInput(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && void handleUseExistingVideoID()} />
                          <button className="btn btn-primary btn-sm"
                            onClick={() => void handleUseExistingVideoID()}
                            disabled={!existingVideoIDInput.trim() || existingVideoIDChecking}
                            style={{ whiteSpace: 'nowrap' }}>
                            {existingVideoIDChecking ? '查询中...' : '验证并使用'}
                          </button>
                        </div>
                      ) : (
                        <SharedUploadZone onFile={handleUploadVideo} accept="video/*" hint="MP4/MOV，建议 16:9 或 1:1，15–60 秒效果最佳"
                          fileName={videoFile?.name} uploading={videoUploading} uploadPct={videoUploadPct}
                          />
                      )}
                      {videoID && (
                        <Hint style={{ color: videoReady ? 'var(--green)' : 'var(--amber, #f59e0b)' }}>
                          {videoReady
                            ? `视频已就绪 · video_id: ${videoID}`
                            : `视频处理中（发布时自动等待）· video_id: ${videoID}`}
                        </Hint>
                      )}
                    </FormGroup>
                    <FormGroup>
                      <Label>视频封面图（可选）</Label>
                      {thumbHash && !thumbFile && (
                        <div style={{ fontSize: 11, color: 'var(--green)', marginBottom: 4 }}>已自动获取封面缩略图，可手动上传替换</div>
                      )}
                      <SharedUploadZone onFile={handleUploadThumb} accept="image/*" hint="JPG/PNG 封面图（留空则使用视频自动截帧）"
                        fileName={thumbFile?.name} uploading={thumbUploading} uploadPct={thumbUploadPct} previewURL={thumbPreviewURL}
                        />
                    </FormGroup>
                    <FormGroup><Label>主文案</Label><textarea className="form-textarea" placeholder="视频广告主文案..." value={message} onChange={(e) => setMessage(e.target.value)} /></FormGroup>
                    <div style={rowSX}>
                      <FormGroup><Label>标题</Label><input className="form-input" style={inputSX} placeholder="视频标题" value={headline} onChange={(e) => setHeadline(e.target.value)} /></FormGroup>
                      <FormGroup><Label>目标链接</Label><input className="form-input" style={inputSX} placeholder="https://example.com" value={link} onChange={(e) => setLink(e.target.value)} /></FormGroup>
                    </div>
                    <FormGroup><Label>行动号召按钮</Label>
                      <MultiSelect
                        single
                        options={[{ value: '', label: '无按钮' }, ...ctaOptions]}
                        value={[ctaType]}
                        onChange={(values) => setCtaType(values[0] ?? '')}
                      />
                    </FormGroup>
                  </div>
                )}

                {/* Carousel */}
                {adType === 'carousel' && (
                  <div>
                    <FormGroup><Label>轮播整体主文案</Label><textarea className="form-textarea" placeholder="显示在所有卡片上方..." value={message} onChange={(e) => setMessage(e.target.value)} /></FormGroup>
                    <div style={rowSX}>
                      <FormGroup><Label required>默认目标链接</Label><input className="form-input" style={inputSX} placeholder="https://example.com" value={link} onChange={(e) => setLink(e.target.value)} /></FormGroup>
                      <FormGroup><Label>整体行动号召</Label>
                        <MultiSelect
                          single
                          options={[{ value: '', label: '无按钮' }, ...ctaOptions]}
                          value={[ctaType]}
                          onChange={(values) => setCtaType(values[0] ?? '')}
                        />
                      </FormGroup>
                    </div>

                    <div style={{ marginBottom: 8, fontSize: 12, fontWeight: 600, color: 'var(--text2)' }}>卡片列表（{carouselCards.length}/10）</div>
                    {carouselCards.map((card, idx) => (
                      <div key={card._key} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 10, background: 'var(--surface2)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                          <span style={{ fontSize: 12, fontWeight: 600 }}>卡片 {idx + 1}</span>
                          <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--red)', fontSize: 11 }}
                            onClick={() => removeCarouselCard(idx)} disabled={carouselCards.length <= 2}>✕ 删除</button>
                        </div>
                        <SharedUploadZone onFile={(f) => handleUploadCarouselCard(idx, f)} accept="image/*" hint="1:1 图片"
                          fileName={card._imageFile?.name} uploading={card._imageUploading} previewURL={card._previewURL}
                          />
                        <div style={{ ...rowSX, marginTop: 8 }}>
                          <FormGroup><Label>卡片标题</Label><input className="form-input" style={inputSX} placeholder="标题" value={card.headline || ''} onChange={(e) => updateCard(idx, 'headline', e.target.value)} /></FormGroup>
                          <FormGroup><Label>卡片链接（留空用默认）</Label><input className="form-input" style={inputSX} placeholder="https://example.com/product" value={card.link || ''} onChange={(e) => updateCard(idx, 'link', e.target.value)} /></FormGroup>
                        </div>
                        <FormGroup><Label>卡片描述</Label><input className="form-input" style={inputSX} placeholder="描述" value={card.description || ''} onChange={(e) => updateCard(idx, 'description', e.target.value)} /></FormGroup>
                      </div>
                    ))}
                    <button type="button" className="btn btn-outline btn-sm" style={{ width: '100%' }} onClick={addCarouselCard} disabled={carouselCards.length >= 10}>
                      + 添加卡片（{carouselCards.length}/10）
                    </button>
                  </div>
                )}

                {adType === 'existing' && (
                  <div>
                    <FormGroup>
                      <Label required>{partnershipUsesExistingPost || partnershipUsesAuthCode ? '帖子信息' : '创意 ID'}</Label>
                      {partnershipPostLocked && partnershipInfo ? (
                        <div style={{ border: '1px solid rgba(18,16,184,.16)', borderRadius: 10, background: 'var(--surface2)', padding: '12px 14px' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
                            已通过广告代码锁定帖子
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.7 }}>
                            <div><span style={{ color: 'var(--text3)' }}>发布者：</span>{partnershipInfo.actor_name || '—'}</div>
                            <div><span style={{ color: 'var(--text3)' }}>帖子 Media ID：</span>{partnershipInfo.media_id || '—'}</div>
                            {partnershipInfo.permalink_url && (
                              <div><a href={partnershipInfo.permalink_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', fontSize: 11 }}>查看原帖</a></div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <input
                              className="form-input"
                              style={{ ...inputSX, flex: 1 }}
                              placeholder={partnershipUsesExistingPost ? '输入与帖子关联的 creative_id' : '输入已在 Facebook 中存在的 creative_id'}
                              value={existingCreativeIDInput}
                              onChange={(e) => setExistingCreativeIDInput(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleApplyExistingCreativeID()}
                            />
                            <button
                              type="button"
                              className="btn btn-primary btn-sm"
                              onClick={handleApplyExistingCreativeID}
                              disabled={!existingCreativeIDInput.trim()}
                              style={{ whiteSpace: 'nowrap' }}
                            >
                              应用
                            </button>
                          </div>
                          <Hint>{partnershipUsesExistingPost ? '按原型先复用帖子对应的 creative_id；应用后会拉取 Facebook 真实预览。' : '应用后会使用该 creative_id 拉取 Facebook 真实预览。'}</Hint>
                        </>
                      )}
                      {existingCreativeID && !partnershipPostLocked && (
                        <Hint style={{ color: 'var(--green)' }}>当前已应用创意 ID：{existingCreativeID}</Hint>
                      )}
                    </FormGroup>
                  </div>
                )}

                {adType === 'post' && (
                  <div>
                    <FormGroup>
                      <Label required>投放帖子</Label>
                      {partnershipPostLocked && partnershipInfo ? (
                        <div style={{ border: '1px solid rgba(18,16,184,.16)', borderRadius: 10, background: 'var(--surface2)', padding: '12px 14px', marginBottom: 10 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>
                            已通过广告代码锁定帖子
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.7 }}>
                            <div><span style={{ color: 'var(--text3)' }}>发布者：</span>{partnershipInfo.actor_name || '—'}</div>
                            <div><span style={{ color: 'var(--text3)' }}>帖子 Media ID：</span>{partnershipInfo.media_id || '—'}</div>
                            {partnershipInfo.permalink_url && (
                              <div><a href={partnershipInfo.permalink_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', fontSize: 11 }}>查看原帖</a></div>
                            )}
                          </div>
                        </div>
                      ) : selectedExistingPost ? (
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: 10, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--surface2)', marginBottom: 10 }}>
                          {selectedExistingPost.thumbnail_url && <img src={selectedExistingPost.thumbnail_url} alt="帖子素材" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 6 }} />}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>{selectedExistingPost.platform === 'facebook' ? 'Facebook 帖子' : 'Instagram 帖子'}</div>
                            <div style={{ fontSize: 12, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedExistingPost.title || '无标题帖子'}</div>
                            <div style={{ fontSize: 11, color: 'var(--text3)' }}>{selectedExistingPost.id} · {selectedExistingPost.created_time ? selectedExistingPost.created_time.slice(0, 10) : ''}</div>
                          </div>
                        </div>
                      ) : (
                        <Hint>请选择 Facebook 或 Instagram 中已有的可投放帖子。</Hint>
                      )}
                      {!partnershipPostLocked && (
                        <button type="button" className="btn btn-primary btn-sm" onClick={() => setPostPickerOpen(true)}>
                        {selectedExistingPost ? '更换帖子' : '选择帖子'}
                        </button>
                      )}
                    </FormGroup>
                    {!partnershipPostLocked && (
                      <>
                        <button type="button" onClick={() => setShowManualPostID((v) => !v)} style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: 11, cursor: 'pointer', padding: 0, marginBottom: 6 }}>
                          {showManualPostID ? '▾ 收起手动输入' : '▸ 手动输入帖子编号'}
                        </button>
                        {showManualPostID && (
                          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                            <input className="form-input" style={{ flex: 1 }} placeholder="输入帖子编号" value={manualPostID} onChange={(e) => setManualPostID(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void handleApplyManualPostID()} />
                            <button className="btn btn-primary btn-sm" type="button" disabled={!manualPostID.trim() || postLoading} onClick={() => void handleApplyManualPostID()}>应用</button>
                          </div>
                        )}
                      </>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--text3)', margin: '10px 0' }}>选择帖子后会优先回填帖子里的主文案和目标链接；已手动填写的内容会保留。</div>
                    <div style={rowSX}>
                      <FormGroup>
                        <Label>目标链接</Label>
                        <input className="form-input" style={inputSX} placeholder="https://example.com/landing" value={link} onChange={(e) => { setLink(e.target.value); setTimeout(triggerPreview, 400); }} />
                      </FormGroup>
                      <FormGroup>
                        <Label>行动号召按钮</Label>
                        <MultiSelect
                          single
                          options={[{ value: '', label: '不覆盖' }, ...ctaOptions]}
                          value={[ctaType]}
                          onChange={(values) => { setCtaType(values[0] ?? ''); setTimeout(triggerPreview, 200); }}
                        />
                      </FormGroup>
                    </div>
                    <FormGroup>
                      <Label>主文案</Label>
                      <textarea className="form-textarea" rows={2} placeholder="保留或补充帖子主文案" value={message} onChange={(e) => { setMessage(e.target.value); setTimeout(triggerPreview, 400); }} />
                    </FormGroup>
                  </div>
                )}

                {/* Catalog / DPA */}
                {adType === 'catalog' && (
                  <div>
                    <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(18,16,184,.06)', border: '1px solid rgba(18,16,184,.15)', fontSize: 12, color: 'var(--text2)', marginBottom: 12 }}>
                      图片和商品信息自动来自广告组中关联的商品目录，文案中可使用动态变量。
                    </div>
                    <FormGroup><Label required>商店链接</Label><input className="form-input" style={inputSX} placeholder="https://www.example.com/store" value={link} onChange={(e) => { setLink(e.target.value); setTimeout(triggerPreview, 400); }} /></FormGroup>
                    <FormGroup><Label>主文案</Label><textarea className="form-textarea" placeholder="广告整体文案（不支持动态变量）" value={message} onChange={(e) => setMessage(e.target.value)} /></FormGroup>
                    <FormGroup>
                      <Label>标题</Label>
                      <input className="form-input" style={inputSX} placeholder="{{product.name | titleize}}" value={headline} onChange={(e) => { setHeadline(e.target.value); setTimeout(triggerPreview, 400); }} />
                      <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {DPA_VARS.slice(0, 3).map((v) => (
                          <span key={v.v} title={v.desc} onClick={() => setHeadline((prev) => prev + v.v)} style={{
                            cursor: 'pointer', fontSize: 10, padding: '2px 6px', borderRadius: 4,
                            background: 'var(--surface3)', color: 'var(--text3)', fontFamily: 'monospace',
                          }}>{v.v}</span>
                        ))}
                      </div>
                    </FormGroup>
                    <FormGroup><Label>描述</Label>
                      <input className="form-input" style={inputSX} placeholder="{{product.current_price}}" value={description} onChange={(e) => setDescription(e.target.value)} />
                    </FormGroup>
                    <FormGroup><Label>行动号召按钮</Label>
                      <MultiSelect
                        single
                        options={[{ value: '', label: '选择' }, ...ctaOptions]}
                        value={[ctaType]}
                        onChange={(values) => setCtaType(values[0] ?? '')}
                      />
                    </FormGroup>
                    {/* DPA variable reference */}
                    <details style={{ fontSize: 11, marginTop: 8 }}>
                      <summary style={{ cursor: 'pointer', color: 'var(--text3)', userSelect: 'none' }}>动态变量说明</summary>
                      <table style={{ marginTop: 6, width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <tbody>
                          {DPA_VARS.map((v) => (
                            <tr key={v.v}>
                              <td style={{ padding: '2px 6px', fontFamily: 'monospace', color: 'var(--primary-light)', background: 'var(--surface2)' }}>{v.v}</td>
                              <td style={{ padding: '2px 6px', color: 'var(--text2)' }}>{v.desc}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </details>
                  </div>
                )}

                {/* Dynamic / Advantage+ */}
                </div>)}

                {/* Partnership override: link + CTA + message */}
                {partnershipUsesAuthCode && partnershipInfo && (
                  <div style={{ marginTop: 12 }}>
                    <div style={rowSX}>
                      <FormGroup><Label>目标链接（覆盖）</Label><input className="form-input" style={inputSX} placeholder="https://example.com" value={link} onChange={(e) => { setLink(e.target.value); setTimeout(triggerPreview, 400); }} /></FormGroup>
                      <FormGroup><Label>行动号召按钮</Label>
                        <MultiSelect
                          single
                          options={[{ value: '', label: '无按钮' }, ...ctaOptions]}
                          value={[ctaType]}
                          onChange={(values) => { setCtaType(values[0] ?? ''); setTimeout(triggerPreview, 200); }}
                        />
                      </FormGroup>
                    </div>
                    <FormGroup><Label>主文案（覆盖）</Label><textarea className="form-textarea" placeholder="覆盖红人原帖文案（可选）" value={message} onChange={(e) => { setMessage(e.target.value); setTimeout(triggerPreview, 400); }} /></FormGroup>
                  </div>
                )}
              </SectionCard>

              {/* UTM tags */}
              {(
                <div style={{ marginBottom: 14 }}>
                  <button type="button" onClick={() => setShowUTM((v) => !v)} style={{
                    background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 12px',
                    cursor: 'pointer', fontSize: 12, color: 'var(--text3)', width: '100%', textAlign: 'left',
                  }}>
                    {showUTM ? '▾' : '▸'} UTM 追踪参数（可选）
                  </button>
                  {showUTM && (
                    <div style={{ padding: '10px 12px', border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 6px 6px', background: 'var(--surface2)' }}>
                      <textarea className="form-textarea" rows={2}
                        placeholder="utm_source=facebook&utm_medium=cpc&utm_campaign={{campaign.name}}"
                        value={urlTags} onChange={(e) => setUrlTags(e.target.value)} />
                      <Hint>支持 Facebook 动态参数：{'{{campaign.name}}'}、{'{{adset.name}}'}、{'{{ad.name}}'}</Hint>
                    </div>
                  )}
                </div>
              )}

              {/* ── 广告标签 ─────────────────────────────────────────────── */}
              {tagConfig.filter(t => t.level === 'ad').length > 0 && (
                <SectionCard title="广告标签">
                  <div className="tag-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px 10px' }}>
                    {tagConfig.filter(t => t.level === 'ad').map(td => {
                      const flatOpts = td.options.flatMap(o =>
                        o.children && o.children.length > 0
                          ? o.children.map(c => ({ value: c.value, label: `${o.label} · ${c.label}` }))
                          : [{ value: o.value, label: o.label }]
                      );
                      return (
                        <div key={td.key}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>{td.label}</div>
                          <MultiSelect single placeholder="请选择"
                            options={flatOpts}
                            value={adTags[td.key] ? [adTags[td.key]] : []}
                            onChange={(vals) => setAdTags(prev => ({ ...prev, [td.key]: vals[0] ?? '' }))}
                          />
                        </div>
                      );
                    })}
                  </div>
                </SectionCard>
              )}
            </div>
          )}

          {/* ── Navigation ───────────────────────────────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginTop: 24, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <button className="btn btn-ghost" style={{ visibility: step > 1 ? 'visible' : 'hidden' }} onClick={handlePrev} disabled={submitting}>
              ← 上一步
            </button>
            <div style={{ flex: '1 1 240px' }} />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => { void handleCloseAction(); }} disabled={submitting || validating}>关闭</button>
              {step < 3 ? (
                <button className="btn btn-primary" onClick={handleNext}>下一步 →</button>
              ) : (
                <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting || validating}>
                  {validating ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div className="spinner" style={{ width: 12, height: 12, borderColor: 'rgba(0,0,0,.2)', borderTopColor: '#fff' }} />
                      验证中...
                    </span>
                  ) : submitting ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div className="spinner" style={{ width: 12, height: 12, borderColor: 'rgba(0,0,0,.2)', borderTopColor: '#fff' }} />
                      创建中...
                    </span>
                  ) : '确认创建'}
                </button>
              )}
            </div>
          </div>
          {/* end parentDataLoaded */}
          </>}
          {/* end available platform wizard */}
          </>}
        </div>

        {/* ── Right: Sidebar ──────────────────────────────────────────────── */}
        <div style={{ position: isMobile ? undefined : 'sticky', top: 72, maxHeight: isMobile ? undefined : 'calc(100vh - 88px)', overflowY: isMobile ? undefined : 'auto' }}>
          {platform === 'google' ? (
            <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', background: 'var(--surface2)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>Google Ads 对接说明</div>
              <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.75 }}>
                当前 Google 使用独立创建表单，流程仍为：广告系列 → 广告组 → 广告（RSA）。
                <br />
                支持关键词、语言/地理 ID、响应式搜索广告标题与描述校验，并按平台扩展表独立落库。
              </div>
            </div>
          ) : platform === 'tiktok' ? (
            <>
              <TikTokGuidePanel step={tikTokStep} objective={tikTokObjective} adFormat={tikTokAdFormat} />
              <TikTokCreativeSpecRef adFormat={tikTokAdFormat} />
            </>
          ) : step === 3 ? (
            <>
              {/* Ad preview — Facebook post style */}
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 10 }}>
                <div style={{ padding: '7px 12px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)', fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>广告预览</span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {previewLoading && <span style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 400 }}>加载中...</span>}
                    {previewHTML && previewHTML.match(/src="([^"]+)"/) && (
                      <a
                        href={previewHTML.match(/src="([^"]+)"/)?.[1] ?? '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: 10, color: 'var(--primary-light)', textDecoration: 'none', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}
                      >
                        ↗ Facebook 真实预览
                      </a>
                    )}
                    {!previewHTML && !previewLoading && (
                      <button onClick={triggerPreview} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: 'var(--text3)', fontWeight: 400, padding: 0 }}>
                        ↺ 刷新
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ padding: 8, background: '#e9eaeb' }}>
                  <LocalAdPreview
                    adType={(partnershipUsesAuthCode && partnershipInfo) ? 'partnership' : adType}
                    imageHash={imageHash}
                    imagePreviewURL={imagePreviewURL}
                    videoID={videoID}
                    message={message}
                    headline={headline}
                    description={description}
                    link={link}
                    ctaType={ctaType}
                    pageName={pages.find((p) => p.id === pageID)?.name || ''}
                  carouselCount={carouselCards.length}
                  carouselPreviews={carouselCards.map((c) => c._previewURL || '').filter(Boolean)}
                  partnershipMediaURL={partnershipInfo?.media_url}
                  partnershipActorName={partnershipInfo?.actor_name}
                  partnershipPermalinkURL={partnershipInfo?.permalink_url}
                  existingCreativeID={existingCreativeID}
                  existingPost={selectedExistingPost}
                />
                </div>
              </div>
              <AdPreviewPanel html={previewHTML} loading={previewLoading} error={previewError} onRefresh={triggerPreview} />
              <CreativeSpecRef />
            </>
          ) : (
            <>
              <PerformanceBenchmarks objective={campaignObjective} />
              <DeliveryLogicPanel step={step} objective={campaignObjective} />
            </>
          )}
        </div>
      </div>

      {/* ── Toast ────────────────────────────────────────────────────────── */}
      {toast && (
        <div style={{ position: 'fixed', top: 64, left: '50%', transform: 'translateX(-50%)', zIndex: 3000, pointerEvents: 'none' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: toast.type === 'success' ? 'rgba(34,197,94,.06)' : toast.type === 'error' ? 'rgba(239,68,68,.06)' : toast.type === 'warning' ? 'rgba(245,158,11,.06)' : 'var(--surface2)',
            border: `1px solid ${toast.type === 'success' ? 'rgba(34,197,94,.35)' : toast.type === 'error' ? 'rgba(239,68,68,.35)' : toast.type === 'warning' ? 'rgba(245,158,11,.35)' : 'var(--border2)'}`,
            color: toast.type === 'success' ? 'var(--green)' : toast.type === 'error' ? 'var(--red)' : toast.type === 'warning' ? 'var(--amber)' : 'var(--text)',
            borderRadius: 10, padding: '11px 18px', fontSize: 13, fontWeight: 500,
            boxShadow: '0 8px 32px rgba(0,0,0,.45)', minWidth: 260, maxWidth: 460,
          }}>
            {toast.msg}
          </div>
        </div>
      )}

      {postPickerOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.42)', zIndex: 2500, display: 'grid', placeItems: 'center', padding: 24 }}>
          <div style={{ width: 'min(980px, 96vw)', maxHeight: '86vh', overflow: 'hidden', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 20px 60px rgba(0,0,0,.25)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <strong>选择帖子</strong>
              <button type="button" onClick={() => setPostPickerOpen(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18, color: 'var(--text3)' }}>×</button>
            </div>
            <div style={{ padding: '10px 16px', display: 'flex', gap: 8, alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
              {(['facebook', 'instagram'] as const).map((tab) => (
                <button key={tab} type="button" className={`btn btn-sm ${postPickerTab === tab ? 'btn-primary' : 'btn-outline'}`} onClick={() => { setPostPickerTab(tab); setPostOptions([]); }}>
                  {tab === 'facebook' ? 'Facebook' : 'Instagram'}
                </button>
              ))}
              <input className="form-input" style={{ marginLeft: 8, flex: 1 }} placeholder="搜索帖子、图片或视频编号，或关键词" value={postSearch} onChange={(e) => setPostSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void loadExistingPosts(postPickerTab, postSearch)} />
              <button type="button" className="btn btn-primary btn-sm" onClick={() => void loadExistingPosts(postPickerTab, postSearch)} disabled={postLoading}>{postLoading ? '加载中...' : '搜索'}</button>
            </div>
            <div style={{ overflow: 'auto', padding: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ color: 'var(--text3)', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                    <th style={{ padding: 8 }}>{postPickerTab === 'facebook' ? 'Facebook 帖子' : 'Instagram 帖子'}</th>
                    <th style={{ padding: 8 }}>帖子编号</th>
                    <th style={{ padding: 8 }}>来源</th>
                    <th style={{ padding: 8 }}>素材</th>
                    <th style={{ padding: 8 }}>互动</th>
                    <th style={{ padding: 8 }}>创建日期</th>
                  </tr>
                </thead>
                <tbody>
                  {postOptions.length === 0 ? (
                    postLoading ? <tr><td colSpan={6}><TableLoadingCell /></td></tr> : <tr><td colSpan={6} style={{ padding: 28, textAlign: 'center', color: 'var(--text3)' }}>暂无帖子</td></tr>
                  ) : postOptions.map((post) => (
                    <tr key={`${post.platform}-${post.id}`} onClick={() => applyExistingPost(post)} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}>
                      <td style={{ padding: 8, minWidth: 300 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          {post.thumbnail_url && <img src={post.thumbnail_url} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 4 }} />}
                          <span style={{ display: 'block', maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{post.title || '无标题帖子'}</span>
                        </div>
                      </td>
                      <td style={{ padding: 8, color: 'var(--text2)' }}>{post.id}</td>
                      <td style={{ padding: 8 }}>{post.source || (post.platform === 'facebook' ? '动态' : 'Instagram')}</td>
                      <td style={{ padding: 8 }}>
                        <div>{post.media_type || '-'}</div>
                      </td>
                      <td style={{ padding: 8, color: 'var(--text3)' }}>赞 {post.like_count || 0} · 评 {post.comment_count || 0} · 分享 {post.share_count || 0}</td>
                      <td style={{ padding: 8 }}>{post.created_time ? post.created_time.slice(0, 10) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: 12, borderTop: '1px solid var(--border)', textAlign: 'right' }}>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => setPostPickerOpen(false)}>取消</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
